import React from 'react';
import { useAppContext } from '../contexts/AppContext';
import ConfirmModal from '../components/ConfirmModal';
import { bookTypeChipLabel } from '../utils/bookLabels';
import MemoryCareComingSoon from '../components/MemoryCareComingSoon';
import { arrayMove } from '@dnd-kit/sortable';
import { pdf } from '@react-pdf/renderer';
import { pdfjs } from 'react-pdf'; // TB-102 Phase 2: read rendered page count from interior blob
import { uploadData, getUrl } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { createLegacyEntry, updateLegacyEntry } from '../graphql/mutations';
import { callAi } from '../utils/aiClient';
import { trackEvent } from '../utils/analytics';
import { useSaleOffer, SALE_OFFER, fmtUsd } from '../hooks/useSaleOffer';
// QRCode is used indirectly via chapterQrCodes from context
import RemnantBookPDF from '../RemnantBookPDF';
import RemnantCoverPDF from '../RemnantCoverPDF';
import MothersDayCover from '../assets/MothersDayCover_thumb.jpg';
import BurgundyCover from '../assets/BurgFullWrapJ_thumb.jpg';
import GoldLinenCover from '../assets/GoldLinen_thumb.jpg';
import CharcoalGoldCover from '../assets/CharcoalGold_thumb.jpg';
import FloralWrapCover from '../assets/mothersday_fullwrap4kJ_thumb.jpg';
import MustardPlumCover from '../assets/floral_final_fullwrap_thumb.jpg';
import SlateChevronCover from '../assets/slatechevron_thumb.jpg';
// Honest front-panel crops (true printed front face) for the full-wrap designs,
// so the preview shows only what actually prints, not the wrap/bleed edges.
import FloralWrapFrontPanel from '../assets/floral_wrap_front_panel_thumb.jpg';
import BurgWrapFrontPanel from '../assets/burg_wrap_front_panel_thumb.jpg';
import BurgWrapCover from '../assets/BurgFullWrapJ_thumb.jpg';
import { OCCASION_TYPES, CELEBRATION_PROMPTS, calculateDeadlines, createCelebrationBookData } from '../celebrationPrompts';
import { MEMORY_PROMPTS } from '../constants/prompts';
import { LAUNCH_COVER_DESIGNS, FULLWRAP_DESIGNS, BOTANICAL_FRAMED_PREVIEW, getFramedPhotoSlot, FLORAL_WRAP_PREVIEW, FLORAL_WRAP_TITLEONLY, floralTitleFontPt, floralFlourishWidthPt, floralWrapOpeningPercent } from '../constants/coverLayout';
import { getCoverDimensions as getCoverSheetDimensions, fullWrapPlacement } from '../constants/coverGeometry';
import MustardPlumFlourish from '../assets/mustard_plum_flourish.png';
import { applyPhotoTone } from '../utils/photoTone';
import { ORDER_API_BASE, TOPAZ_API_BASE } from '../constants/config';
import { cleanContributionContent, promptToChapterTitle } from '../utils/contentHelpers';
import { cleanPagesForSave, stBookEstimate, resolvablePageCount, withStoryRemovedFromBook, clearTombstones, originalKeyFromEnhanced } from '../utils/bookHelpers';
import { runStoryDelete } from '../utils/storyDeleteFlow';
import { isImageFile, normalizeImageForBook, checkPhotoQuality } from '../utils/imageHelpers';
import { getAuthorVantage } from '../utils/interviewEngine';
import ReorderableChapterList from '../components/ReorderableChapterList';
import ChapterSearchList from '../components/ChapterSearchList';
import BookSpreadPreview from '../components/BookSpreadPreview';
import ChapterPagePreview from '../components/ChapterPagePreview';
import {
  Image as PhImage, FileText as PhFileText, Layout as PhLayout, TextAlignLeft as PhAlignLeft,
  FrameCorners as PhFrame, TextT as PhType, Rows as PhLandscape, Columns as PhPortrait,
  Printer as PhPrinter, Package as PhPackage, House as PhHouse, User as PhUser,
  Palette as PhPalette, Camera as PhCamera, ArrowCounterClockwise as PhUndo,
  Sparkle as PhSparkle, BookOpenText as PhBookOpen, Envelope as PhMail,
  PencilSimple as PhPencil, Gear as PhGear, Users as PhUsers,
  Plus as PhPlus, MagnifyingGlass as PhSearch, Warning as PhWarning, Microphone,
} from '@phosphor-icons/react';

// Returns the Cognito idToken JWT string for authenticated Order API requests.
// Returns null if the session cannot be retrieved (should not happen in authenticated context).
async function getAuthToken() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (err) {
    console.error('[Auth] Failed to retrieve auth session:', err);
    return null;
  }
}

// Full-res cover images for PDF/print — served from public/, not bundled by webpack
const COVER_FULL_RES = {
  burgundy: '/covers/BurgFullWrapJ.jpg',
  mothersday: '/covers/MothersDayCover.png',
  linen: '/covers/GoldLinen.png',
  charcoalgold: '/covers/CharcoalGold.png',
  floralwrap: '/covers/mothersday_fullwrap4kJ.jpg',
  burgwrap: '/covers/BurgFullWrapJ.jpg',
  mustardplum: '/covers/floral_final_fullwrap.jpg',
  slatechevron: '/covers/slatechevron_fullwrap.jpg',
};

export default function BookBuilderPage() {
  const client = React.useMemo(() => generateClient(), []);
  const ctx = useAppContext();
  const {
    // User & auth
    isKeeper, isAdmin,
    // Navigation & mode
    setActiveTab, setAccountSection,
    // Memory Care coming-soon gate (F-12)
    MEMORY_CARE_ENABLED, openMemoryCareComingSoon, submitMemoryCareInterest,
    // Stories
    stories, setStories, refreshStories, executeDeleteStory,
    // Multi-book system
    books, booksRef, activeBookId, activeBook, isGiftBook, userDataLoaded,
    persistBooks, saveBookData, removeStoryPageEverywhere, removeStoryPageFromBook,
    // Account settings
    accountName, legacySubjectName,
    // Book builder state
    stBookPages, setStBookPages, stBookTitle, setStBookTitle,
    stBookSubtitle, setStBookSubtitle, stBookAuthor, setStBookAuthor,
    stBookDedication, setStBookDedication, stBookCover, setStBookCover, stBookCoverDesign, setStBookCoverDesign,
    stBookCoverPhotoPath, setStBookCoverPhotoPath,
    stBookCoverPhotoUrl, setStBookCoverPhotoUrl,
    stBookCoverPhotoBase64, setStBookCoverPhotoBase64, resolveCoverPhotoBase64,
    stBookCoverPhotoY, setStBookCoverPhotoY,
    stBookCoverPhotoOrientation, setStBookCoverPhotoOrientation,
    stBookCoverPhotoTone, setStBookCoverPhotoTone,
    stBookStep, setStBookStep, stBookOpen, setStBookOpen,
    stBookPreviewIdx, setStBookPreviewIdx,
    stBookSaveStatus,
    saveActiveBook, enterBook, exitToBookList, createPersonalBook, deleteBook,
    // Premium
    // Toast
    showToast,
    // Helpers
    syncGiftBookChapters,
    // Book builder extras
    showGiftCreate, setShowGiftCreate,
    hideGiftCta, setHideGiftCta,
    cbCreateStep, setCbCreateStep, cbOccasion, setCbOccasion,
    cbRecipientName, setCbRecipientName, cbDeliveryDate, setCbDeliveryDate,
    cbCustomOccasion, setCbCustomOccasion,
    cbAuthorRelationship, setCbAuthorRelationship,
    cbExtraCopies, setCbExtraCopies, cbExtraAddresses, setCbExtraAddresses,
    cbUseSameAddress, setCbUseSameAddress,
    expandedContributor, setExpandedContributor,
    stBookEditIdx, setStBookEditIdx,
    stBookPhotoUploading, setStBookPhotoUploading,
    stBookPhotoRef,
    stMemoryMode, setStMemoryMode, stMemoryTitle, setStMemoryTitle,
    stMemoryContent, setStMemoryContent, stMemoryPhotoPath, setStMemoryPhotoPath,
    stMemoryPhotoPreview, setStMemoryPhotoPreview, stMemoryPhotoBase64, setStMemoryPhotoBase64,
    stOrderStep, setStOrderStep, stOrderAddress, setStOrderAddress,
    stOrderId, setStOrderId, stOrderError, setStOrderError,
    stOrderLoading, setStOrderLoading, stOrderStatus, setStOrderStatus,
    stCheckingStatus, setStCheckingStatus,
    stInviteQuestion, setStInviteQuestion, stInviteMessage, setStInviteMessage,
    stInviteLink, setStInviteLink, stInviteLoading, setStInviteLoading,
    stInviteCopied, setStInviteCopied, stInviteName, setStInviteName,
    stInviteRelationship, setStInviteRelationship, setStInvites,
    stPreviewPhotos,
    photosResolving,
    setupBlankQrForAllChapters, blankQrBusy, isBookToolsOwner,
    photoQualityWarning, setPhotoQualityWarning,
    accountSettings, saveAccountSettings,
    stPhotoStoryLoading, setStPhotoStoryLoading,
    setStOriginalContent,
    chapterQrCodes,
    setChapterMediaModal,
    setChapterMediaType,
    chapterMediaSaving,
    removeChapterMedia, linkInterviewRecording, resetChapterMedia,
    streetInputRef,
    user,
    giftSyncedRef,
    // Navigation helpers for cross-tab links
    setStShowGateway, setStInterviewMode,
    setMemoryDementiaMode, setMemoryTab, setMemoryStep,
    // Memory Care subjects (for My Books fold)
    memoryCareSubjects,
    setMcSubject, setMcStep,
  } = ctx;

  // ── Floral cover photo tone (color / sepia / bw) ──
  // The chosen tone is baked into the pixels (photoTone util) once, and the
  // SAME baked data URL feeds both the on-screen preview and the print PDF, so
  // they match exactly (COVER-001). 'color' is a no-op passthrough. Only the
  // floral wrap design uses the treatment.
  const [stTonedCoverUrl, setStTonedCoverUrl] = React.useState('');
  React.useEffect(() => {
    let cancelled = false;
    const usesTone = stBookCoverDesign === 'mustardplum' && stBookCoverPhotoTone && stBookCoverPhotoTone !== 'color';
    if (!usesTone) { setStTonedCoverUrl(''); return; }
    (async () => {
      const base = (await resolveCoverPhotoBase64()) || stBookCoverPhotoBase64 || stBookCoverPhotoUrl;
      if (!base) { if (!cancelled) setStTonedCoverUrl(''); return; }
      try {
        const toned = await applyPhotoTone(base, stBookCoverPhotoTone);
        if (!cancelled) setStTonedCoverUrl(toned || '');
      } catch (err) {
        console.warn('[Cover Tone] bake failed, using original photo:', err);
        if (!cancelled) setStTonedCoverUrl('');
      }
    })();
    return () => { cancelled = true; };
  }, [stBookCoverDesign, stBookCoverPhotoTone, stBookCoverPhotoBase64, stBookCoverPhotoUrl, resolveCoverPhotoBase64]);

  // Cover photo source to DISPLAY in previews: the toned bake when the floral
  // tone treatment is active and ready, otherwise the raw photo.
  const coverPhotoDisplaySrc = (
    stBookCoverDesign === 'mustardplum' && stBookCoverPhotoTone !== 'color' && stTonedCoverUrl
  ) ? stTonedCoverUrl : (stBookCoverPhotoBase64 || stBookCoverPhotoUrl || '');

  // Floral wrap 3D-preview front face — mirrors FrontClassicFloralWrap /
  // FrontFramedFloralWrap in RemnantCoverPDF.js using the shared
  // FLORAL_WRAP_PREVIEW constants, so preview == PDF (COVER-001).
  //
  // COVER-002: the background is now the SAME single flat full-wrap image
  // the PDF paints once (no separate pre-cropped front-panel thumbnail —
  // that asset created a seam in the PDF and is retired here too), windowed
  // to the front-panel region using the exact same sheet geometry
  // (coverGeometry.getCoverDimensions) rather than a CSS background-position
  // approximation. Content is centered on the wreath opening's true,
  // script-derived, page-count-aware center (floralWrapOpeningPercent),
  // not a fixed fraction. All sizes scale with the rendered panel px width
  // `fw`.
  const renderFloralWrapPreview = () => {
    const isFramed = stBookCover === 'framed';
    const fw = Math.min(300, window.innerWidth - 64);
    const panelH = fw * 1.293; // preview front-cover div is fw wide x fw*1.293 tall
    const orient = stBookCoverPhotoOrientation === 'portrait' ? 'portrait' : 'landscape';
    const P = FLORAL_WRAP_PREVIEW;
    const L = FLORAL_WRAP_TITLEONLY;
    // Live page-count estimate (no interior PDF render needed for a preview) —
    // the checkout-time PDF always uses the real rendered page count and is
    // exact; this is a close, live-updating approximation (COVER-001 note
    // in coverLayout.js's floralWrapOpeningPercent).
    // F-251: count only pages that will actually print (a deleted story's page is skipped by the
    // PDF build), so the cover geometry is sized for the book that really comes out.
    const previewPageCount = stBookEstimate(resolvablePageCount(stBookPages, stories)).luluPages;
    const dim = getCoverSheetDimensions(previewPageCount);
    const { xPct, yPct } = floralWrapOpeningPercent(previewPageCount);
    // Title uses the same fit-to-width sizer as the PDF (COVER-001), scaled from
    // the 612pt front panel down to the preview's px width.
    const titlePx = floralTitleFontPt(stBookTitle, isFramed) * (fw / 612);
    const gapPx = P.gapRatio * fw;
    const flourishW = floralFlourishWidthPt(stBookTitle) * (fw / 612);
    // The true mat/keyline (4.9pt / 1pt) scale to sub-pixel at preview size and
    // the browser rounds them away unevenly. Floor them to 1px (keyline) / 2px
    // (cream) so the two-tone mat always renders uniformly on all four edges.
    const creamPad = Math.max(2, (P.matCreamPadPct / 100) * fw);
    const keyPad = Math.max(1, (P.keylinePadPct / 100) * fw);
    // COVER-001: 'LoraCover' is the self-hosted, exact-same Lora the PDF embeds,
    // so the preview wraps text identically to the print (a Georgia fallback is
    // wider and would wrap a one-line title to two).
    //
    // COVER-003: maxWidth is an ABSOLUTE PX value (mirroring the PDF's
    // `width: contentBoxW`, itself an absolute pt value — RemnantCoverPDF.js's
    // FrontFramedFloralWrap/FrontClassicFloralWrap), NOT a percentage. A
    // percentage here resolves against this element's PARENT, and in the
    // framed (with-photo) branch that parent is an auto/shrink-to-fit flex
    // column whose resolved width is driven by whichever child is widest —
    // in practice the photo mat box (a narrower, definite-width sibling), not
    // the full clearing width. That silently re-constrained the title to a
    // box narrower than the PDF's, causing it to wrap a line early even with
    // the correct font loaded (caught by
    // e2e/tests/06-cover-preview-regression.spec.ts's COVER-003 suite, which
    // measures the real rendered box, not just the font). A fixed px value
    // resolves the same regardless of any sibling's width, exactly like the
    // PDF's own absolute-pt box.
    const clearingMaxWidthPx = (P.clearingMaxWidthPct / 100) * fw;
    const titleStyle = { fontFamily: "'LoraCover', 'Lora', Georgia, serif", fontWeight: 700, fontSize: titlePx, lineHeight: 1.15, color: P.titleColor, textAlign: 'center', maxWidth: clearingMaxWidthPx };
    const subStyle = { fontFamily: "'LoraCover', 'Lora', Georgia, serif", fontStyle: 'italic', fontSize: P.subtitleRatio[isFramed ? 'framed' : 'classic'] * fw, color: P.subtitleColor, textAlign: 'center', lineHeight: 1.4, maxWidth: clearingMaxWidthPx };
    const authStyle = { fontFamily: "'LoraCover', 'Lora', Georgia, serif", fontStyle: 'italic', fontSize: P.authorRatio[isFramed ? 'framed' : 'classic'] * fw, color: P.subtitleColor, textAlign: 'center', maxWidth: clearingMaxWidthPx };
    // Single flat wrap image, placed by the SHARED coverGeometry.fullWrapPlacement
    // transform — the exact same one RemnantCoverPDF paints with (COVER-001), so
    // preview == PDF by construction. F-COVER-CROP: that transform anchors the
    // wreath's axis of symmetry to the front-panel center at every page count
    // (this replaced the old match-width windowing, which drifted the medallion
    // +0.10in right on short books). The preview box (inset:0) is the front-panel
    // viewport onto the sheet; convert the placement's PDF points to preview px
    // (pxPerPt) and offset by the front-panel's own sheet position.
    const pxPerPt = fw / dim.panelW;
    const pl = fullWrapPlacement(dim);
    const imgLeftPx = (pl.leftPt - dim.frontFullLeft) * pxPerPt;
    const imgTopPx = (pl.topPt - dim.frontFullTop) * pxPerPt;
    const imgWpx = pl.widthPt * pxPerPt;
    const imgHpx = pl.heightPt * pxPerPt;
    const bg = (
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        <img src={MustardPlumCover} alt="" style={{ position: 'absolute', left: imgLeftPx, top: imgTopPx, width: imgWpx, height: imgHpx, maxWidth: 'none' }} />
      </div>
    );
    const flourish = (w) => <img src={MustardPlumFlourish} alt="" style={{ width: w, height: w / 12, display: 'block' }} />;
    // Centered at xPct (the true opening x-center), not the panel's
    // geometric horizontal center — left:xPct% + translateX(-50%) centers
    // the (auto-width) row at that point regardless of its content width.
    const rowAt = (topFrac, node) => <div style={{ position: 'absolute', left: `${xPct}%`, transform: 'translateX(-50%)', top: topFrac * panelH, display: 'flex', justifyContent: 'center', zIndex: 1 }}>{node}</div>;

    // FRAMED (photo): title + cream/teal mat + subtitle/author, centered on the wreath opening.
    if (isFramed) {
      return (<>
        {bg}
        <div style={{ position: 'absolute', left: `${xPct}%`, top: `${yPct}%`, transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1 }}>
          <div data-testid="cover-preview-title" style={{ ...titleStyle, marginBottom: gapPx }}>{stBookTitle || 'Our Story'}</div>
          {coverPhotoDisplaySrc && (
            <div style={{ background: P.matCreamColor, padding: creamPad, marginBottom: gapPx }}>
              <div style={{ background: P.titleColor, padding: keyPad }}>
                <div style={{ width: (P.photoWidthPct[orient] / 100) * fw, aspectRatio: P.photoAspect[orient], overflow: 'hidden' }}>
                  <img loading="lazy" src={coverPhotoDisplaySrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `center ${stBookCoverPhotoY}%`, display: 'block' }} />
                </div>
              </div>
            </div>
          )}
          {stBookSubtitle && <div data-testid="cover-preview-subtitle" style={{ ...subStyle, marginBottom: 2 }}>{stBookSubtitle}</div>}
          {stBookAuthor && <div data-testid="cover-preview-author" style={{ ...authStyle, marginTop: 2 }}>{stBookAuthor}</div>}
        </div>
      </>);
    }

    // TITLE-ONLY distributed (D): spread title / flourish / subtitle / author down the clearing.
    if (stBookSubtitle || stBookAuthor) {
      return (<>
        {bg}
        {rowAt(L.titleTop, <div data-testid="cover-preview-title" style={titleStyle}>{stBookTitle || 'Our Story'}</div>)}
        {rowAt(L.flourishTop, flourish(flourishW))}
        {stBookSubtitle && rowAt(L.subtitleTop, <div data-testid="cover-preview-subtitle" style={subStyle}>{stBookSubtitle}</div>)}
        {stBookAuthor && rowAt(L.authorTop, <div data-testid="cover-preview-author" style={authStyle}>{stBookAuthor}</div>)}
      </>);
    }

    // BARE title (no subtitle/author): flourish high, title centered, flourish low —
    // spread down the clearing with larger ornaments so a single word is not floating.
    return (<>
      {bg}
      {rowAt(L.bareFlourishTop, flourish(flourishW * L.bareFlourishScale))}
      {rowAt(L.bareTitleTop, <div data-testid="cover-preview-title" style={titleStyle}>{stBookTitle || 'Our Story'}</div>)}
      {rowAt(L.bareFlourishBottom, flourish(flourishW * L.bareFlourishScale))}
    </>);
  };

  // Move-to-book picker state
  const [movePickerIdx, setMovePickerIdx] = React.useState(null);
  const [confirmRemoveChapter, setConfirmRemoveChapter] = React.useState(false);
  const [confirmDeleteChapter, setConfirmDeleteChapter] = React.useState(false);
  const [previewModalOpen, setPreviewModalOpen] = React.useState(false);

  // Delete book confirmation state
  const [confirmDeleteId, setConfirmDeleteId] = React.useState(null);

  // Enhance-all confirmation state
  const [confirmEnhanceAll, setConfirmEnhanceAll] = React.useState(null); // { count } or null
  const [cbStepError, setCbStepError] = React.useState(''); // celebration wizard: which required field is missing
  // My Book redesign: the "More" tools menu (collapsed by default to keep the view calm)
  const [moreOpen, setMoreOpen] = React.useState(false);
  // My Book redesign: the book switcher (tap the title to switch books / start a new one)
  const [switcherOpen, setSwitcherOpen] = React.useState(false);

  // Editable photo title on enhancement detail page
  const [isEditingPhotoTitle, setIsEditingPhotoTitle] = React.useState(false);
  const [photoTitleDraft, setPhotoTitleDraft] = React.useState('');

  // Enhancement state
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewData, setPreviewData] = React.useState(null);
  const [sliderPos, setSliderPos] = React.useState(50);
  const sliderRef = React.useRef(null);
  const sliderDragging = React.useRef(false);
  const [enhancePreviewPage, setEnhancePreviewPage] = React.useState(null);
  const [enhanceProcessingMsg, setEnhanceProcessingMsg] = React.useState('');
  const [enhanceAllLoading, setEnhanceAllLoading] = React.useState(false);
  const [enhanceAllProgress, setEnhanceAllProgress] = React.useState({ done: 0, total: 0 });
  const enhanceAllAbort = React.useRef(false);
  const [photoFilter, setPhotoFilter] = React.useState('all'); // 'all' | 'unenhanced' | 'enhanced' | 'low'

  // ── Promo code state (TB-078) — local to the address/checkout flow ──
  const [stPromoCode, setStPromoCode] = React.useState('');
  const [stPromoValidation, setStPromoValidation] = React.useState(null);
  // { valid: bool, discountCents: N, finalPriceCents: N, error: '...' } | null
  const [stPromoLoading, setStPromoLoading] = React.useState(false);

  // ── F-163: cap-aware SALE display + auto-apply (single source = /order/validate-promo) ──
  const saleOffer = useSaleOffer();
  // Auto-apply the discount the moment the user reaches the order flow — no typing required — but ONLY if the
  // server says it is live (honest revert if capped/inactive) and the user has not entered their own code
  // (e.g. a gift code). The server still bakes the real discount at checkout; this only pre-fills the applied
  // state so the price shows correctly up front. GIFT-002 preserved (no session-level Stripe coupon).
  React.useEffect(() => {
    if (saleOffer.loading || !saleOffer.saleActive) return;
    const inOrderFlow = stOrderStep === 'address' || stOrderStep === 'address_checkout' || stOrderStep === 'upsell';
    if (!inOrderFlow) return;
    if (stPromoCode || stPromoValidation) return; // never clobber a code the user typed/applied
    setStPromoCode(SALE_OFFER.code);
    setStPromoValidation({ valid: true, discountCents: saleOffer.discountCents, finalPriceCents: saleOffer.saleCents, autoApplied: true });
  }, [saleOffer.loading, saleOffer.saleActive, saleOffer.discountCents, saleOffer.saleCents, stOrderStep, stPromoCode, stPromoValidation]);

  // F-163: the price shown on the order surfaces — reflects the applied code (auto TALE30 or a typed gift code),
  // else the live sale, else the anchor. The "Save $30" tag is TALE30-only (a gift code shows its own price).
  const orderPricing = () => {
    const applied = stPromoValidation && stPromoValidation.valid ? stPromoValidation : null;
    const finalCents = applied && typeof applied.finalPriceCents === 'number'
      ? applied.finalPriceCents
      : (saleOffer.saleActive ? saleOffer.saleCents : SALE_OFFER.anchorCents);
    const onSale = finalCents < SALE_OFFER.anchorCents;
    const isSaleOffer = onSale && (applied ? stPromoCode === SALE_OFFER.code : saleOffer.saleActive);
    return { anchorCents: SALE_OFFER.anchorCents, finalCents, onSale, isSaleOffer };
  };

  // ── Backfill photoEnhanced flag from enhancedPhotos array ──
  // Wrap designs (floral, burgundy, mustardplum) only support the Framed layout —
  // classic text-only doesn't work over a full-bleed wrap illustration. Auto-normalize
  // any stale 'classic' state whenever a wrap design is selected.
  //
  // F-76 (2026-07-11): this used to hardcode ['floralwrap', 'burgwrap'], a duplicate
  // of FULLWRAP_DESIGNS (constants/coverLayout.js) that drifted when 'mustardplum' was
  // added there (8ac0d7c) but not here. Because 8aa5f69 then made 'mustardplum' the
  // DEFAULT new-book coverDesign, every new book got stuck on stBookCover:'classic' —
  // the Cover Layout picker is display:none for FULLWRAP_DESIGNS (line ~3158) so there
  // was no manual way to reach 'framed' either, hiding the photo-upload option and
  // rendering the title in the title-only layout instead of the framed/photo layout
  // (both in the live preview AND the checkout-time PDF — RemnantCoverPDF.js keys off
  // the same coverStyle field). Now reads the single source of truth instead of a
  // second hardcoded list (Change Protocol rule 3: duplicated things get deduped).
  React.useEffect(() => {
    if (FULLWRAP_DESIGNS.includes(stBookCoverDesign) && stBookCover !== 'framed') {
      setStBookCover('framed');
    }
  }, [stBookCoverDesign, stBookCover, setStBookCover]);

  // Photos enhanced before the persistence fix are missing photoEnhanced on saved pages.
  // Enhanced keys follow the pattern: original_enhanced.ext (e.g. photo.jpg → photo_enhanced.jpg)
  React.useEffect(() => {
    const enhancedKeys = activeBook?.enhancedPhotos;
    if (!enhancedKeys?.length || !stBookPages?.length) return;
    // Build set of original paths that have an enhanced version
    const enhancedOriginals = new Set();
    for (const ek of enhancedKeys) {
      // Reverse the _enhanced suffix to get the original key
      const match = ek.match(/^(.+)_enhanced(\.[^.]+)$/);
      if (match) enhancedOriginals.add(match[1] + match[2]);
      // Also add the enhanced key itself (in case photoPath was already swapped)
      enhancedOriginals.add(ek);
    }
    let needsUpdate = false;
    const reconciled = stBookPages.map(p => {
      if (p.photoEnhanced || !p.photoPath) return p;
      if (enhancedOriginals.has(p.photoPath)) {
        needsUpdate = true;
        return { ...p, photoEnhanced: true };
      }
      return p;
    });
    if (needsUpdate) {
      setStBookPages(reconciled);
      saveBookData({ pages: cleanPagesForSave(reconciled) });
    }
  }, [activeBook?.enhancedPhotos, activeBook?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetGiftCreate = () => { setCbCreateStep(0); setCbOccasion(null); setCbRecipientName(''); setCbDeliveryDate(''); setCbCustomOccasion(''); setCbAuthorRelationship(''); setShowGiftCreate(false); };

  // ── finishGiftCreate ──
  const finishGiftCreate = () => {
    const cbData = createCelebrationBookData({
      occasion: cbOccasion,
      recipientName: cbRecipientName.trim(),
      deliveryDate: cbDeliveryDate,
      organizerName: accountName || user?.username || '',
    });
    // Convert celebration book data to unified book format
    const newBook = {
      id: cbData.id, bookType: 'GIFT',
      title: cbData.bookTitle, subtitle: cbData.subtitle || '', author: 'From Your Family',
      dedication: cbData.dedication || '', coverStyle: cbData.coverTemplate || 'classic',
      // Occasion-aware default cover: a bright floral wrap is wrong for a memorial.
      coverDesign: cbOccasion === 'IN_MEMORY' ? 'burgwrap' : 'floralwrap',
      coverPhotoPath: null, pages: [],
      occasion: cbData.occasion, recipientName: cbData.recipientName,
      deliveryDate: cbData.deliveryDate, contributionDeadline: cbData.contributionDeadline,
      orderByDate: cbData.orderByDate, contributors: [],
      createdAt: cbData.createdAt,
      authorRelationship: (cbAuthorRelationship || '').trim() || null,
    };
    if (cbOccasion === 'OTHER' && cbCustomOccasion.trim()) {
      newBook.occasionCustomName = cbCustomOccasion.trim();
      newBook.title = `A Book for ${cbRecipientName.trim()}`;
    }
    const updated = [...booksRef.current, newBook];
    persistBooks(updated);
    enterBook(newBook.id);
    resetGiftCreate();
    // Occasion-aware: never say "Celebration" over an In-Memory (grief) book.
    showToast(cbOccasion === 'IN_MEMORY' ? 'Your memory book is ready.' : 'Celebration Book created!');
  };

  // ── handleEnhancePhoto — confirm then navigate to enhancement preview (step 8) ──
  const [confirmEnhancePage, setConfirmEnhancePage] = React.useState(null);
  const [enhanceReturnStep, setEnhanceReturnStep] = React.useState(0);
  const [confirmRevertPage, setConfirmRevertPage] = React.useState(null);

  // F-312: never OFFER a revert we cannot perform.
  //
  // When the page stores `originalPhotoPath` we trust it — the app wrote it. When it does not (every
  // photo enhanced before F-301, because the merge discarded page-field writes) the original key is
  // DERIVED from the enhancer's `<original>_preview.<ext>` naming. Derivation is a convention, not a
  // fact about storage, so the object is checked before the dialog appears. The failure mode this
  // rules out is the worse one: offering an undo, taking the tap, and swapping the photo to a key
  // that 404s — a customer would see their photo vanish and have no way back. Failing here costs one
  // honest sentence instead.
  const offerRevert = async (page) => {
    if (page.originalPhotoPath || (page.storyId === '__cover__' && activeBook?.coverPhotoOriginalPath)) {
      setConfirmRevertPage(page);
      return;
    }
    const derived = originalKeyFromEnhanced(page.photoPath);
    if (!derived) {
      showToast("This photo's original isn't available to restore.", 'error');
      return;
    }
    try {
      const fileName = derived.split('/').pop();
      await getUrl({ path: ({ identityId }) => `private/${identityId}/photos/${fileName}`,
                     options: { validateObjectExistence: true } });
      setConfirmRevertPage({ ...page, originalPhotoPath: derived });
    } catch (err) {
      console.warn('[Revert] original not found in storage:', derived, err?.message);
      showToast("This photo's original isn't available to restore.", 'error');
    }
  };

  const handleEnhancePhoto = (page) => {
    // F-311: an ALREADY-ENHANCED photo offers REVERT. This used to `return` and do nothing at all,
    // while the FAQ two inches down the same screen said "Tap any enhanced photo and choose 'Revert
    // to original photo.'" The copy described the right product; the behaviour is what was wrong.
    if (page.photoEnhanced) { offerRevert(page); return; }
    // Show confirmation with remaining credits
    setConfirmEnhancePage(page);
  };
  const proceedWithEnhance = (page) => {
    setConfirmEnhancePage(null);
    setEnhanceReturnStep(stBookStep); // remember where we came from
    setEnhancePreviewPage(page);
    setPreviewData(null);
    setPreviewLoading(false);
    setEnhanceProcessingMsg('');
    setSliderPos(50);
    setIsEditingPhotoTitle(false);
    setStBookStep(8);
    window.scrollTo(0, 0);
  };

  // Revert an enhanced photo to its original
  const revertEnhancedPhoto = (page) => {
    // F-311: DERIVE the original when it was never stored. `originalPhotoPath` is written by
    // runEnhancement, and until F-301 every page-field write was discarded by the merge — so for
    // any photo enhanced before that fix the pointer back does not exist. Reading only the stored
    // field would leave revert inert on exactly the books that need it most, including the first
    // paying customer's. The enhancer's `<original>_preview.<ext>` naming is a code convention, so
    // the original key is recoverable from the enhanced one.
    const derived = originalKeyFromEnhanced(page.photoPath);

    // Cover photo revert
    if (page.storyId === '__cover__') {
      const origPath = activeBook?.coverPhotoOriginalPath || derived;
      if (!origPath) return;
      setStBookCoverPhotoPath(origPath);
      setStBookCoverPhotoUrl(''); // will re-resolve from path
      setStBookCoverPhotoBase64('');
      const enhancedPhotos = (activeBook?.enhancedPhotos || []).filter(k => k !== page.photoPath);
      saveBookData({
        coverPhotoPath: origPath,
        coverPhotoOriginalPath: null,
        coverPhotoOriginalUrl: null,
        coverPhotoEnhanced: false,
        enhancedPhotos,
      });
      showToast('Cover photo reverted to original');
      setStBookStep(9);
      setEnhancePreviewPage(null);
      window.scrollTo(0, 0);
      return;
    }

    if (!page.originalPhotoPath && !derived) return; // nothing to go back to; never blank the photo
    const updatedPages = stBookPages.map(p =>
      p.storyId === page.storyId ? {
        ...p,
        photoPath: p.originalPhotoPath || derived,
        photoUrl: p.originalPhotoUrl || null,
        photoBase64: p.originalPhotoBase64 || null,
        photoEnhanced: false,
        originalPhotoPath: undefined,
        originalPhotoUrl: undefined,
        originalPhotoBase64: undefined,
      } : p
    );
    setStBookPages(updatedPages);
    // Remove from enhancedPhotos array
    const enhancedPhotos = (activeBook?.enhancedPhotos || []).filter(k => k !== page.photoPath);
    saveBookData({ pages: cleanPagesForSave(updatedPages), enhancedPhotos });
    showToast('Photo reverted to original');
    setStBookStep(9);
    setEnhancePreviewPage(null);
    window.scrollTo(0, 0);
  };

  // Run Topaz enhancement and show before/after
  const runEnhancement = async (page) => {
    setPreviewLoading(true);
    setPreviewData(null);
    setSliderPos(50);
    const messages = [
      'Analyzing your photo...',
      'Enhancing resolution for print...',
      'Sharpening faces and detail...',
      'Optimizing colors for paper...',
      'Almost there...',
    ];
    let msgIdx = 0;
    setEnhanceProcessingMsg(messages[0]);
    const msgInterval = setInterval(() => {
      msgIdx = Math.min(msgIdx + 1, messages.length - 1);
      setEnhanceProcessingMsg(messages[msgIdx]);
    }, 7000);
    try {
      // TB-085: Topaz now requires a Cognito ID token (server-side auth + cap).
      const topazToken = await getAuthToken();
      const resp = await fetch(TOPAZ_API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(topazToken ? { 'Authorization': `Bearer ${topazToken}` } : {}) },
        body: JSON.stringify({ action: 'preview_enhance', keys: [page.photoPath] }),
      });
      if (resp.status === 429 || resp.status === 503) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.error || "You've reached today's photo enhancement limit. Reach out to support@taleboundbook.com if you need more.");
      }
      const data = await resp.json();
      console.log('[Photo AI] Response:', JSON.stringify(data));
      if (data.previews?.length > 0) {
        setSliderPos(100); // start showing full original
        setPreviewData(data.previews[0]);
        // Smooth animated reveal: original → sweep to center to show enhanced
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const startTime = performance.now();
        const duration = 1200; // ms
        const animate = (now) => {
          const elapsed = now - startTime;
          const t = Math.min(elapsed / duration, 1);
          // Ease-out cubic for a satisfying deceleration
          const eased = 1 - Math.pow(1 - t, 3);
          setSliderPos(100 - (eased * 50)); // 100 → 50
          if (t < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      } else {
        const failMsg = data.failed?.[0]?.error || data.error || 'Unknown error';
        console.error('[Photo AI] Enhancement failed:', failMsg);
        showToast(`Enhancement failed: ${failMsg}`, 'error');
      }
    } catch (err) {
      console.error('Enhancement failed:', err);
      showToast('Enhancement failed — please try again', 'error');
    }
    clearInterval(msgInterval);
    setPreviewLoading(false);
    setEnhanceProcessingMsg('');
  };

  // Apply enhanced photo to the page (swap paths, fetch base64 for PDF)
  const applyEnhancedPhoto = async (preview) => {
    const page = enhancePreviewPage;
    if (!page || !preview) return;
    // Fetch enhanced image as base64 for PDF rendering
    let enhancedBase64 = null;
    try {
      const imgResp = await fetch(preview.enhancedUrl);
      const blob = await imgResp.blob();
      enhancedBase64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn('Enhanced base64 conversion failed:', err);
    }

    // Cover photo enhancement — update cover state instead of stBookPages
    if (page.storyId === '__cover__') {
      const origPath = stBookCoverPhotoPath;
      // Resolve a fresh signed URL for the enhanced key (presigned preview URL will expire)
      let resolvedUrl = preview.enhancedUrl;
      let resolvedBase64 = enhancedBase64;
      try {
        const enhancedFileName = preview.enhancedKey.split('/').pop();
        const pathFn = ({ identityId }) => `private/${identityId}/photos/${enhancedFileName}`;
        const urlResult = await getUrl({ path: pathFn });
        if (urlResult?.url) resolvedUrl = urlResult.url.toString();
        // Fetch base64 from the resolved URL
        if (!resolvedBase64) {
          const imgResp = await fetch(resolvedUrl);
          const blob = await imgResp.blob();
          resolvedBase64 = await new Promise((res, rej) => {
            const reader = new FileReader();
            reader.onload = () => res(reader.result);
            reader.onerror = rej;
            reader.readAsDataURL(blob);
          });
        }
      } catch (err) {
        console.warn('[Cover Photo] Enhanced URL resolve failed:', err);
      }
      setStBookCoverPhotoPath(preview.enhancedKey);
      setStBookCoverPhotoUrl(resolvedUrl);
      setStBookCoverPhotoBase64(resolvedBase64 || stBookCoverPhotoBase64);
      const freeUsed = accountSettings.enhancementsUsed || 0;
      if (freeUsed < 10) {
        saveAccountSettings({ enhancementsUsed: freeUsed + 1 });
      }
      saveBookData({
        coverPhotoPath: preview.enhancedKey,
        coverPhotoOriginalPath: origPath,
        coverPhotoEnhanced: true,
        enhancedPhotos: [...(activeBook?.enhancedPhotos || []), preview.enhancedKey],
      });
      const remaining = Math.max(0, 9 - freeUsed);
      showToast(`Cover photo enhanced! ${remaining} enhancement${remaining !== 1 ? 's' : ''} remaining`);
      setStBookStep(9);
      setEnhancePreviewPage(null);
      window.scrollTo(0, 0);
      return;
    }

    // Swap photo on the page, store originals for revert
    const updatedPages = stBookPages.map(p =>
      p.storyId === page.storyId ? {
        ...p,
        originalPhotoPath: p.originalPhotoPath || p.photoPath,
        originalPhotoUrl: p.originalPhotoUrl || p.photoUrl,
        originalPhotoBase64: p.originalPhotoBase64 || p.photoBase64,
        photoPath: preview.enhancedKey,
        photoUrl: preview.enhancedUrl,
        photoBase64: enhancedBase64 || p.photoBase64,
        photoEnhanced: true,
      } : p
    );
    setStBookPages(updatedPages);
    // Track free usage
    const freeUsed = accountSettings.enhancementsUsed || 0;
    if (freeUsed < 10) {
      saveAccountSettings({ enhancementsUsed: freeUsed + 1 });
    }
    // Track enhanced photos on the book + persist updated pages
    saveBookData({
      enhancedPhotos: [...(activeBook?.enhancedPhotos || []), preview.enhancedKey],
      pages: cleanPagesForSave(updatedPages),
    });
    const remaining = Math.max(0, 9 - freeUsed);
    showToast(`Photo enhanced! ${remaining} enhancement${remaining !== 1 ? 's' : ''} remaining`);
    const isCoverPhoto = enhancePreviewPage?.storyId === '__cover__';
    setStBookStep(isCoverPhoto ? 9 : enhanceReturnStep || 0);
    setEnhancePreviewPage(null);
    window.scrollTo(0, 0);
  };

  // Enhance all remaining un-enhanced photos (after unlimited unlocked)
  const handleEnhanceAll = async () => {
    const unenhanced = stBookPages.filter(p => (p.photoUrl || p.photoPath) && !p.photoEnhanced);
    if (unenhanced.length === 0) { showToast('All photos are already enhanced!'); return; }
    setConfirmEnhanceAll({ count: unenhanced.length });
  };

  const executeEnhanceAll = async () => {
    const unenhanced = stBookPages.filter(p => (p.photoUrl || p.photoPath) && !p.photoEnhanced);
    enhanceAllAbort.current = false;
    setEnhanceAllLoading(true);
    setEnhanceAllProgress({ done: 0, total: unenhanced.length });
    let enhanced = 0;
    const newEnhancedKeys = [];
    for (const page of unenhanced) {
      if (enhanceAllAbort.current) break;
      try {
        // TB-085: Topaz now requires a Cognito ID token (server-side auth + cap).
        const topazToken = await getAuthToken();
        const resp = await fetch(TOPAZ_API_BASE, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(topazToken ? { 'Authorization': `Bearer ${topazToken}` } : {}) },
          body: JSON.stringify({ action: 'preview_enhance', keys: [page.photoPath] }),
        });
        if (resp.status === 429 || resp.status === 503) {
          const errBody = await resp.json().catch(() => ({}));
          throw new Error(errBody.error || "You've reached today's photo enhancement limit. Reach out to support@taleboundbook.com if you need more.");
        }
        const data = await resp.json();
        if (data.previews?.length > 0) {
          const preview = data.previews[0];
          let enhancedBase64 = null;
          try {
            const imgResp = await fetch(preview.enhancedUrl);
            const blob = await imgResp.blob();
            enhancedBase64 = await new Promise((res, rej) => {
              const reader = new FileReader();
              reader.onload = () => res(reader.result);
              reader.onerror = rej;
              reader.readAsDataURL(blob);
            });
          } catch (b64Err) {
            console.warn('Enhanced base64 conversion failed:', b64Err);
          }
          // Update React state only (no DB write per photo)
          setStBookPages(prev => prev.map(p =>
            p.storyId === page.storyId ? {
              ...p,
              originalPhotoPath: p.originalPhotoPath || p.photoPath,
              originalPhotoUrl: p.originalPhotoUrl || p.photoUrl,
              originalPhotoBase64: p.originalPhotoBase64 || p.photoBase64,
              photoPath: preview.enhancedKey,
              photoUrl: preview.enhancedUrl,
              photoBase64: enhancedBase64 || p.photoBase64,
              photoEnhanced: true,
            } : p
          ));
          newEnhancedKeys.push(preview.enhancedKey);
          enhanced++;
        }
      } catch (err) {
        console.error(`Enhancement failed for ${page.photoPath}:`, err);
      }
      setEnhanceAllProgress({ done: enhanced, total: unenhanced.length });
    }
    const wasCancelled = enhanceAllAbort.current;
    enhanceAllAbort.current = false;
    setEnhanceAllLoading(false);
    // Persist once at the end — pages + enhanced photo keys
    if (enhanced > 0) {
      setStBookPages(prev => {
        saveBookData({
          pages: cleanPagesForSave(prev),
          enhancedPhotos: [...(activeBook?.enhancedPhotos || []), ...newEnhancedKeys],
        });
        return prev;
      });
    }
    showToast(wasCancelled
      ? `Stopped — ${enhanced} photo${enhanced !== 1 ? 's' : ''} enhanced before cancellation`
      : enhanced > 0
        ? `${enhanced} photo${enhanced !== 1 ? 's' : ''} enhanced!`
        : 'Enhancement could not be completed — please try again');
  };

  // ── handleStBookPhoto ──
  const handleStBookPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || stBookEditIdx === null) return;
    if (!isImageFile(file)) { showToast('Please choose an image file.', 'error'); return; }
    if (file.size > 10 * 1024 * 1024) { showToast('Image must be under 10 MB.', 'error'); return; }
    setStBookPhotoUploading(true);
    try {
      // Normalize HEIC/WebP to JPEG — everything else passes through unchanged
      const normalizedFile = await normalizeImageForBook(file);
      const ext = normalizedFile.name.split('.').pop() || 'jpg';
      const fileName = `book_photo_${Date.now()}.${ext}`;
      const pathBuilder = ({ identityId }) => `private/${identityId}/photos/${fileName}`;
      const uploadTask = uploadData({
        path: pathBuilder,
        data: normalizedFile,
        options: { contentType: normalizedFile.type, cacheControl: 'public, max-age=31536000, immutable' }
      });
      const uploadResult = await uploadTask.result;
      const storagePath = uploadResult?.path;
      if (!storagePath) throw new Error('Upload succeeded but no path returned');
      console.log('[Photo Upload] storagePath saved:', storagePath);
      // Get a fresh signed URL for immediate display (don't use blob URL)
      const urlResult = await getUrl({ path: storagePath });
      const signedUrl = urlResult?.url?.toString() || URL.createObjectURL(normalizedFile);
      // Also convert to base64 immediately so book preview can render without waiting
      let photoBase64 = null;
      try {
        photoBase64 = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = () => res(reader.result);
          reader.onerror = rej;
          reader.readAsDataURL(normalizedFile);
        });
        console.log('[Photo Upload] Base64 cached at upload time:', `${Math.round(normalizedFile.size/1024)}KB`);
      } catch (b64Err) {
        console.warn('[Photo Upload] Base64 conversion failed:', b64Err.message);
      }
      // Check photo quality for print
      const currentLayout = stBookPages[stBookEditIdx]?.layout || 'photo-spread';
      const quality = await checkPhotoQuality(normalizedFile, currentLayout);
      setPhotoQualityWarning(quality.level !== 'good' ? quality : null);
      // Update page with photo data + quality level for enhancement badge
      const updated = [...stBookPages];
      updated[stBookEditIdx] = {
        ...updated[stBookEditIdx],
        photoUrl: signedUrl, photoPath: storagePath, photoBase64,
        photoQuality: quality.level,
        photoEnhanced: false,
      };
      setStBookPages(updated);
      // Persist photoPath on the story itself so it survives remove/re-add
      const storyId = updated[stBookEditIdx]?.storyId;
      if (storyId && storagePath) {
        try { await client.graphql({ query: updateLegacyEntry, variables: { input: { id: storyId, photoPath: storagePath } } }); } catch (e) { console.warn('Failed to save photoPath on story:', e); }
      }
      showToast('Photo added!');
    } catch (err) {
      console.error('Book photo upload error:', err);
      showToast('Something went wrong uploading your photo. Please try again.', 'error');
    }
    setStBookPhotoUploading(false);
    if (stBookPhotoRef.current) stBookPhotoRef.current.value = '';
  };

  // ── handlePhotoStory ──
  const handlePhotoStory = async (description, pageIdx) => {
    if (!description?.trim()) return;
    setStPhotoStoryLoading(true);
    try {
      const page = stBookPages[pageIdx];
      const story = stories.find(s => s.id === page?.storyId);
      const data = await callAi({
        action: 'expand',
        messages: [{ role: 'user', content: description }],
        roughDraft: `Write a photo caption (2-3 sentences) for a keepsake book chapter${story ? ` titled "${story.title}"` : ''}. Use their description as-is — don't add poetic language or emotions they didn't express. Just describe what's in the photo in a warm, simple way.\n\nTheir description: "${description}"`,
      });
      if (data.text) {
        const updated = [...stBookPages];
        updated[pageIdx] = { ...updated[pageIdx], caption: data.text.trim() };
        setStBookPages(updated);
        showToast('Caption created!');
      }
    } catch (err) { showToast('Caption generation failed.', 'error'); }
    setStPhotoStoryLoading(false);
  };

  // ── renderCbCreateWizard ──
  const renderCbCreateWizard = () => {
    const occasionType = OCCASION_TYPES.find(o => o.id === cbOccasion);
    const deadlines = cbDeliveryDate ? calculateDeadlines(cbDeliveryDate) : null;
    const today = new Date().toISOString().split('T')[0];
    const threeWeeksOut = new Date(Date.now() + 21 * 86400000).toISOString().split('T')[0];
    const tooSoon = cbDeliveryDate && cbDeliveryDate < threeWeeksOut;

    return (
      <div className="storyteller-content" data-journey-step="book-gift-create" style={{ maxWidth: 560, margin: '0 auto' }}>
        <button onClick={resetGiftCreate} className="st-back" style={{ textAlign: 'left', width: '100%' }}>← Cancel</button>

        {/* Progress dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 28 }}>
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: i <= cbCreateStep ? '#0f172a' : 'rgba(15,23,42,0.15)', transition: 'background 0.2s' }} />
          ))}
        </div>

        {/* Step 0: Pick occasion */}
        {cbCreateStep === 0 && (
          <div style={{ textAlign: 'center' }}>
            <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 8 }}>Create a book for someone you love</h2>
            <p className="st-sub" style={{ marginBottom: 32 }}>Gather stories from the people who know them, and turn them into a keepsake hardcover.</p>
            <p className="st-label" style={{ marginBottom: 16, fontSize: '1.05rem', color: '#3c4653' }}>What's the occasion?</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {OCCASION_TYPES.map(o => (
                <button key={o.id} onClick={() => { setCbOccasion(o.id); setCbCreateStep(1); }}
                  style={{
                    padding: '24px 16px', background: '#fff', border: '2px solid rgba(15,23,42,0.1)', borderRadius: 14,
                    cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = o.accent; e.currentTarget.style.background = `${o.accent}08`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(15,23,42,0.1)'; e.currentTarget.style.background = '#fff'; }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>{o.emoji}</div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', fontWeight: 500, color: '#0f172a' }}>{o.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Recipient name */}
        {cbCreateStep === 1 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{occasionType?.emoji}</div>
            <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 8 }}>Who is this book for?</h2>
            <p className="st-sub" style={{ marginBottom: 28 }}>Enter their name or what everyone calls them.</p>
            {cbOccasion === 'OTHER' && (
              <div style={{ marginBottom: 20 }}>
                <label className="st-label">What's the occasion?</label>
                <input className="st-input" value={cbCustomOccasion} onChange={e => { setCbCustomOccasion(e.target.value); if (cbStepError) setCbStepError(''); }}
                  placeholder="e.g. Anniversary, Valentine's, Retirement, Graduation, Just because" style={{ textAlign: 'center' }} />
                {cbStepError === 'occasion' && <p style={{ color: '#c0392b', fontSize: '0.9rem', marginTop: 6 }}>Add the occasion to continue.</p>}
              </div>
            )}
            <input className="st-input" value={cbRecipientName} onChange={e => { setCbRecipientName(e.target.value); if (cbStepError) setCbStepError(''); }}
              placeholder="e.g. Mom, Grandma Rose, Dad" autoFocus
              style={{ textAlign: 'center', fontSize: '1.2rem', marginBottom: cbStepError === 'name' ? 6 : 24 }} />
            {cbStepError === 'name' && <p style={{ color: '#c0392b', fontSize: '0.9rem', marginBottom: 18 }}>Please enter their name to continue.</p>}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => setCbCreateStep(0)} className="st-btn-secondary">← Back</button>
              <button onClick={() => {
                if (cbOccasion === 'OTHER' && !cbCustomOccasion.trim()) { setCbStepError('occasion'); return; }
                if (!cbRecipientName.trim()) { setCbStepError('name'); return; }
                setCbStepError(''); setCbCreateStep(2);
              }} className="st-btn-primary">
                Next →
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Author relationship (new) */}
        {cbCreateStep === 2 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{occasionType?.emoji}</div>
            <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 8 }}>How do you know {cbRecipientName || 'them'}?</h2>
            <p className="st-sub" style={{ marginBottom: 28 }}>This helps us phrase the questions the right way for you.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, maxWidth: 400, margin: '0 auto 24px' }}>
              {[
                { label: 'Partner or spouse', value: 'partner', note: 'We use your shared story' },
                { label: 'Son or daughter', value: 'child' },
                { label: 'Grandchild', value: 'grandchild' },
                { label: 'Parent', value: 'parent' },
                { label: 'Grandparent', value: 'grandparent' },
                { label: 'Sibling', value: 'sibling' },
                { label: 'Friend', value: 'friend' },
                { label: 'Colleague or coworker', value: 'colleague' },
                { label: 'Other', value: 'other' },
              ].map(opt => {
                const selected = cbAuthorRelationship === opt.value;
                return (
                  <button key={opt.value} onClick={() => { setCbAuthorRelationship(opt.value); setCbStepError(''); }}
                    style={{
                      padding: '16px 20px', background: selected ? 'rgba(15,23,42,0.06)' : '#fff',
                      border: `2px solid ${selected ? '#0f172a' : 'rgba(15,23,42,0.12)'}`,
                      borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                    <span style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#0f172a' }}>{opt.label}</span>
                    {opt.note && <span style={{ fontSize: '0.85rem', color: '#8a7e72', fontStyle: 'italic' }}>{opt.note}</span>}
                  </button>
                );
              })}
            </div>
            {cbStepError === 'rel' && <p style={{ color: '#c0392b', fontSize: '0.9rem', marginBottom: 12 }}>Pick one so Pip can phrase the questions for you.</p>}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => setCbCreateStep(1)} className="st-btn-secondary">← Back</button>
              <button onClick={() => { if (!cbAuthorRelationship) { setCbStepError('rel'); return; } setCbStepError(''); setCbCreateStep(3); }} className="st-btn-primary">Next →</button>
            </div>
          </div>
        )}

        {/* Step 3: Delivery date (was step 2) */}
        {cbCreateStep === 3 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{occasionType?.emoji}</div>
            <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 8 }}>When should it arrive?</h2>
            <p className="st-sub" style={{ marginBottom: 28 }}>Pick the date you want the book in hand.</p>
            <input type="date" className="st-input" value={cbDeliveryDate} onChange={e => setCbDeliveryDate(e.target.value)}
              min={today} style={{ textAlign: 'center', fontSize: '1.1rem', marginBottom: 16 }} />
            {deadlines && (
              <div style={{ padding: '16px 20px', background: tooSoon ? 'rgba(220,80,60,0.06)' : 'rgba(15,23,42,0.04)', borderRadius: 12, border: `1px solid ${tooSoon ? 'rgba(220,80,60,0.2)' : 'rgba(15,23,42,0.1)'}`, marginBottom: 20, textAlign: 'left' }}>
                <div style={{ fontSize: '0.95rem', color: '#3c4653', lineHeight: 1.5 }}>
                  Have your book ready by <strong style={{ color: '#0f172a' }}>{new Date(deadlines.orderByDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong> <span style={{ color: '#8a7e72', fontStyle: 'italic' }}>(recommended)</span>.
                </div>
                {tooSoon && (
                  <div style={{ marginTop: 10, fontSize: '0.95rem', color: '#c53030', fontWeight: 500 }}>
                    This delivery date may be too tight. Printing and shipping take about 2 weeks — consider a later date to give yourself room to finish.
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => setCbCreateStep(2)} className="st-btn-secondary">← Back</button>
              <button onClick={() => setCbCreateStep(4)} className="st-btn-primary" disabled={!cbDeliveryDate}>Next →</button>
            </div>
          </div>
        )}

        {/* Step 4: Confirm & create (was step 3) */}
        {cbCreateStep === 4 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{occasionType?.emoji}</div>
            <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 8 }}>Ready to create!</h2>
            <p className="st-sub" style={{ marginBottom: 28 }}>Here's a summary. You can change everything later.</p>
            <div style={{ padding: '24px', background: '#fff', border: '2px solid rgba(15,23,42,0.1)', borderRadius: 16, marginBottom: 24, textAlign: 'left' }}>
              <div style={{ marginBottom: 12 }}>
                <span style={{ fontSize: '0.9rem', color: '#3c4653', textTransform: 'uppercase', letterSpacing: 1.5 }}>Occasion</span>
                <div style={{ fontSize: '1.1rem', color: '#0f172a', fontFamily: "'Lora', serif", marginTop: 2 }}>
                  {occasionType?.emoji} {cbOccasion === 'OTHER' ? cbCustomOccasion : occasionType?.label}
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <span style={{ fontSize: '0.9rem', color: '#3c4653', textTransform: 'uppercase', letterSpacing: 1.5 }}>Recipient</span>
                <div style={{ fontSize: '1.1rem', color: '#0f172a', fontFamily: "'Lora', serif", marginTop: 2 }}>{cbRecipientName}</div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <span style={{ fontSize: '0.9rem', color: '#3c4653', textTransform: 'uppercase', letterSpacing: 1.5 }}>Book Title</span>
                <div style={{ fontSize: '1.1rem', color: '#0f172a', fontFamily: "'Lora', serif", fontStyle: 'italic', marginTop: 2 }}>
                  {cbOccasion === 'OTHER' && cbCustomOccasion.trim() ? `A Book for ${cbRecipientName}` : occasionType?.defaultTitle(cbRecipientName)}
                </div>
              </div>
              {deadlines && (
                <div>
                  <span style={{ fontSize: '0.9rem', color: '#3c4653', textTransform: 'uppercase', letterSpacing: 1.5 }}>Delivery</span>
                  <div style={{ fontSize: '1.1rem', color: '#0f172a', fontFamily: "'Lora', serif", marginTop: 2 }}>
                    {new Date(cbDeliveryDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => setCbCreateStep(3)} className="st-btn-secondary">← Back</button>
              <button onClick={finishGiftCreate} className="st-btn-primary">Create Book</button>
            </div>
          </div>
        )}
      </div>
    );
  };


  // ── renderBookList ──
  // ── Book List Screen ──
  const renderBookList = () => {
    return (
      <div className="storyteller-content" data-journey-step="book-list">
        {/* This screen is now purely the book-type chooser (reached via "Start a new book"
            or first run). Switching between existing books happens in the title switcher,
            so we no longer show a "My Books" list grid here. */}
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <img src="/pip/pip-face.png" alt="Pip" style={{ width: 92, height: 92, objectFit: 'contain', margin: '4px auto 6px', display: 'block', filter: 'drop-shadow(0 8px 14px rgba(120,70,20,0.2))' }} />
          <h2 className="st-h2" style={{ fontSize: '1.9rem', marginBottom: 8 }}>Welcome! What would you like to begin?</h2>
          <p className="st-sub" style={{ marginBottom: 4 }}>Tell me which one feels right, and I'll set it up with you.</p>
        </div>

        {false && (
          <div style={{ display: 'grid', gap: 14, marginBottom: 24 }}>
            {books.map(book => {
              const occ = book.bookType === 'GIFT' ? OCCASION_TYPES.find(o => o.id === book.occasion) : null;
              const daysLeft = book.deliveryDate ? Math.ceil((new Date(book.deliveryDate) - new Date()) / 86400000) : null;
              const contributed = book.bookType === 'GIFT' ? (book.contributors || []).filter(c => c.status === 'CONTRIBUTED').length : 0;
              const totalContrib = book.bookType === 'GIFT' ? (book.contributors || []).length : 0;
              const isConfirming = confirmDeleteId === book.id;
              return (
                <div key={book.id} className="book-card" style={{ position: 'relative' }}>
                  {/* Delete confirmation overlay */}
                  {isConfirming && (
                    <div style={{
                      position: 'absolute', inset: 0, zIndex: 2,
                      background: 'rgba(255,255,255,0.97)', borderRadius: 14,
                      border: '2px solid rgba(192,57,43,0.3)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12,
                      padding: '20px 24px',
                    }}>
                      <p style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#0f172a', textAlign: 'center', margin: 0, maxWidth: 360 }}>
                        Delete <strong>{book.title || 'this book'}</strong>?
                      </p>
                      <p style={{ fontSize: '0.9rem', color: '#3c4653', textAlign: 'center', margin: 0, lineHeight: 1.5, maxWidth: 360 }}>
                        {(book.pages || []).length > 0
                          ? `Your ${(book.pages || []).length} chapter${(book.pages || []).length !== 1 ? 's' : ''} won't be deleted — only this book is removed.`
                          : 'This cannot be undone.'}
                      </p>
                      <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                        <button onClick={() => { deleteBook(book.id); setConfirmDeleteId(null); }}
                          style={{ padding: '10px 24px', fontSize: '1rem', fontFamily: "'Lora', serif", fontWeight: 600, background: '#c0392b', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                          Delete
                        </button>
                        <button onClick={() => setConfirmDeleteId(null)}
                          style={{ padding: '10px 24px', fontSize: '1rem', fontFamily: "'Lora', serif", fontWeight: 500, background: 'rgba(15,23,42,0.06)', color: '#0f172a', border: '1px solid rgba(15,23,42,0.15)', borderRadius: 8, cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  <button onClick={() => enterBook(book.id)}
                    style={{
                      width: '100%', padding: '18px 20px', background: '#fff',
                      border: '1.5px solid rgba(15,23,42,0.1)', borderRadius: 14,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16,
                      textAlign: 'left', transition: 'border-color 0.15s, box-shadow 0.15s',
                      opacity: isConfirming ? 0.3 : 1,
                    }}
                    onMouseEnter={e => { if (!isConfirming) { e.currentTarget.style.borderColor = occ?.accent || '#0f172a'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; } }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(15,23,42,0.1)'; e.currentTarget.style.boxShadow = 'none'; }}>
                    <div style={{
                      width: 52, height: 68, borderRadius: '2px 6px 6px 2px', flexShrink: 0,
                      background: book.occasion === 'MOTHERS_DAY' ? 'none' : book.bookType === 'GIFT' ? `linear-gradient(135deg, ${occ?.accent || '#3c4653'}, ${occ?.accent || '#3c4653'}cc)` : 'linear-gradient(135deg, #0f172a, #3a3530)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '2px 2px 6px rgba(0,0,0,0.15)',
                      overflow: 'hidden', position: 'relative',
                    }}>
                      {book.occasion === 'MOTHERS_DAY' ? (
                        <img src={FloralWrapCover} alt="" loading="lazy" decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center bottom' }} />
                      ) : (
                        <span style={{ fontSize: 22, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))' }}>
                          {book.bookType === 'GIFT' ? (occ?.emoji || '') : ''}
                        </span>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "'Lora', serif", fontSize: '1.1rem', fontWeight: 500, color: '#0f172a', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {book.title || 'Untitled Book'}
                      </div>
                      <div style={{ fontSize: '0.92rem', color: '#3c4653', marginTop: 3 }}>
                        {book.bookType === 'GIFT' ? (
                          <>
                            {occ?.label || 'Celebration'} for {book.recipientName} · {(book.pages || []).length} chapters
                            {totalContrib > 0 && <> · {contributed}/{totalContrib} contributed</>}
                          </>
                        ) : (
                          <>
                            {resolvablePageCount(book.pages, stories)} chapters
                            {resolvablePageCount(book.pages, stories) > 0 && (
                              <> · {book.realPageCount != null
                                ? `${book.realPageCount} pages`
                                : `~${stBookEstimate(resolvablePageCount(book.pages, stories)).printPages} pages`}</>
                            )}
                          </>
                        )}
                      </div>
                      {book.bookType === 'GIFT' && daysLeft != null && (
                        <div style={{ fontSize: '0.85rem', marginTop: 4, color: daysLeft <= 7 ? '#c53030' : daysLeft <= 14 ? '#b8860b' : '#6b8f71', fontWeight: 500 }}>
                          {daysLeft > 0 ? `${daysLeft} days until delivery` : 'Past delivery date'}
                        </div>
                      )}
                    </div>
                    <span style={{ color: '#3c4653', fontSize: '1.2rem', flexShrink: 0 }}>→</span>
                  </button>
                  {/* Delete button — small, subtle, requires confirmation */}
                  <button
                    className="book-delete-btn"
                    onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(isConfirming ? null : book.id); }}
                    title="Delete book"
                    style={{
                      position: 'absolute', top: 8, right: 8, zIndex: 3,
                      width: 28, height: 28, borderRadius: '50%',
                      background: 'rgba(15,23,42,0.04)',
                      border: 'none', cursor: 'pointer',
                      display: isConfirming ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.85rem', color: '#3c4653',
                    }}>
                    ×
                  </button>
                </div>
              );
            })}

            {/* Memory Care subject cards — virtual books, no real Book row */}
            {memoryCareSubjects.length > 0 && (
              <>
                {memoryCareSubjects.map(subject => {
                  const lastSessionLabel = subject.lastSessionDate
                    ? (() => {
                        const diff = Date.now() - new Date(subject.lastSessionDate).getTime();
                        const days = Math.floor(diff / 86400000);
                        if (days === 0) return 'today';
                        if (days === 1) return 'yesterday';
                        if (days < 7) return `${days} days ago`;
                        if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`;
                        return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? 's' : ''} ago`;
                      })()
                    : null;
                  return (
                    <div key={`mc-${subject.id}`} className="book-card" style={{ position: 'relative' }}>
                      <button
                        onClick={() => {
                          if (!MEMORY_CARE_ENABLED) { openMemoryCareComingSoon(); return; }
                          setMcSubject(subject);
                          setMcStep('welcome');
                          setMemoryTab('interview');
                          setMemoryStep('intro');
                          setActiveTab('memory');
                          window.scrollTo(0, 0);
                        }}
                        style={{
                          width: '100%', padding: '18px 20px', background: '#fff',
                          border: '1.5px solid rgba(155,143,170,0.35)', borderRadius: 14,
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16,
                          textAlign: 'left', transition: 'border-color 0.15s, box-shadow 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#9b8faa'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(155,143,170,0.18)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(155,143,170,0.35)'; e.currentTarget.style.boxShadow = 'none'; }}>
                        {/* Memory Care spine — lavender */}
                        <div style={{
                          width: 52, height: 68, borderRadius: '2px 6px 6px 2px', flexShrink: 0,
                          background: 'linear-gradient(135deg, #9b8faa, #7a6b8a)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: '2px 2px 6px rgba(0,0,0,0.15)',
                        }}>
                          <PhSparkle size={22} weight="duotone" color="#fff" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: "'Lora', serif", fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 2, color: '#9b8faa', marginBottom: 3 }}>
                            Memory Care
                          </div>
                          <div style={{ fontFamily: "'Lora', serif", fontSize: '1.1rem', fontWeight: 500, color: '#0f172a', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
                            {subject.name}
                          </div>
                          <div style={{ fontSize: '0.92rem', color: '#3c4653', marginTop: 3 }}>
                            {(subject.sessionCount || 0)} session{(subject.sessionCount || 0) !== 1 ? 's' : ''}
                            {lastSessionLabel && <> · Last: {lastSessionLabel}</>}
                          </div>
                        </div>
                        <span style={{ color: '#9b8faa', fontSize: '1.2rem', flexShrink: 0 }}>→</span>
                      </button>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {/* Continue an in-progress Memory Care interview (re-homed here so existing
            subjects are reachable from the chooser, not only the title switcher). */}
        {(memoryCareSubjects || []).length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontFamily: "'Lora', serif", fontSize: '0.95rem', color: '#7a6b8a', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 10 }}>Continue a Memory Care interview</h3>
            <div style={{ display: 'grid', gap: 10 }}>
              {memoryCareSubjects.map(subject => (
                <button key={`mc-chooser-${subject.id}`}
                  onClick={() => { if (!MEMORY_CARE_ENABLED) { openMemoryCareComingSoon(); return; } setMcSubject(subject); setMcStep('welcome'); setMemoryTab('interview'); setMemoryStep('intro'); setActiveTab('memory'); window.scrollTo(0, 0); }}
                  style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 14, padding: '14px 16px', background: '#fff', border: '1.5px solid rgba(155,143,170,0.4)', borderRadius: 14, cursor: 'pointer', textAlign: 'left' }}>
                  <div style={{ width: 44, height: 56, borderRadius: '2px 6px 6px 2px', flexShrink: 0, background: 'linear-gradient(135deg,#9b8faa,#7a6b8a)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PhSparkle size={20} weight="duotone" color="#fff" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Lora', serif", fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1.5, color: '#9b8faa' }}>Memory Care</div>
                    <div style={{ fontFamily: "'Lora', serif", fontSize: '1.1rem', fontWeight: 500, color: '#0f172a' }}>{subject.name}</div>
                    <div style={{ fontSize: '0.9rem', color: '#3c4653' }}>{(subject.sessionCount || 0)} session{(subject.sessionCount || 0) !== 1 ? 's' : ''}</div>
                  </div>
                  <span style={{ color: '#9b8faa', fontSize: '1.2rem' }}>→</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Warm first-class choice: write your own vs celebrate someone (+ Memory Care). */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 540, margin: '0 auto', paddingBottom: 24 }}>
          {/* Write my own story */}
          <button onClick={() => { createPersonalBook(); setActiveTab('talk'); }}
            style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 22px', background: '#fff', border: '2px solid rgba(15,23,42,0.1)', borderRadius: 16, cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s, transform 0.15s, box-shadow 0.15s', boxShadow: '0 4px 18px rgba(120,70,20,0.06)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#c9a84c'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(15,23,42,0.1)'; e.currentTarget.style.transform = 'none'; }}>
            <div style={{ flexShrink: 0, color: '#7c2d12' }}><PhBookOpen size={40} weight="duotone" /></div>
            <div>
              <div style={{ fontFamily: "'Lora', serif", fontSize: '1.2rem', fontWeight: 600, color: '#0f172a' }}>Write my own story</div>
              <div style={{ fontSize: '0.95rem', color: '#5a4a3a', marginTop: 3, lineHeight: 1.45 }}>Tell your life one story at a time. I'll interview you and turn it into a real book.</div>
            </div>
          </button>
          {/* Celebrate someone */}
          <button onClick={() => { setShowGiftCreate(true); setCbCreateStep(0); }}
            style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 22px', background: '#fffdf5', border: '2px solid #7c2d12', borderRadius: 16, cursor: 'pointer', textAlign: 'left', transition: 'transform 0.15s, box-shadow 0.15s', boxShadow: '0 6px 22px rgba(124,45,18,0.12)' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(124,45,18,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 6px 22px rgba(124,45,18,0.12)'; }}>
            <div style={{ flexShrink: 0, color: '#7c2d12' }}><PhPackage size={40} weight="duotone" /></div>
            <div>
              <div style={{ fontFamily: "'Lora', serif", fontSize: '1.2rem', fontWeight: 600, color: '#0f172a' }}>Celebrate someone I love</div>
              <div style={{ fontSize: '0.95rem', color: '#5a4a3a', marginTop: 3, lineHeight: 1.45 }}>Make a book about someone special, with stories gathered from the whole family.</div>
            </div>
          </button>
          {/* Memory Care (tertiary) — F-12: gated behind the coming-soon placeholder + interest sensor. */}
          {MEMORY_CARE_ENABLED ? (
            <button
              onClick={() => {
                setMcSubject(null);
                setMcStep('welcome');
                setMemoryTab('interview');
                setMemoryStep('intro');
                setActiveTab('memory');
                window.scrollTo(0, 0);
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', background: 'transparent', border: '1px dashed rgba(155,143,170,0.55)', borderRadius: 14, cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#9b8faa'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(155,143,170,0.55)'}>
              <div style={{ flexShrink: 0, color: '#9b8faa' }}><PhSparkle size={26} weight="duotone" /></div>
              <div>
                <div style={{ fontFamily: "'Lora', serif", fontSize: '1.02rem', fontWeight: 600, color: '#0f172a' }}>Capture a loved one's stories</div>
                <div style={{ fontSize: '0.88rem', color: '#5a4a3a', marginTop: 1, lineHeight: 1.4 }}>For a parent or grandparent with memory loss. Always free.</div>
              </div>
            </button>
          ) : (
            <MemoryCareComingSoon variant="tile" onInterest={submitMemoryCareInterest} />
          )}
        </div>
        <div style={{ textAlign: 'center', paddingBottom: 60 }}>
          <a href="/gift" target="_blank" rel="noopener noreferrer" style={{ fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: '0.95rem', color: '#9a7a3a', textDecoration: 'none' }}>
            Give this as a gift →
          </a>
        </div>
      </div>
    );
  };

  // ── renderStorytellerBook ──
  const renderStorytellerBook = () => {
    // ── Gift Book Create Wizard ──
    if (showGiftCreate) return renderCbCreateWizard();
    // Wait for DynamoDB to load before deciding chooser-vs-book. On refresh,
    // activeBookId is null until UserData returns, which would briefly flash the
    // "What kind of book" chooser before the real book loads.
    if (!userDataLoaded) {
      return (
        <div className="storyteller-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div style={{ width: 34, height: 34, border: '3px solid rgba(201,168,76,0.25)', borderTopColor: '#D2761F', borderRadius: '50%', animation: 'tbspin 0.8s linear infinite' }} />
          <style>{`@keyframes tbspin { to { transform: rotate(360deg); } }`}</style>
        </div>
      );
    }
    // ── Book List (when no book is selected) ──
    if (!activeBookId) return renderBookList();

    // F-251: estimate from the pages that will actually print, not the raw array.
    const est = stBookEstimate(resolvablePageCount(stBookPages, stories));

    // ── Step 4: Reorder Chapters (drag & drop) ──
    if (stBookStep === 4) {
      const handleReorderDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
          const oldIndex = stBookPages.findIndex(p => p.storyId === active.id);
          const newIndex = stBookPages.findIndex(p => p.storyId === over.id);
          setStBookPages(arrayMove(stBookPages, oldIndex, newIndex));
        }
      };
      const saveAndGoBack = async () => {
        saveActiveBook();
        setStBookStep(0); window.scrollTo(0, 0);
        showToast('Chapter order saved!');
      };
      return (
        <div className="storyteller-content" data-journey-step="book-4">
          <button onClick={saveAndGoBack} className="st-back">← Done</button>
          <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 4 }}>Reorder Chapters</h2>
          <p className="st-sub" style={{ marginBottom: 6, color: '#3c4653' }}>Hold and drag to change chapter order.</p>
          <p className="st-sub" style={{ marginBottom: 20, fontSize: '1rem', color: '#3c4653' }}>Your changes are saved when you tap Done.</p>

          {stBookPages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 16px', color: '#3c4653' }}>
              <p style={{ fontSize: '1.1rem' }}>No chapters yet.</p>
            </div>
          ) : (
            <ReorderableChapterList
              pages={stBookPages}
              stories={stories}
              onReorder={handleReorderDragEnd}
            />
          )}

          {stBookPages.length > 1 && (
            <button onClick={saveAndGoBack} className="st-btn-primary" style={{ width: '100%', marginTop: 16 }}>✓ Save Order</button>
          )}
        </div>
      );
    }

    // ── Step 5: Add a Bonus Page ──
    if (stBookStep === 5) {
      const handleBonusPhotoUpload = async (file) => {
        if (!file) return;
        if (!isImageFile(file)) { showToast('Please choose an image file.', 'error'); return; }
        if (file.size > 10 * 1024 * 1024) { showToast('Image must be under 10 MB.', 'error'); return; }
        try {
          const normalizedFile = await normalizeImageForBook(file);
          const ext = normalizedFile.name.split('.').pop() || 'jpg';
          const fileName = `bonus_${Date.now()}.${ext}`;
          const pathBuilder = ({ identityId }) => `private/${identityId}/photos/${fileName}`;
          const uploadResult = await (uploadData({ path: pathBuilder, data: normalizedFile, options: { contentType: normalizedFile.type, cacheControl: 'public, max-age=31536000, immutable' } })).result;
          const storagePath = uploadResult?.path;
          if (!storagePath) throw new Error('Upload succeeded but no path returned');
          const urlResult = await getUrl({ path: storagePath });
          const signedUrl = urlResult?.url?.toString() || URL.createObjectURL(normalizedFile);
          setStMemoryPhotoPath(storagePath);
          setStMemoryPhotoPreview(signedUrl);
          // Generate base64 for Open Book preview rendering
          const base64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(normalizedFile); });
          setStMemoryPhotoBase64(base64);
          showToast('Photo uploaded!');
        } catch (err) { showToast('Something went wrong uploading your photo. Please try again.', 'error'); }
      };

      const bonusPhotoInputRef = React.createRef();

      return (
        <div className="storyteller-content" data-journey-step="book-5">
          <button onClick={() => { setStMemoryMode(null); setStMemoryTitle(''); setStMemoryContent(''); setStMemoryPhotoPath(''); setStMemoryPhotoPreview(''); setStMemoryPhotoBase64(null); setStBookStep(0); window.scrollTo(0, 0); }} className="st-back">← Back</button>
          <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 4 }}>Add a special page</h2>
          <p className="st-sub" style={{ marginBottom: 20 }}>A recipe, poem, quote, photo, or anything you'd like — perfect for making your book feel complete.</p>

          {/* Type picker */}
          {!stMemoryMode && (
            <div>
              <button onClick={() => setStMemoryMode('prompt')}
                style={{ width: '100%', padding: '18px 20px', marginBottom: 10, background: 'linear-gradient(135deg, rgba(15,23,42,0.05), rgba(15,23,42,0.02))', border: '2px solid rgba(15,23,42,0.12)', borderRadius: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left' }}>
                <PhPencil size={28} weight="duotone" />
                <div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 600, color: '#1a1612', fontFamily: "'Lora', serif" }}>Write Something</div>
                  <div style={{ fontSize: '1.05rem', color: '#3c4653', marginTop: 2 }}>A recipe, poem, quote, letter, or short memory</div>
                </div>
              </button>
              <button onClick={() => setStMemoryMode('photo')}
                style={{ width: '100%', padding: '18px 20px', marginBottom: 10, background: '#ffffff', border: '2px solid rgba(15,23,42,0.08)', borderRadius: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left' }}>
                <PhCamera size={28} weight="duotone" />
                <div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 600, color: '#1a1612', fontFamily: "'Lora', serif" }}>Photo Page</div>
                  <div style={{ fontSize: '1.05rem', color: '#3c4653', marginTop: 2 }}>A full-page photo with a caption</div>
                </div>
              </button>
            </div>
          )}

          {/* ── Write Something: prompt picker then editor ── */}
          {stMemoryMode === 'prompt' && !stMemoryTitle && (
            <div>
              <button onClick={() => setStMemoryMode(null)} className="st-back" style={{ marginBottom: 12 }}>← Choose type</button>
              <p style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', letterSpacing: 2, color: '#0f172a', textTransform: 'uppercase', marginBottom: 12 }}>Pick a prompt or write your own</p>
              
              {/* Custom option */}
              <button onClick={() => setStMemoryTitle('Untitled')}
                style={{ width: '100%', padding: '14px 18px', marginBottom: 16, background: 'rgba(15,23,42,0.05)', border: '2px dashed rgba(15,23,42,0.12)', borderRadius: 12, cursor: 'pointer', textAlign: 'left', fontFamily: "'Lora', serif", fontSize: '1rem', color: '#3c4653' }}>
                ✏️ Write my own — custom title & text
              </button>

              {MEMORY_PROMPTS.map(cat => (
                <div key={cat.cat} style={{ marginBottom: 16 }}>
                  <p style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', letterSpacing: 2, color: '#7a6b5a', textTransform: 'uppercase', marginBottom: 8 }}>{cat.cat}</p>
                  {cat.prompts.map(p => (
                    <button key={p.title} onClick={() => { setStMemoryTitle(p.title); setStMemoryContent(''); }}
                      style={{ width: '100%', padding: '12px 16px', marginBottom: 6, background: '#fff', border: '1px solid rgba(15,23,42,0.18)', borderRadius: 10, cursor: 'pointer', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: '1.05rem', fontWeight: 600, color: '#1a1612', fontFamily: "'Lora', serif" }}>{p.title}</span>
                      <span style={{ fontSize: '1rem', color: '#3c4653' }}>{p.hint}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* ── Editor ── */}
          {stMemoryMode === 'prompt' && stMemoryTitle && (
            <div>
              <button onClick={() => setStMemoryTitle('')} className="st-back" style={{ marginBottom: 12 }}>← Pick a different prompt</button>
              <label className="st-label">Title</label>
              <input className="st-input" value={stMemoryTitle} onChange={e => setStMemoryTitle(e.target.value)} placeholder="Title for this page" style={{ marginBottom: 14 }} />
              <label className="st-label">Your Text</label>
              <textarea className="st-input" value={stMemoryContent} onChange={e => setStMemoryContent(e.target.value)}
                placeholder="Write anything — a recipe, poem, quote, memory, letter... this will be a page in your book."
                style={{ minHeight: 180, resize: 'vertical', fontFamily: "'Lora', serif", lineHeight: 1.7 }} />
              <p style={{ fontSize: '1rem', color: '#7a6b5a', marginTop: 4, marginBottom: 16 }}>{(stMemoryContent || '').split(/\s+/).filter(Boolean).length} words</p>
              
              {/* Optional photo — matches chapter photo upload style */}
              <input ref={bonusPhotoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleBonusPhotoUpload(e.target.files[0])} />
              {stMemoryPhotoPreview ? (
                <div style={{ marginBottom: 20 }}>
                  <label className="st-label">Photo</label>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={stMemoryPhotoPreview} alt="Preview" style={{ width: 240, height: 180, objectFit: 'cover', borderRadius: 10, border: '1px solid rgba(15,23,42,0.1)' }} />
                    <button onClick={() => { setStMemoryPhotoPath(''); setStMemoryPhotoPreview(''); setStMemoryPhotoBase64(null); }}
                      style={{ position: 'absolute', top: 6, right: 6, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: 20 }}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'rgba(15,23,42,0.2)'; }}
                  onDragLeave={e => { e.currentTarget.style.borderColor = 'rgba(15,23,42,0.08)'; }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'rgba(15,23,42,0.08)'; handleBonusPhotoUpload(e.dataTransfer.files[0]); }}>
                  <button onClick={() => bonusPhotoInputRef.current?.click()}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '20px 22px', background: 'rgba(15,23,42,0.03)', border: '1px dashed rgba(15,23,42,0.08)', borderRadius: 14, cursor: 'pointer', width: '100%' }}>
                    <PhCamera size={28} weight="duotone" />
                    <span style={{ fontSize: '1rem', color: '#0f172a', fontFamily: "'Lora', serif" }}>
                      Add a photo (optional) — tap or drag & drop
                    </span>
                  </button>
                </div>
              )}

              <button disabled={!stMemoryContent?.trim()} onClick={async () => {
                try {
                  const result = await client.graphql({ query: createLegacyEntry, variables: { input: {
                    userId: user.userId || user.username,
                    title: stMemoryTitle.trim(),
                    content: stMemoryContent.trim(),
                    type: 'memory',
                    createdAt: new Date().toISOString()
                  }}});
                  const newId = result.data.createLegacyEntry.id;
                  await refreshStories();
                  const newPage = { storyId: newId, caption: '', layout: 'classic' };
                  if (stMemoryPhotoPath) { newPage.photoPath = stMemoryPhotoPath; newPage.photoBase64 = stMemoryPhotoBase64; }
                  const updatedPages = [...stBookPages, newPage];
                  setStBookPages(updatedPages);
                  const bookData = { title: stBookTitle, subtitle: stBookSubtitle, author: stBookAuthor, dedication: stBookDedication, coverStyle: stBookCover, coverDesign: stBookCoverDesign, coverPhotoPath: stBookCoverPhotoPath, coverPhotoY: stBookCoverPhotoY, pages: cleanPagesForSave(updatedPages) };
                  saveBookData(bookData);
                  setStMemoryMode(null); setStMemoryTitle(''); setStMemoryContent(''); setStMemoryPhotoPath(''); setStMemoryPhotoPreview(''); setStMemoryPhotoBase64(null);
                  setStBookStep(0); window.scrollTo(0, 0);
                  showToast('Bonus page added to your book!');
                } catch (err) { showToast('Something went wrong saving your work. Please try again.', 'error'); }
              }} className="st-btn-primary" style={{ width: '100%', marginTop: 8 }}>
                Add to My Book
              </button>
            </div>
          )}

          {/* ── Photo page ── */}
          {stMemoryMode === 'photo' && (
            <div>
              <button onClick={() => setStMemoryMode(null)} className="st-back" style={{ marginBottom: 12 }}>← Choose type</button>
              <input ref={bonusPhotoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleBonusPhotoUpload(e.target.files[0])} />
              
              {stMemoryPhotoPreview ? (
                <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 14 }}>
                  <img src={stMemoryPhotoPreview} alt="Preview" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 10, border: '1px solid rgba(15,23,42,0.12)', flexShrink: 0 }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                    <button onClick={() => bonusPhotoInputRef.current?.click()}
                      style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid rgba(15,23,42,0.15)', background: '#fff', cursor: 'pointer', fontSize: '1rem', color: '#0f172a', fontFamily: "'Lora', serif", textAlign: 'left' }}>
                       Change photo
                    </button>
                    <button onClick={() => { setStMemoryPhotoPath(''); setStMemoryPhotoPreview(''); setStMemoryPhotoBase64(null); }}
                      style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid rgba(15,23,42,0.15)', background: '#fff', cursor: 'pointer', fontSize: '1rem', color: '#3c4653', fontFamily: "'Lora', serif", textAlign: 'left' }}>
                      ✕ Remove photo
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: 20 }}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'rgba(15,23,42,0.2)'; }}
                  onDragLeave={e => { e.currentTarget.style.borderColor = 'rgba(15,23,42,0.12)'; }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'rgba(15,23,42,0.12)'; handleBonusPhotoUpload(e.dataTransfer.files[0]); }}>
                  <button onClick={() => bonusPhotoInputRef.current?.click()}
                    style={{ width: '100%', padding: '40px 20px', background: 'rgba(15,23,42,0.03)', border: '2px dashed rgba(15,23,42,0.12)', borderRadius: 14, cursor: 'pointer', textAlign: 'center' }}>
                    <div style={{ marginBottom: 8 }}><PhCamera size={40} weight="duotone" /></div>
                    <div style={{ fontSize: '1.1rem', color: '#0f172a', fontFamily: "'Lora', serif", fontWeight: 600 }}>Tap to choose a photo</div>
                    <div style={{ fontSize: '1.05rem', color: '#3c4653', marginTop: 4 }}>or drag & drop an image here</div>
                  </button>
                </div>
              )}

              <label className="st-label">Caption</label>
              <input className="st-input" value={stMemoryTitle} onChange={e => setStMemoryTitle(e.target.value)} placeholder="e.g. Grandma's garden, summer 1985" style={{ marginBottom: 14 }} />
              <label className="st-label">Description (optional)</label>
              <textarea className="st-input" value={stMemoryContent} onChange={e => setStMemoryContent(e.target.value)}
                placeholder="A sentence or two about this photo..."
                style={{ minHeight: 80, resize: 'vertical' }} />

              <button disabled={!stMemoryPhotoPath || !stMemoryTitle?.trim()} onClick={async () => {
                try {
                  const result = await client.graphql({ query: createLegacyEntry, variables: { input: {
                    userId: user.userId || user.username,
                    title: stMemoryTitle.trim(),
                    content: stMemoryContent?.trim() || '',
                    type: 'memory',
                    createdAt: new Date().toISOString()
                  }}});
                  const newId = result.data.createLegacyEntry.id;
                  await refreshStories();
                  const newPage = { storyId: newId, photoPath: stMemoryPhotoPath, photoBase64: stMemoryPhotoBase64, caption: stMemoryTitle.trim(), layout: 'photo-spread' };
                  const updatedPages = [...stBookPages, newPage];
                  setStBookPages(updatedPages);
                  const bookData = { title: stBookTitle, subtitle: stBookSubtitle, author: stBookAuthor, dedication: stBookDedication, coverStyle: stBookCover, coverDesign: stBookCoverDesign, coverPhotoPath: stBookCoverPhotoPath, coverPhotoY: stBookCoverPhotoY, pages: cleanPagesForSave(updatedPages) };
                  saveBookData(bookData);
                  setStMemoryMode(null); setStMemoryTitle(''); setStMemoryContent(''); setStMemoryPhotoPath(''); setStMemoryPhotoPreview(''); setStMemoryPhotoBase64(null);
                  setStBookStep(0); window.scrollTo(0, 0);
                  showToast('Photo page added to your book!');
                } catch (err) { showToast('Something went wrong saving your work. Please try again.', 'error'); }
              }} className="st-btn-primary" style={{ width: '100%', marginTop: 8 }}>
                Add to My Book
              </button>
            </div>
          )}
        </div>
      );
    }

    // ── Step 6: Order Flow ──
    if (stBookStep === 6) {

      // Order success
      if (stOrderStep === 'success') {
        const checkStatus = async () => {
          if (!stOrderId) return;
          setStCheckingStatus(true);
          try {
            const token = await getAuthToken();
            const resp = await fetch(`${ORDER_API_BASE}/order/status/${stOrderId}`, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            });
            const data = await resp.json();
            setStOrderStatus(data);
          } catch (err) { console.error('Status check failed:', err); }
          setStCheckingStatus(false);
        };

        const statusEmoji = {
          'PENDING_PAYMENT': '⏳', 'PAID': '✅', 'SUBMITTED_TO_LULU': '🖨️',
          'IN_PRODUCTION': '📖', 'SHIPPED': '📦', 'LULU_ERROR': '⚠️', 'CANCELLED': '🚫'
        };
        const statusLabel = {
          'PENDING_PAYMENT': 'Waiting for payment', 'PAID': 'Payment received',
          'SUBMITTED_TO_LULU': 'Sent to printer', 'IN_PRODUCTION': 'Being printed',
          'SHIPPED': 'Shipped!', 'LULU_ERROR': 'Issue with printing', 'CANCELLED': 'Cancelled'
        };

        return (
          <div className="storyteller-content" data-journey-step="book-6-success" style={{ textAlign: 'center', paddingTop: 20 }}>
            {/* Celebration header */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(15,23,42,0.05) 0%, rgba(15,23,42,0.01) 100%)',
              borderRadius: 20, padding: '48px 32px 40px', marginBottom: 32,
              border: '1px solid rgba(15,23,42,0.07)',
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '180px', height: '2px', background: 'linear-gradient(90deg, transparent, rgba(15,23,42,0.25), transparent)' }} />
              <div style={{ fontSize: 56, marginBottom: 16 }}>✦</div>
              <h2 style={{ fontFamily: "'Lora', serif", fontSize: '1.6rem', color: '#0f172a', letterSpacing: '1px', marginBottom: 12, fontWeight: 400 }}>Your book is on its way.</h2>
              <div style={{ width: 60, height: 1, background: '#7c2d12', margin: '0 auto 16px' }} />
              <p style={{ fontFamily: "'Lora', serif", fontSize: '1.2rem', color: '#3c4653', fontStyle: 'italic', lineHeight: 1.7, maxWidth: 400, margin: '0 auto' }}>
                Something real, something lasting. A piece of your story is being brought to life.
              </p>
            </div>

            {/* What happens next */}
            <div style={{
              background: '#fff', borderRadius: 16, padding: '28px 24px', marginBottom: 24,
              border: '1px solid rgba(15,23,42,0.07)', textAlign: 'left',
            }}>
              <p style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#0f172a', textTransform: 'uppercase', marginBottom: 20 }}>What Happens Next</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[
                  { icon: <PhPrinter size={20} weight="duotone" />, label: 'Printing', desc: 'Your hardcover book is being prepared at our print facility.' },
                  { icon: <PhPackage size={20} weight="duotone" />, label: 'Packaging', desc: 'It will be carefully wrapped and shipped to you.' },
                  { icon: <PhHouse size={20} weight="duotone" />, label: 'Delivery', desc: 'Usually arrives within 7–10 business days.' },
                ].map(step => (
                  <div key={step.label} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '1.4rem', flexShrink: 0, marginTop: 2 }}>{step.icon}</span>
                    <div>
                      <p style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '1px', color: '#0f172a', marginBottom: 2 }}>{step.label}</p>
                      <p style={{ fontSize: '1rem', color: '#3c4653', lineHeight: 1.5 }}>{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: '0.9rem', color: '#3c4653', marginTop: 16, lineHeight: 1.6 }}>
                If your book hasn't arrived after 12 business days, visit <span onClick={() => { setActiveTab('account'); setAccountSection('orders'); window.scrollTo(0, 0); }} style={{ color: '#7c2d12', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>Account &rarr; Orders</span> or email us at <a href="mailto:support@taleboundbook.com" style={{ color: '#7c2d12' }}>support@taleboundbook.com</a>.
              </p>
            </div>

            {/* UX (UX_BACKLOG H-04): photo-enhancement nudge removed from the order SUCCESS
                screen. The book is already submitted to print here, so enhancing does nothing
                for this order, and "{N} photos not enhanced yet" read as a problem with the
                order the customer just paid for. Enhancement stays available pre-order via the
                book overview "Enhance photos" action and the photo step. */}

            {/* Shipping address */}
            <div style={{
              background: '#fff', borderRadius: 16, padding: '24px', marginBottom: 24,
              border: '1px solid rgba(15,23,42,0.07)', textAlign: 'left',
            }}>
              <p style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#0f172a', textTransform: 'uppercase', marginBottom: 14 }}>Shipping To</p>
              <p style={{ fontSize: '1.1rem', color: '#0f172a', fontWeight: 600, marginBottom: 2 }}>{stOrderAddress.name}</p>
              <p style={{ fontSize: '1rem', color: '#3c4653' }}>{stOrderAddress.street1}{stOrderAddress.street2 ? `, ${stOrderAddress.street2}` : ''}</p>
              <p style={{ fontSize: '1rem', color: '#3c4653' }}>{stOrderAddress.city}, {stOrderAddress.stateCode} {stOrderAddress.zip}</p>
            </div>

            {/* Order status */}
            {stOrderStatus && (
              <div style={{
                background: '#fff', borderRadius: 16, padding: '24px', marginBottom: 24,
                border: '1px solid rgba(15,23,42,0.07)',
              }}>
                <p style={{ fontSize: '1.15rem', color: '#0f172a', fontWeight: 600 }}>
                  {statusEmoji[stOrderStatus.status] || ''} {statusLabel[stOrderStatus.status] || stOrderStatus.status}
                </p>
                {stOrderStatus.status === 'LULU_ERROR' && (
                  <p style={{ fontSize: '1.05rem', color: '#c0392b', marginTop: 8 }}>
                    There was an issue with your order. Please contact us at support@taleboundbook.com with your order ID.
                  </p>
                )}
                {stOrderStatus.tracking && (
                  <p style={{ fontSize: '1rem', color: '#3c4653', marginTop: 8 }}>Tracking: {stOrderStatus.tracking}</p>
                )}
              </div>
            )}

            <p style={{ fontSize: '1rem', color: '#3c4653', marginBottom: 20 }}>Order ID: {stOrderId}</p>

            <button onClick={checkStatus} disabled={stCheckingStatus} className="st-btn-secondary" style={{ width: '100%', marginBottom: 12 }}>
              {stCheckingStatus ? ' Checking...' : ' Track my order'}
            </button>

            <button onClick={() => { setStBookStep(0); setStOrderStep(null); setStOrderStatus(null); window.scrollTo(0, 0); }} className="st-btn-primary" style={{ width: '100%' }}>
              ← Back to My Book
            </button>
          </div>
        );
      }

      // Processing / waiting for Stripe redirect
      if (stOrderStep === 'processing') {
        return (
          <div className="storyteller-content" data-journey-step="book-6-processing" style={{ textAlign: 'center', paddingTop: 60 }}>
            <div style={{ marginBottom: 20 }}><PhPackage size={56} weight="duotone" /></div>
            <h2 className="st-h2" style={{ fontSize: '1.8rem' }}>Processing your order...</h2>
            <p className="st-sub" style={{ marginTop: 12 }}>Preparing your book files. This may take a moment.</p>
            <div style={{ width: 48, height: 48, border: '3px solid rgba(15,23,42,0.25)', borderTopColor: 'rgba(15,23,42,0.3)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '40px auto' }} />
          </div>
        );
      }

      // Error
      if (stOrderStep === 'error') {
        return (
          <div className="storyteller-content" data-journey-step="book-6-error" style={{ textAlign: 'center', paddingTop: 40 }}>
            <div style={{ fontSize: 56, marginBottom: 20 }}>😕</div>
            <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 8 }}>Something went wrong</h2>
            <p className="st-sub" style={{ marginBottom: 24 }}>{stOrderError || 'We couldn\'t process your order. Please try again.'}</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => setStOrderStep('address')} className="st-btn-primary">Try Again</button>
              <button onClick={() => { setStBookStep(0); setStOrderStep(null); window.scrollTo(0, 0); }} className="st-btn-secondary">← Back</button>
            </div>
          </div>
        );
      }

      // ── Extra Copies Upsell (celebration books only) ──
      if (stOrderStep === 'upsell') {
        const basePrice = 89.99;
        const extraPrice = 69.99;
        const totalPrice = basePrice + (cbExtraCopies * extraPrice);

        const updateExtraAddr = (idx, field, val) => {
          setCbExtraAddresses(prev => {
            const updated = [...prev];
            if (field === 'firstName' || field === 'lastName') {
              updated[idx] = { ...updated[idx], [field]: val, name: (field === 'firstName' ? val + ' ' + (updated[idx].lastName || '') : (updated[idx].firstName || '') + ' ' + val).trim() };
            } else {
              updated[idx] = { ...updated[idx], [field]: val };
            }
            return updated;
          });
        };

        return (
          <div className="storyteller-content" data-journey-step="book-6-upsell">
            <button onClick={() => { setStOrderStep('address'); window.scrollTo(0, 0); }} className="st-back">← Back</button>
            <h2 className="st-h2" style={{ fontSize: '1.9rem', marginBottom: 4 }}>Want Extra Copies?</h2>
            <p className="st-sub" style={{ marginBottom: 28 }}>Gift extra copies to family members — each one printed and shipped individually.</p>

            {/* Base order summary */}
            <div className="st-warm-box" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
              <div><PhBookOpen size={40} weight="duotone" /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#0f172a', fontFamily: "'Lora', serif" }}>Your copy</div>
                <div style={{ fontSize: '1rem', color: '#3c4653', marginTop: 2 }}>Ships to {stOrderAddress.name || 'you'}</div>
              </div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#0f172a', fontFamily: "'Lora', serif" }}>${basePrice.toFixed(2)}</div>
            </div>

            {/* Extra copies selector */}
            <div style={{ background: '#fff', borderRadius: 16, padding: '24px', marginBottom: 20, border: '1px solid rgba(15,23,42,0.07)' }}>
              <p style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#0f172a', textTransform: 'uppercase', marginBottom: 16 }}>Extra Copies</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginBottom: 12 }}>
                <button onClick={() => { const n = Math.max(0, cbExtraCopies - 1); setCbExtraCopies(n); setCbExtraAddresses(prev => prev.slice(0, n)); }}
                  style={{ width: 44, height: 44, borderRadius: '50%', border: '2px solid #0f172a', background: 'transparent', fontSize: '1.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0f172a' }}>−</button>
                <span style={{ fontSize: '2rem', fontWeight: 700, color: '#0f172a', fontFamily: "'Lora', serif", minWidth: 40, textAlign: 'center' }}>{cbExtraCopies}</span>
                <button onClick={() => { const n = Math.min(10, cbExtraCopies + 1); setCbExtraCopies(n); setCbExtraAddresses(prev => { const a = [...prev]; while (a.length < n) a.push({ name: '', firstName: '', lastName: '', street1: '', street2: '', city: '', stateCode: '', zip: '', countryCode: 'US', phone: '' }); return a.slice(0, n); }); }}
                  style={{ width: 44, height: 44, borderRadius: '50%', border: '2px solid #0f172a', background: 'transparent', fontSize: '1.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0f172a' }}>+</button>
              </div>
              <p style={{ textAlign: 'center', color: '#3c4653', fontSize: '1rem' }}>${extraPrice.toFixed(2)} each · Same hardcover book</p>
            </div>

            {/* Shipping address option for extras */}
            {cbExtraCopies > 0 && (
              <div style={{ background: '#fff', borderRadius: 16, padding: '24px', marginBottom: 20, border: '1px solid rgba(15,23,42,0.07)' }}>
                <p style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#0f172a', textTransform: 'uppercase', marginBottom: 16 }}>Shipping for Extra Copies</p>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 8, fontSize: '1rem', color: '#0f172a' }}>
                  <input type="radio" checked={cbUseSameAddress} onChange={() => setCbUseSameAddress(true)} style={{ accentColor: '#0f172a' }} />
                  Ship all to same address
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '1rem', color: '#0f172a' }}>
                  <input type="radio" checked={!cbUseSameAddress} onChange={() => setCbUseSameAddress(false)} style={{ accentColor: '#0f172a' }} />
                  Different addresses for each copy
                </label>

                {!cbUseSameAddress && cbExtraAddresses.map((addr, idx) => (
                  <div key={idx} style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid rgba(15,23,42,0.08)' }}>
                    <p style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: '#0f172a', marginBottom: 12, fontWeight: 600 }}>Copy {idx + 1}</p>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '0.85rem', color: '#3c4653', display: 'block', marginBottom: 4 }}>First Name</label>
                        <input className="st-input" value={addr.firstName || ''} onChange={e => updateExtraAddr(idx, 'firstName', e.target.value)} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '0.85rem', color: '#3c4653', display: 'block', marginBottom: 4 }}>Last Name</label>
                        <input className="st-input" value={addr.lastName || ''} onChange={e => updateExtraAddr(idx, 'lastName', e.target.value)} />
                      </div>
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: '0.85rem', color: '#3c4653', display: 'block', marginBottom: 4 }}>Street Address</label>
                      <input className="st-input" value={addr.street1} onChange={e => updateExtraAddr(idx, 'street1', e.target.value)} />
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: '0.85rem', color: '#3c4653', display: 'block', marginBottom: 4 }}>Apt / Suite <span style={{ fontStyle: 'italic' }}>(optional)</span></label>
                      <input className="st-input" value={addr.street2} onChange={e => updateExtraAddr(idx, 'street2', e.target.value)} />
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                      <div style={{ flex: 2 }}>
                        <label style={{ fontSize: '0.85rem', color: '#3c4653', display: 'block', marginBottom: 4 }}>City</label>
                        <input className="st-input" value={addr.city} onChange={e => updateExtraAddr(idx, 'city', e.target.value)} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '0.85rem', color: '#3c4653', display: 'block', marginBottom: 4 }}>State</label>
                        <input className="st-input" value={addr.stateCode} onChange={e => updateExtraAddr(idx, 'stateCode', e.target.value.toUpperCase().slice(0, 2))} maxLength={2} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '0.85rem', color: '#3c4653', display: 'block', marginBottom: 4 }}>ZIP</label>
                        <input className="st-input" value={addr.zip} onChange={e => updateExtraAddr(idx, 'zip', e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Total */}
            <div style={{ background: 'rgba(15,23,42,0.04)', borderRadius: 16, padding: '20px 24px', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#3c4653', fontSize: '1rem' }}>Your copy</span>
                <span style={{ color: '#0f172a', fontSize: '1rem' }}>${basePrice.toFixed(2)}</span>
              </div>
              {cbExtraCopies > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#3c4653', fontSize: '1rem' }}>{cbExtraCopies} extra {cbExtraCopies === 1 ? 'copy' : 'copies'} × ${extraPrice.toFixed(2)}</span>
                  <span style={{ color: '#0f172a', fontSize: '1rem' }}>${(cbExtraCopies * extraPrice).toFixed(2)}</span>
                </div>
              )}
              <div style={{ borderTop: '1px solid rgba(15,23,42,0.12)', paddingTop: 10, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#0f172a', fontSize: '1.15rem', fontWeight: 700, fontFamily: "'Lora', serif" }}>Total</span>
                <span style={{ color: '#0f172a', fontSize: '1.15rem', fontWeight: 700, fontFamily: "'Lora', serif" }}>${totalPrice.toFixed(2)}</span>
              </div>
            </div>

            {/* Continue to checkout */}
            <button onClick={() => { setStOrderStep('address_checkout'); window.scrollTo(0, 0); }}
              className="st-btn-primary" style={{ width: '100%', marginTop: 8 }}>
              💳 Continue to Checkout · ${totalPrice.toFixed(2)}
            </button>

            {cbExtraCopies === 0 && (
              <button onClick={() => { setStOrderStep('address_checkout'); window.scrollTo(0, 0); }}
                className="st-btn-secondary" style={{ width: '100%', marginTop: 8 }}>
                No thanks, just my copy
              </button>
            )}
          </div>
        );
      }

      // ── Checkout processing (triggered from address form or upsell) ──
      if (stOrderStep === 'address_checkout') {
        const isCb = isGiftBook;
        const extraPrice = 69.99;
        const totalCopies = 1 + (isCb ? cbExtraCopies : 0);
        const totalPrice = 89.99 + (isCb ? cbExtraCopies * extraPrice : 0);

        // Build extra shipping addresses for celebration extra copies
        const extraShippingAddresses = [];
        if (isCb && cbExtraCopies > 0) {
          for (let i = 0; i < cbExtraCopies; i++) {
            if (cbUseSameAddress) {
              extraShippingAddresses.push({
                name: stOrderAddress.name, street1: stOrderAddress.street1, street2: stOrderAddress.street2 || '',
                city: stOrderAddress.city, stateCode: stOrderAddress.stateCode, zip: stOrderAddress.zip,
                countryCode: stOrderAddress.countryCode || 'US', phone: stOrderAddress.phone,
              });
            } else {
              const a = cbExtraAddresses[i] || {};
              extraShippingAddresses.push({
                name: a.name || stOrderAddress.name, street1: a.street1 || stOrderAddress.street1,
                street2: a.street2 || '', city: a.city || stOrderAddress.city,
                stateCode: a.stateCode || stOrderAddress.stateCode, zip: a.zip || stOrderAddress.zip,
                countryCode: a.countryCode || 'US', phone: a.phone || stOrderAddress.phone,
              });
            }
          }
        }

        // Auto-trigger checkout on mount
        const doCheckout = async () => {
          setStOrderLoading(true);
          setStOrderError('');
          try {
            const orderAddress = {
              name: stOrderAddress.name, street1: stOrderAddress.street1, street2: stOrderAddress.street2 || '',
              city: stOrderAddress.city, stateCode: stOrderAddress.stateCode, zip: stOrderAddress.zip,
              countryCode: stOrderAddress.countryCode || 'US', phone: stOrderAddress.phone,
            };

            setStOrderError('Generating your book PDF...');
            const pdfChapters = stBookPages.map(page => {
              const story = stories.find(s => s.id === page.storyId);
              if (!story) return null;
              const subtitle = '';
              const contribRelationship = page.contributorName && activeBook?.contributors
                ? (activeBook.contributors.find(c => c.name && page.contributorName.toLowerCase().includes(c.name.toLowerCase()))?.relationship || '')
                : '';
              const rawContent = story.content || '';
              const content = page.contributorName ? cleanContributionContent(rawContent) : rawContent;
              return {
                title: promptToChapterTitle(story.title) || 'Untitled', content,
                photoUrl: page.photoBase64 || page.photoUrl || null, caption: page.caption || '',
                layout: page.layout || 'classic',
                photoFocusX: page.photoFocusX ?? 50, photoFocusY: page.photoFocusY ?? 50,
                photoOrientation: page.photoOrientation || 'landscape', subtitle,
                contributorName: page.contributorName || '',
                contributorRelationship: contribRelationship,
                isKidStory: page.isKidStory || false,
                qrDataUrl: (page.shareId && page.qrEnabled !== false) ? chapterQrCodes[page.storyId] : null,
                mediaType: page.mediaType,
              };
            }).filter(Boolean);

            // Build the shared props object so we can reuse it for a possible
            // second render (odd-page re-pad) without repeating every prop.
            const interiorDocProps = {
              title: stBookTitle || 'Our Story', author: stBookAuthor, familyName: stBookAuthor || '',
              dedication: stBookDedication || '', isCaretaker: isKeeper,
              lovedOneName: legacySubjectName, chapters: pdfChapters,
              occasion: isGiftBook ? activeBook?.occasion : null,
              recipientName: isGiftBook ? (activeBook?.recipientName || '') : '',
              contributorCount: isGiftBook ? (activeBook?.contributors?.length || 0) : 0,
            };
            const interiorDoc = <RemnantBookPDF {...interiorDocProps} />;
            let interiorBlob = await pdf(interiorDoc).toBlob();

            // TB-102 Phase 2: read the real rendered page count from the interior blob
            // using pdf.js (already configured globally in App.js). This is the only
            // reliable count — RemnantBookPDF's internal pageNum tracks components, not
            // rendered pages (a single TextPage with wrap can span multiple pages).
            // The cover spine width and the Lulu order MUST use this number, not the
            // stBookEstimate, to avoid a systematic off-center shift on the physical book.
            let realPages = est.luluPages; // fallback: use estimate if pdf.js read fails
            try {
              const buf = await interiorBlob.arrayBuffer();
              const pdfDoc = await pdfjs.getDocument({ data: buf }).promise;
              realPages = pdfDoc.numPages;
              // Guarantee even page count. Lulu auto-adds a blank to odd-count hardcovers,
              // which would change the spine width and shift the front panel. If the
              // rendered count is odd, regenerate with forceEvenPages=true so we pad
              // deliberately rather than letting Lulu do it silently.
              if (realPages % 2 !== 0) {
                const interiorDocEven = <RemnantBookPDF {...interiorDocProps} forceEvenPages={true} />;
                interiorBlob = await pdf(interiorDocEven).toBlob();
                const buf2 = await interiorBlob.arrayBuffer();
                const pdfDoc2 = await pdfjs.getDocument({ data: buf2 }).promise;
                realPages = pdfDoc2.numPages;
              }
              // Safety floor: Lulu requires at least 24 pages for hardcover.
              realPages = Math.max(realPages, 24);
              console.log('[TB-102] Interior rendered pages:', realPages, '(estimate was', est.luluPages, ')');
            } catch (pdfjsErr) {
              console.warn('[TB-102] pdf.js page-count read failed — falling back to estimate (', est.luluPages, '):', pdfjsErr);
              realPages = est.luluPages;
            }

            setStOrderError('Generating cover...');
            // Resolve the cover photo as base64 for PDF embedding.
            // resolveCoverPhotoBase64 returns cached state or fetches fresh via Amplify downloadData.
            // stBookCoverPhotoUrl must NOT be used as a fallback here — it may hold a signed S3 URL
            // that has expired or that @react-pdf/renderer cannot fetch (CORS).
            // Compress to <500KB before passing to @react-pdf/renderer — oversized images cause
            // silent failures where the PDF renders without the photo (Decision Register 2026-03-22).
            let coverPhotoForPdf = await resolveCoverPhotoBase64() || null;
            // Bake the chosen tone (sepia/bw) into the print image so the PDF
            // prints exactly what the customer previewed (floral wrap only).
            if (coverPhotoForPdf && stBookCoverDesign === 'mustardplum' && stBookCoverPhotoTone && stBookCoverPhotoTone !== 'color') {
              try { coverPhotoForPdf = await applyPhotoTone(coverPhotoForPdf, stBookCoverPhotoTone); }
              catch (toneErr) { console.warn('[Cover Photo] tone bake failed during checkout — using natural color:', toneErr); }
            }
            if (coverPhotoForPdf && coverPhotoForPdf.length > 500000) {
              try {
                const img = new window.Image();
                await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = coverPhotoForPdf; });
                const canvas = document.createElement('canvas');
                const maxW = 1200;
                const scale = Math.min(1, maxW / img.naturalWidth);
                canvas.width = Math.round(img.naturalWidth * scale);
                canvas.height = Math.round(img.naturalHeight * scale);
                canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
                coverPhotoForPdf = canvas.toDataURL('image/jpeg', 0.85);
              } catch (compressErr) {
                console.warn('[Cover Photo] Compress failed during checkout — using uncompressed base64:', compressErr);
              }
            }
            // TB-102 Phase 2: use realPages (from rendered interior blob) for the cover PDF.
            // est.luluPages is intentionally kept only for the pre-order price display and
            // cover PREVIEW surfaces (step 0 / step 2) — not for print files or the order.
            const coverDoc = (
              <RemnantCoverPDF title={stBookTitle || 'Our Story'} subtitle={stBookSubtitle} author={stBookAuthor}
                pageCount={realPages} coverStyle={stBookCover} coverDesign={stBookCoverDesign}
                coverPhotoUrl={coverPhotoForPdf}
                coverPhotoY={stBookCoverPhotoY}
                coverPhotoOrientation={stBookCoverPhotoOrientation}
                botanicalImageUrl={['burgundy','mothersday','linen','charcoalgold'].includes(stBookCoverDesign) ? COVER_FULL_RES[stBookCoverDesign] : null}
                fullWrapImageUrl={FULLWRAP_DESIGNS.includes(stBookCoverDesign) ? COVER_FULL_RES[stBookCoverDesign] : null} />
            );
            const coverBlob = await pdf(coverDoc).toBlob();

            // TB-067: admin-only local PDF download so Alex can review the
            // exact bytes before clicking Pay in the Lulu dashboard. Gated to
            // the admin user (Cognito sub matches ADMIN_USER_ID) — customer
            // browsers never trigger this.
            if (isAdmin) {
              const __downloadBlob = (blob, filename) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              };
              const __stamp = new Date().toISOString().replace(/[:.]/g, '-');
              __downloadBlob(coverBlob, `talebound-cover-${__stamp}.pdf`);
              __downloadBlob(interiorBlob, `talebound-interior-${__stamp}.pdf`);
              // Extra admin-only copy with the trim/bleed/safe-zone/spine grid
              // overlaid, for margin review. A SEPARATE render (debug=true) so the
              // clean coverBlob sent to Lulu is never touched by the guides.
              try {
                const gridCoverBlob = await pdf(
                  <RemnantCoverPDF title={stBookTitle || 'Our Story'} subtitle={stBookSubtitle} author={stBookAuthor}
                    pageCount={realPages} coverStyle={stBookCover} coverDesign={stBookCoverDesign}
                    coverPhotoUrl={coverPhotoForPdf}
                    coverPhotoY={stBookCoverPhotoY}
                    coverPhotoOrientation={stBookCoverPhotoOrientation}
                    botanicalImageUrl={['burgundy','mothersday','linen','charcoalgold'].includes(stBookCoverDesign) ? COVER_FULL_RES[stBookCoverDesign] : null}
                    fullWrapImageUrl={FULLWRAP_DESIGNS.includes(stBookCoverDesign) ? COVER_FULL_RES[stBookCoverDesign] : null}
                    debug={true} />
                ).toBlob();
                __downloadBlob(gridCoverBlob, `talebound-cover-GRID-${__stamp}.pdf`);
              } catch (gridErr) { console.warn('[Cover Grid] grid render failed (non-blocking):', gridErr); }
            }

            setStOrderError('Creating your order...');
            const authToken = await getAuthToken();
            const authHeaders = authToken
              ? { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` }
              : { 'Content-Type': 'application/json' };

            const checkoutResp = await fetch(`${ORDER_API_BASE}/order/create-checkout`, {
              method: 'POST', headers: authHeaders,
              body: JSON.stringify({
                bookTitle: stBookTitle,
                pageCount: realPages, shippingAddress: orderAddress, // TB-102: real rendered count, not estimate
                quantity: totalCopies,
                extraShippingAddresses: extraShippingAddresses.length > 0 ? extraShippingAddresses : undefined,
                enhancementChoice: null, // beta: enhancements are free (capped at 10)
                enhancementCount: stBookPages.filter(p => p.photoEnhanced).length,
                promoCode: stPromoCode.trim() ? stPromoCode.trim().toUpperCase() : undefined,
              }),
            });
            const checkoutData = await checkoutResp.json();
            if (!checkoutData.orderId) throw new Error(checkoutData.error || 'Could not create order');

            setStOrderError('Uploading your book files...');
            const uploadResp = await fetch(`${ORDER_API_BASE}/order/upload-url`, {
              method: 'POST', headers: authHeaders,
              body: JSON.stringify({ orderId: checkoutData.orderId }),
            });
            const uploadData = await uploadResp.json();
            if (!uploadData.interiorUploadUrl) throw new Error('Could not get upload URLs');

            await Promise.all([
              fetch(uploadData.interiorUploadUrl, { method: 'PUT', headers: { 'Content-Type': 'application/pdf' }, body: interiorBlob }),
              fetch(uploadData.coverUploadUrl, { method: 'PUT', headers: { 'Content-Type': 'application/pdf' }, body: coverBlob }),
            ]);

            setStOrderError('Redirecting to checkout...');
            if (checkoutData.checkoutUrl) {
              setStOrderId(checkoutData.orderId);
              // GA4 — book order heading to Stripe. Value is the promo-aware first-copy
              // price (intent signal); accurate revenue comes from the server-side
              // purchase event (Phase 3).
              trackEvent('begin_checkout', {
                currency: 'USD',
                value: (stPromoValidation?.valid && typeof stPromoValidation.finalPriceCents === 'number'
                  ? stPromoValidation.finalPriceCents : 8999) / 100,
                coupon: stPromoCode.trim() ? stPromoCode.trim().toUpperCase() : undefined,
                item_category: 'book',
                quantity: totalCopies,
              });
              window.location.href = checkoutData.checkoutUrl;
            } else {
              throw new Error(checkoutData.error || 'Could not create checkout session');
            }
          } catch (err) {
            // Surface promo-specific errors distinctly so the user knows to check their code
            const isPromoError = err.message && (err.message.toLowerCase().includes('promo') || err.message.toLowerCase().includes('code'));
            if (isPromoError) {
              setStOrderError(err.message);
              setStOrderStep('address'); // Send them back to fix the code
            } else {
              setStOrderError('Something went wrong starting checkout. Please try again — you have not been charged.');
              setStOrderStep('error');
            }
          }
          setStOrderLoading(false);
        };

        // Derive display price for Preparing Your Order screen
        const checkoutDisplayPrice = (stPromoValidation?.valid && typeof stPromoValidation.finalPriceCents === 'number')
          ? (stPromoValidation.finalPriceCents / 100).toFixed(2)
          : totalPrice.toFixed(2);
        const promoAppliedLabel = stPromoValidation?.valid && stPromoCode
          ? (stPromoCode === SALE_OFFER.code ? ` · ${SALE_OFFER.appliedLabel}` : ` · promo ${stPromoCode.toUpperCase()} applied`)
          : '';

        return (
          <div className="storyteller-content" data-journey-step="book-6-checkout" style={{ textAlign: 'center', paddingTop: 40 }}>
            <div style={{ marginBottom: 16 }}><PhBookOpen size={56} weight="duotone" /></div>
            <h2 className="st-h2" style={{ fontSize: '1.6rem', marginBottom: 8 }}>Preparing Your Order</h2>
            <p className="st-sub" style={{ marginBottom: 8 }}>{totalCopies} {totalCopies === 1 ? 'copy' : 'copies'}{promoAppliedLabel}</p>
            {/* UX_BACKLOG M-06: status vs error is now decided by whether we're loading, not by
                sniffing the message for the word "Error" (a real failure like "Payment declined"
                used to render as calm status). Loading = calm progress; not-loading + a message = error. */}
            {stOrderLoading ? (
              <div style={{ marginTop: 20 }}>
                <div className="spinner" style={{ margin: '0 auto 12px' }} />
                <p style={{ fontSize: '1rem', color: '#3c4653' }}>{stOrderError || 'Preparing your order...'}</p>
                <p style={{ fontSize: '0.9rem', color: '#7a6b54', marginTop: 4 }}>Please don't close this page.</p>
              </div>
            ) : stOrderError ? (
              <div style={{ background: 'rgba(220,80,60,0.08)', border: '1px solid rgba(220,80,60,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, textAlign: 'left' }}>
                <p style={{ fontSize: '1rem', color: '#c0392b', margin: 0 }}>{stOrderError}</p>
                <p style={{ fontSize: '0.9rem', color: '#7a6b54', margin: '6px 0 0' }}>You can go back and try again.</p>
              </div>
            ) : (
              <button onClick={doCheckout} className="st-btn-primary" style={{ width: '100%', marginTop: 16 }}>
                {stPromoValidation?.valid && stPromoValidation.finalPriceCents === 0 ? 'Place Order' : `Pay $${checkoutDisplayPrice} & Place Order`}
              </button>
            )}
            <button onClick={() => { setStOrderStep(isGiftBook ? 'upsell' : 'address'); setStOrderLoading(false); setStOrderError(''); window.scrollTo(0, 0); }}
              className="st-btn-secondary" style={{ width: '100%', marginTop: 12 }}>
              ← Back
            </button>
          </div>
        );
      }

      // Shipping address form (default)
      const addressValid = stOrderAddress.name?.trim() && stOrderAddress.street1?.trim() && stOrderAddress.city?.trim() && stOrderAddress.stateCode?.trim() && stOrderAddress.zip?.trim();
      return (
        <div className="storyteller-content" data-journey-step="book-6">
          <button onClick={() => { setStBookStep(0); setStOrderStep(null); window.scrollTo(0, 0); }} className="st-back">← Back</button>
          <h2 className="st-h2" style={{ fontSize: '1.9rem', marginBottom: 4 }}>Order Your Book</h2>
          <p className="st-sub" style={{ marginBottom: 28 }}>Hardcover 8.5×11" keepsake book, printed and shipped to your door.</p>

          {/* Order summary */}
          <div className="st-warm-box" style={{ marginBottom: 28, display: 'flex', alignItems: 'center', gap: 16 }}>
            <div><PhBookOpen size={40} weight="duotone" /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 600, color: '#0f172a', fontFamily: "'Lora', serif" }}>"{stBookTitle}"</div>
              <div style={{ fontSize: '1rem', color: '#3c4653', marginTop: 2 }}>{stBookPages.length} chapters · Hardcover</div>
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', fontFamily: "'Lora', serif", textAlign: 'right' }}>
              {(() => {
                const p = orderPricing();
                if (!p.onSale) return '$89.99';
                return (
                  <>
                    <span style={{ display: 'block', fontSize: '0.95rem', fontWeight: 500, textDecoration: 'line-through', opacity: 0.5 }}>{fmtUsd(p.anchorCents)}</span>
                    {fmtUsd(p.finalCents)}
                    {p.isSaleOffer && <span style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#2e7d32' }}>{SALE_OFFER.tag}</span>}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Promo / gift code — moved ABOVE the address form (UX_BACKLOG H-03/SYS-05) so
              gifted users see "claim your free hardcover" before filling a long form, and the
              price reflects any discount up front. */}
          <div style={{ marginBottom: 28 }}>
            <label style={{
              fontSize: '1rem', color: '#3c4653', display: 'block', marginBottom: 5,
              fontFamily: "'Lora', serif", letterSpacing: '1px', textTransform: 'uppercase',
            }}>
              Promo or Gift Code <span style={{ fontWeight: 400, fontStyle: 'italic', textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
            </label>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', fontStyle: 'italic', margin: '0 0 8px', fontFamily: "'Lora', serif" }}>
              Were you gifted Talebound? Enter your gift code here to claim your free hardcover.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="st-input"
                style={{ flex: 1, textTransform: 'uppercase' }}
                value={stPromoCode}
                onChange={e => {
                  const v = e.target.value.trim().toUpperCase();
                  setStPromoCode(v);
                  if (stPromoValidation) setStPromoValidation(null);
                }}
                placeholder="Enter promo or gift code"
                maxLength={32}
              />
              <button
                className="st-btn-secondary"
                style={{ whiteSpace: 'nowrap' }}
                disabled={!stPromoCode || stPromoLoading}
                onClick={async () => {
                  setStPromoLoading(true);
                  setStPromoValidation(null);
                  try {
                    const resp = await fetch(`${ORDER_API_BASE}/order/validate-promo`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ promoCode: stPromoCode }),
                    });
                    const data = await resp.json();
                    setStPromoValidation(data);
                  } catch {
                    setStPromoValidation({ valid: false, error: 'Could not validate code. Try again.' });
                  }
                  setStPromoLoading(false);
                }}
              >
                {stPromoLoading ? '...' : 'Apply'}
              </button>
            </div>
            {stPromoValidation && stPromoValidation.valid && (
              <p style={{ fontSize: '0.9rem', color: '#2e7d32', marginTop: 6 }}>
                {stPromoCode === SALE_OFFER.code
                  ? SALE_OFFER.appliedLabel
                  : `Code applied. You save ${fmtUsd(stPromoValidation.discountCents)}.`}
              </p>
            )}
            {stPromoValidation && !stPromoValidation.valid && (
              <p style={{ fontSize: '0.9rem', color: '#c0392b', marginTop: 6 }}>
                {stPromoValidation.error}
              </p>
            )}
          </div>

          {/* Shipping address */}
          <p style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#0f172a', textTransform: 'uppercase', marginBottom: 16 }}>Shipping Address</p>

                    <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '1rem', color: '#3c4653', display: 'block', marginBottom: 5, fontFamily: "'Lora', serif", letterSpacing: '1px', textTransform: 'uppercase' }}>First Name</label>
              <input className="st-input" autoComplete="new-password" value={stOrderAddress.firstName || ''} onChange={e => { setStOrderAddress(p => ({...p, firstName: e.target.value, name: (e.target.value + ' ' + (p.lastName || '')).trim()}));}} placeholder="" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '1rem', color: '#3c4653', display: 'block', marginBottom: 5, fontFamily: "'Lora', serif", letterSpacing: '1px', textTransform: 'uppercase' }}>Last Name</label>
              <input className="st-input" autoComplete="new-password" value={stOrderAddress.lastName || ''} onChange={e => { setStOrderAddress(p => ({...p, lastName: e.target.value, name: ((p.firstName || '') + ' ' + e.target.value).trim()}));}} placeholder="" />
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: '1rem', color: '#3c4653', display: 'block', marginBottom: 5, fontFamily: "'Lora', serif", letterSpacing: '1px', textTransform: 'uppercase' }}>Street Address</label>
            <input ref={streetInputRef} autoComplete="new-password" className="st-input" value={stOrderAddress.street1} onChange={e => { setStOrderAddress(p => ({...p, street1: e.target.value}));}} placeholder="Start typing to search..." />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: '1rem', color: '#3c4653', display: 'block', marginBottom: 5, fontFamily: "'Lora', serif", letterSpacing: '1px', textTransform: 'uppercase' }}>Apt / Suite <span style={{ fontWeight: 400, fontStyle: 'italic', textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
            <input className="st-input" autoComplete="new-password" value={stOrderAddress.street2} onChange={e => { setStOrderAddress(p => ({...p, street2: e.target.value}));}} placeholder="" />
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
            <div style={{ flex: 2 }}>
              <label style={{ fontSize: '1rem', color: '#3c4653', display: 'block', marginBottom: 5, fontFamily: "'Lora', serif", letterSpacing: '1px', textTransform: 'uppercase' }}>City</label>
              <input className="st-input" autoComplete="new-password" value={stOrderAddress.city} onChange={e => { setStOrderAddress(p => ({...p, city: e.target.value}));}} placeholder="" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '1rem', color: '#3c4653', display: 'block', marginBottom: 5, fontFamily: "'Lora', serif", letterSpacing: '1px', textTransform: 'uppercase' }}>State</label>
              <input className="st-input" autoComplete="new-password" value={stOrderAddress.stateCode} onChange={e => { setStOrderAddress(p => ({...p, stateCode: e.target.value.toUpperCase().slice(0, 2)}));}} placeholder="" maxLength={2} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '1rem', color: '#3c4653', display: 'block', marginBottom: 5, fontFamily: "'Lora', serif", letterSpacing: '1px', textTransform: 'uppercase' }}>ZIP</label>
              <input className="st-input" autoComplete="new-password" value={stOrderAddress.zip} onChange={e => { setStOrderAddress(p => ({...p, zip: e.target.value}));}} placeholder="" />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: '1rem', color: '#3c4653', display: 'block', marginBottom: 5, fontFamily: "'Lora', serif", letterSpacing: '1px', textTransform: 'uppercase' }}>Phone <span style={{ fontWeight: 400, fontStyle: 'italic', textTransform: 'none', letterSpacing: 0 }}>(for delivery updates)</span></label>
            <input className="st-input" autoComplete="new-password" value={stOrderAddress.phone} onChange={e => setStOrderAddress(p => ({...p, phone: e.target.value}))} placeholder="(555) 000-0000" />
          </div>

          {/* Error display */}
          {stOrderError && !stOrderLoading && (
            <div style={{ background: 'rgba(220,80,60,0.08)', border: '1px solid rgba(220,80,60,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
              <p style={{ fontSize: '1rem', color: '#c0392b', margin: 0 }}> {stOrderError}</p>
            </div>
          )}

          {/* Checkout */}
          {isGiftBook ? (
            <button disabled={!addressValid} onClick={() => { setCbExtraCopies(0); setCbExtraAddresses([]); setCbUseSameAddress(true); setStOrderStep('upsell'); window.scrollTo(0, 0); }}
              className="st-btn-primary" style={{ width: '100%', marginTop: 8 }}>
              Continue →
            </button>
          ) : (
            <button disabled={!addressValid || stOrderLoading} onClick={() => { setStOrderStep('address_checkout'); }}
              className="st-btn-primary" style={{ width: '100%', marginTop: 8, background: stOrderLoading ? 'rgba(15,23,42,0.07)' : undefined }}>
              {stOrderLoading
                ? (stOrderError && !stOrderError.includes('Error') ? ` ${stOrderError}` : ' Preparing checkout...')
                : (() => {
                    const p = orderPricing();
                    return p.finalCents === 0 ? 'Place Order' : `Pay ${fmtUsd(p.finalCents)} & Place Order`;
                  })()
              }
            </button>
          )}

          <p style={{ fontSize: '1rem', color: '#3c4653', textAlign: 'center', marginTop: 16 }}>
            Secure payment via Stripe. Free shipping within the US. Usually arrives in 7-10 business days.
          </p>
        </div>
      );
    }

    if (stBookStep === 7) {
      const handleCreateInvite = async () => {
        if (!stInviteName.trim()) { showToast('Please enter the contributor\'s name', 'error'); return; }
        if (!stInviteQuestion.trim()) { showToast('Please pick or write a question', 'error'); return; }
        setStInviteLoading(true);
        try {
          // TB-044: cryptographically secure invite token (was Math.random — guessable).
          // 16 random bytes = 128 bits of entropy, encoded as 32-char hex.
          const tokenBytes = new Uint8Array(16);
          (window.crypto || window.msCrypto).getRandomValues(tokenBytes);
          const token = Array.from(tokenBytes).map(b => b.toString(16).padStart(2, '0')).join('');
          const inviteData = {
            token,
            userId: user.userId || user.username,
            creatorName: stBookAuthor || 'Your family',
            bookTitle: stBookTitle || 'Our Family Story',
            question: stInviteQuestion.trim(),
            message: stInviteMessage.trim() || `We're putting together a family book and would love to include your story.`,
            contributorName: stInviteName.trim(),
            contributorRelationship: stInviteRelationship.trim(),
            // Wire the contribution straight to this book. Without celebrationBookId the
            // Lambda must rely on a fragile token-scan to attach the story, which can
            // silently orphan it (written to the DB but never added to the book).
            celebrationBookId: activeBookId,
            occasion: activeBook?.occasion,
            recipientName: activeBook?.recipientName,
            contributionDeadline: activeBook?.contributionDeadline,
            createdAt: new Date().toISOString(),
          };

          // Save invite via Lambda (direct to API Gateway)
          try {
            const inviteAuthToken = await getAuthToken();
            const resp = await fetch('https://ndb4d1r8u9.execute-api.us-east-1.amazonaws.com/dev/contribute', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(inviteAuthToken ? { 'Authorization': `Bearer ${inviteAuthToken}` } : {}) },
              body: JSON.stringify({ action: 'create_invite', ...inviteData }),
            });
            if (!resp.ok) throw new Error('Failed to save invite');
          } catch (apiErr) {
            console.warn('Invite API error:', apiErr);
            showToast('Could not save the invite. Please try again.', 'error');
            setStInviteLoading(false);
            return;
          }

          const link = `${window.location.origin}/contribute/${token}`;
          setStInviteLink(link);
          setStInvites(prev => [...prev, { ...inviteData, link }]);
          setStInviteCopied(false);

          // Add contributor to the active book's contributors array
          if (activeBookId) {
            const contributor = { name: stInviteName.trim(), relationship: stInviteRelationship.trim(), status: 'INVITED', token, question: stInviteQuestion.trim(), invitedAt: new Date().toISOString() };
            const updatedBooks = booksRef.current.map(b => b.id === activeBookId ? { ...b, contributors: [...(b.contributors || []), contributor] } : b);
            persistBooks(updatedBooks);
          }
        } catch (err) {
          showToast('Something went wrong creating the invite. Please try again.', 'error');
        }
        setStInviteLoading(false);
      };

      const handleCopyLink = () => {
        navigator.clipboard.writeText(stInviteLink).then(() => {
          setStInviteCopied(true);
          showToast('✓ Link copied!');
          setTimeout(() => setStInviteCopied(false), 3000);
        });
      };

      // Pull occasion-specific prompts if available, otherwise generic
      const occasionPrompts = isGiftBook && activeBook?.occasion && CELEBRATION_PROMPTS[activeBook.occasion]
        ? CELEBRATION_PROMPTS[activeBook.occasion].slice(0, 6).map(p => p.text)
        : null;
      const suggestedQuestions = occasionPrompts || [
        "What's a memory from childhood that still makes you smile?",
        "What's a story about our family that you think should be in this book?",
        "Describe a moment with our family that you'll never forget.",
        "What's something you wish more people in the family knew about you?",
        "What's a lesson you learned from someone in our family?",
        "What's a tradition or inside joke that makes our family ours?",
      ];

      return (
        <div className="storyteller-content" data-journey-step="book-7">
          <button onClick={() => { setStBookStep(0); setStInviteLink(''); setStInviteQuestion(''); setStInviteMessage(''); setStInviteName(''); setStInviteRelationship(''); window.scrollTo(0, 0); }} className="st-back">← Back</button>
          <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}><PhMail size={24} weight="duotone" /> Ask someone to add their story</h2>
          <p className="st-sub" style={{ marginBottom: 24 }}>Send a link to a family member. They can write a story for your book — no account needed.</p>

          {!stInviteLink ? (
            <>
              {/* Contributor name + relationship */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Their Name</label>
                  <input className="inp" value={stInviteName} onChange={e => setStInviteName(e.target.value)} placeholder="e.g. Aunt Linda" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Relationship</label>
                  <input className="inp" value={stInviteRelationship} onChange={e => setStInviteRelationship(e.target.value)} placeholder="e.g. Sister, Grandson" />
                </div>
              </div>

              {/* Pick one question */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                  Pick one question for them to answer
                </label>
                <p style={{ fontSize: '0.9rem', color: '#3c4653', marginBottom: 12, lineHeight: 1.5 }}>
                  They'll receive exactly this question. Send additional invites with different questions to ask multiple things.
                </p>
                {suggestedQuestions.map((q, i) => (
                  <button key={i} onClick={() => setStInviteQuestion(q)}
                    style={{
                      width: '100%', padding: '12px 16px', marginBottom: 8,
                      background: stInviteQuestion === q ? 'rgba(201,168,76,0.1)' : '#fff',
                      border: stInviteQuestion === q ? '2px solid #7c2d12' : '1px solid rgba(15,23,42,0.1)',
                      borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                      fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#0f172a',
                      fontStyle: 'italic', lineHeight: 1.5,
                      transition: 'all 0.15s',
                    }}>
                    "{q}"
                  </button>
                ))}
                <div style={{ marginTop: 12 }}>
                  <label style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                    Or write your own question
                  </label>
                  <input
                    value={suggestedQuestions.includes(stInviteQuestion) ? '' : stInviteQuestion}
                    onChange={e => setStInviteQuestion(e.target.value)}
                    className="inp"
                    placeholder="e.g. What's your favorite memory with Grandpa?"
                    style={{ fontStyle: 'italic' }}
                  />
                </div>
              </div>

              {/* Personal message (optional) */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                  Personal message (optional)
                </label>
                <textarea
                  value={stInviteMessage}
                  onChange={e => setStInviteMessage(e.target.value)}
                  className="inp"
                  rows={3}
                  placeholder="e.g. Hi Aunt Linda! I'm making a book for Mom and would love to include a story from you..."
                  style={{ resize: 'vertical', lineHeight: 1.6 }}
                />
              </div>

              {/* Create button */}
              <button
                onClick={handleCreateInvite}
                disabled={!stInviteName.trim() || !stInviteQuestion.trim() || stInviteLoading}
                className="st-btn-primary"
                style={{ width: '100%', padding: '16px', fontSize: '1.05rem' }}>
                {stInviteLoading ? 'Creating…' : (stInviteName.trim() ? `Create invite for ${stInviteName.trim()}` : 'Create invite')}
              </button>
            </>
          ) : (
            /* ── Link generated ── */
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>✉️</div>
              <h3 style={{ fontFamily: "'Lora', serif", fontSize: '1.3rem', color: '#0f172a', marginBottom: 8 }}>Invite created for {stInviteName}!</h3>
              <p style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#3c4653', fontStyle: 'italic', marginBottom: 24, lineHeight: 1.6 }}>
                Share this link with {stInviteName}. They'll see the question and can write their story right away — no account needed.
              </p>

              {/* Link box */}
              <div style={{ 
                background: '#fff', border: '1px solid rgba(15,23,42,0.12)', borderRadius: 10,
                padding: '14px 16px', marginBottom: 16, wordBreak: 'break-all',
                fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#3c4653', textAlign: 'left'
              }}>
                {stInviteLink}
              </div>

              {/* Copy + Share buttons */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                <button onClick={handleCopyLink} className="st-btn-primary" style={{ flex: 1, padding: '14px' }}>
                  {stInviteCopied ? '✓ Copied!' : ' Copy Link'}
                </button>
                {navigator.share && (
                  <button onClick={() => {
                    navigator.share({
                      title: 'Share a story for our family book',
                      text: stInviteMessage || `I'm making a family book and would love your story!`,
                      url: stInviteLink,
                    }).catch(() => {});
                  }} className="st-btn-secondary" style={{ flex: 1, padding: '14px' }}>
                     Share
                  </button>
                )}
              </div>

              {/* Preview what they'll see */}
              <div style={{ background: 'rgba(15,23,42,0.02)', borderRadius: 12, padding: '16px', border: '1px solid rgba(15,23,42,0.06)', textAlign: 'left', marginBottom: 20 }}>
                <div style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', letterSpacing: '2px', color: '#b0a090', textTransform: 'uppercase', marginBottom: 8 }}>They'll see</div>
                <div style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: '#0f172a', fontStyle: 'italic', lineHeight: 1.6 }}>
                  "{stInviteQuestion}"
                </div>
              </div>

              {/* Create another */}
              <button onClick={() => { setStInviteLink(''); setStInviteQuestion(''); setStInviteMessage(''); setStInviteName(''); setStInviteRelationship(''); }}
                className="st-btn-secondary" style={{ width: '100%' }}>
                 Create Another Invite
              </button>
            </div>
          )}
        </div>
      );
    }

    // ── Enhancement Preview Screen (step 8) ──
    if (stBookStep === 8 && enhancePreviewPage) {
      const previewStory = stories.find(s => s.id === enhancePreviewPage.storyId);
      const freeUsed = accountSettings.enhancementsUsed || 0;
      const freeRemaining = Math.max(0, 10 - freeUsed);
      const canEnhance = freeRemaining > 0;

      const handleSliderMove = (e) => {
        const rect = sliderRef.current?.getBoundingClientRect();
        if (!rect) return;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
        setSliderPos(pct);
      };
      const handlePointerDown = (e) => {
        sliderDragging.current = true;
        handleSliderMove(e);
        const onMove = (ev) => { if (sliderDragging.current) handleSliderMove(ev); };
        const onUp = () => { sliderDragging.current = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onUp); };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: true });
        window.addEventListener('touchend', onUp);
      };

      const isCoverPhoto = enhancePreviewPage.storyId === '__cover__';

      return (
        <div className="storyteller-content" data-journey-step="book-8">
          <button onClick={() => { setStBookStep(isCoverPhoto ? 9 : enhanceReturnStep || 0); setEnhancePreviewPage(null); window.scrollTo(0, 0); }} className="st-back">← Back</button>
          <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 4 }}>
            {isCoverPhoto ? 'Cover Photo' : enhancePreviewPage.photoEnhanced ? 'Photo Details' : 'AI Photo Enhancement'}
          </h2>

          {/* Photo title — editable (not for cover photo) */}
          {!isCoverPhoto && (() => {
            const currentPhotoCaption = enhancePreviewPage.caption || '';
            const handleSavePhotoTitle = () => {
              setIsEditingPhotoTitle(false);
              const trimmed = photoTitleDraft.trim();
              const updatedPages = stBookPages.map(p =>
                p.storyId === enhancePreviewPage.storyId ? { ...p, caption: trimmed } : p
              );
              setStBookPages(updatedPages);
              setEnhancePreviewPage(prev => prev ? { ...prev, caption: trimmed } : prev);
              saveBookData({ pages: cleanPagesForSave(updatedPages) });
            };
            return (
              <div style={{ marginBottom: '20px' }}>
                {isEditingPhotoTitle ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      className="st-input"
                      style={{ fontSize: '1.15rem', flex: 1 }}
                      value={photoTitleDraft}
                      onChange={e => setPhotoTitleDraft(e.target.value)}
                      onBlur={handleSavePhotoTitle}
                      onKeyDown={e => { if (e.key === 'Enter') handleSavePhotoTitle(); if (e.key === 'Escape') setIsEditingPhotoTitle(false); }}
                      placeholder="e.g. Mom and Dad, 1987"
                      autoFocus
                      maxLength={80}
                    />
                    <button
                      onClick={handleSavePhotoTitle}
                      style={{
                        padding: '10px 18px', borderRadius: '10px', background: '#7c2d12',
                        color: '#0f172a', border: 'none', fontFamily: "'Lora', serif",
                        fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                        whiteSpace: 'nowrap', flexShrink: 0,
                      }}
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => { setPhotoTitleDraft(currentPhotoCaption); setIsEditingPhotoTitle(true); }}
                    style={{
                      fontSize: '1.15rem',
                      color: currentPhotoCaption ? '#0f172a' : 'rgba(15,23,42,0.35)',
                      fontFamily: "'Lora', serif",
                      fontStyle: currentPhotoCaption ? 'normal' : 'italic',
                      cursor: 'text', display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '6px 0', borderBottom: '1px dashed rgba(15,23,42,0.15)',
                      width: 'fit-content',
                    }}
                  >
                    {currentPhotoCaption || 'Add a photo title...'}
                    <span style={{ fontSize: '0.75rem', color: 'rgba(15,23,42,0.3)', fontStyle: 'normal' }}>✏️</span>
                  </div>
                )}
                <div style={{ fontSize: '0.78rem', color: 'rgba(15,23,42,0.4)', marginTop: '5px', fontFamily: "'Lora', serif" }}>
                  Photo title — appears in the book and on the photo card
                </div>
              </div>
            );
          })()}

          {/* Enhancement counter */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: canEnhance ? 'linear-gradient(135deg, #fffdf5, #fff8e7)' : 'rgba(15,23,42,0.04)',
            border: `1.5px solid ${canEnhance ? 'rgba(201,168,76,0.35)' : 'rgba(15,23,42,0.1)'}`,
            borderRadius: 10, padding: '10px 16px', marginBottom: 20,
          }}>
            {canEnhance ? (
              <span style={{ fontSize: '0.92rem', color: '#3c4653', fontWeight: 500 }}>
                {freeRemaining} of 10 enhancement{freeRemaining !== 1 ? 's' : ''} remaining
              </span>
            ) : (
              <span style={{ fontSize: '0.92rem', color: '#3c4653' }}>
                You've used all 10 enhancements
              </span>
            )}
          </div>

          {/* State: Already enhanced — show photo + revert option */}
          {!previewLoading && !previewData && enhancePreviewPage.photoEnhanced && (
            <>
              {(enhancePreviewPage.photoBase64 || enhancePreviewPage.photoUrl) && (
                <div style={{ marginBottom: 20, position: 'relative' }}>
                  <img src={enhancePreviewPage.photoBase64 || enhancePreviewPage.photoUrl} alt="Enhanced photo" style={{ width: '100%', borderRadius: 12, border: '1px solid rgba(15,23,42,0.1)' }} />
                  <div style={{
                    position: 'absolute', top: 12, right: 12,
                    background: 'rgba(90,122,74,0.9)', backdropFilter: 'blur(8px)',
                    color: '#fff', padding: '5px 14px', borderRadius: 20,
                    fontSize: 12, fontWeight: 600, letterSpacing: '0.5px',
                    fontFamily: "'Lora', serif",
                  }}>✓ Enhanced</div>
                </div>
              )}
              <div style={{
                background: 'rgba(90,122,74,0.06)', border: '1px solid rgba(90,122,74,0.2)',
                borderRadius: 12, padding: '14px 18px', marginBottom: 20,
                fontSize: '0.92rem', color: '#3a5a2a', lineHeight: 1.6,
              }}>
                This photo has been enhanced with AI — sharpened, upscaled to 300 DPI, and optimized for print.
              </div>
              {enhancePreviewPage.originalPhotoPath && (
                <button
                  onClick={() => revertEnhancedPhoto(enhancePreviewPage)}
                  style={{
                    width: '100%', padding: '14px 20px', marginBottom: 10,
                    background: 'none', border: '1.5px solid rgba(192,57,43,0.3)',
                    borderRadius: 12, cursor: 'pointer',
                    fontSize: '1rem', color: '#8b2c1a',
                    fontFamily: "'Lora', serif",
                  }}>
                  ↩ Revert to original photo
                </button>
              )}
              <button onClick={() => { setStBookStep(isCoverPhoto ? 9 : enhanceReturnStep || 9); setEnhancePreviewPage(null); window.scrollTo(0, 0); }}
                className="st-btn-secondary" style={{ width: '100%', borderRadius: 12 }}>
                ← Back
              </button>
            </>
          )}

          {/* State: Not started — show photo + explain + enhance button */}
          {!previewLoading && !previewData && !enhancePreviewPage.photoEnhanced && (
            <>
              {(enhancePreviewPage.photoBase64 || enhancePreviewPage.photoUrl) && (
                <div style={{ marginBottom: 20, position: 'relative' }}>
                  <img src={enhancePreviewPage.photoBase64 || enhancePreviewPage.photoUrl} alt="Your photo" style={{ width: '100%', borderRadius: 12, border: '1px solid rgba(15,23,42,0.1)' }} />
                </div>
              )}

              {/* What enhancement does */}
              <div style={{
                background: '#fffdf5', border: '1px solid rgba(201,168,76,0.2)',
                borderRadius: 12, padding: '16px 18px', marginBottom: 24,
              }}>
                <p style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', marginBottom: 8, fontFamily: "'Lora', serif" }}>
                  What AI Photo Enhancement does
                </p>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.92rem', color: '#3c4653', lineHeight: 1.7 }}>
                  <li><strong>Print-ready upscaling</strong> — 300 DPI at full page size</li>
                  <li><strong>Face clarity</strong> — sharpens faces naturally</li>
                  <li><strong>Detail recovery</strong> — restores detail lost in compression</li>
                  <li><strong>Print optimization</strong> — calibrated for ink on paper</li>
                </ul>
              </div>

              {canEnhance ? (
                <>
                  <button onClick={() => runEnhancement(enhancePreviewPage)}
                    className="st-btn-primary" style={{ width: '100%', marginBottom: 10, background: '#7c2d12', borderColor: '#7c2d12' }}>
                    {freeRemaining === 1 ? ' Use My Last Enhancement' : ` Enhance Photo  ·  ${freeRemaining} remaining`}
                  </button>
                  <p style={{ fontSize: '0.82rem', color: '#b0a698', textAlign: 'center', marginBottom: 10 }}>
                    Takes about 30 seconds — you'll see a before/after comparison
                  </p>
                </>
              ) : (
                /* TB-082: replaced upgrade-to-premium prompt with friendly cap message */
                <div style={{
                  background: 'rgba(15,23,42,0.04)', border: '1.5px solid rgba(15,23,42,0.1)',
                  borderRadius: 14, padding: '20px 20px', marginBottom: 16, textAlign: 'center',
                }}>
                  <p style={{ fontSize: '1rem', color: '#3c4653' }}>
                    You've used your 10 included photo enhancements. More options coming soon.
                  </p>
                </div>
              )}
              <button onClick={() => { setStBookStep(isCoverPhoto ? 9 : enhanceReturnStep || 0); setEnhancePreviewPage(null); window.scrollTo(0, 0); }}
                className="st-btn-secondary" style={{ width: '100%' }}>
                Skip — use photo as-is
              </button>
            </>
          )}

          {/* State: Processing — show photo with staged messages */}
          {previewLoading && (
            <div style={{ position: 'relative', marginBottom: 24 }}>
              {(enhancePreviewPage.photoBase64 || enhancePreviewPage.photoUrl) && (
                <div style={{ position: 'relative' }}>
                  <img src={enhancePreviewPage.photoBase64 || enhancePreviewPage.photoUrl} alt="Your photo" style={{ width: '100%', borderRadius: 12, filter: 'brightness(0.7)', transition: 'filter 0.5s' }} />
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', borderRadius: 12,
                    background: 'rgba(15,23,36,0.4)',
                  }}>
                    <div className="spinner" style={{ marginBottom: 16, borderColor: 'rgba(255,255,255,0.2)', borderTopColor: '#7c2d12' }} />
                    <p style={{ fontSize: '1.05rem', color: '#fff', fontWeight: 500, textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                      {enhanceProcessingMsg}
                    </p>
                  </div>
                </div>
              )}
              {!enhancePreviewPage.photoUrl && (
                <div style={{ textAlign: 'center', padding: '60px 16px' }}>
                  <div className="spinner" style={{ margin: '0 auto 16px' }} />
                  <p style={{ fontSize: '1rem', color: '#3c4653' }}>{enhanceProcessingMsg}</p>
                </div>
              )}
            </div>
          )}

          {/* State: Result — before/after slider */}
          {!previewLoading && previewData && (
            <>
              {/* Success banner */}
              <div style={{
                textAlign: 'center', marginBottom: 20, padding: '14px 20px',
                background: 'linear-gradient(135deg, rgba(90,122,74,0.1), rgba(201,168,76,0.06))',
                border: '1.5px solid rgba(90,122,74,0.3)', borderRadius: 14,
                animation: 'fadeIn 0.5s ease',
              }}>
                <p style={{ fontSize: '1.2rem', fontWeight: 700, color: '#3a5a2a', fontFamily: "'Lora', serif", marginBottom: 4, letterSpacing: '0.5px' }}>
                   Enhancement Complete
                </p>
                <p style={{ fontSize: '0.88rem', color: '#5a7a4a' }}>
                  Drag the handle to reveal the difference
                </p>
              </div>

              {/* Before/After comparison slider */}
              <div
                ref={sliderRef}
                onMouseDown={handlePointerDown}
                onTouchStart={handlePointerDown}
                style={{
                  position: 'relative', width: '100%', aspectRatio: '4/3',
                  borderRadius: 16, overflow: 'hidden', cursor: 'col-resize',
                  border: '2px solid rgba(201,168,76,0.2)',
                  boxShadow: '0 8px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
                  marginBottom: 12, userSelect: 'none', WebkitUserSelect: 'none',
                  animation: 'fadeIn 0.6s ease',
                }}
              >
                {/* Enhanced (full background) */}
                <img src={previewData.enhancedUrl} alt="Enhanced" draggable={false}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />

                {/* Original (clipped by slider position) */}
                <div style={{ position: 'absolute', inset: 0, width: `${sliderPos}%`, overflow: 'hidden' }}>
                  <img src={previewData.originalUrl} alt="Original" draggable={false}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'left center', minWidth: sliderRef.current ? sliderRef.current.offsetWidth : '100%', pointerEvents: 'none' }} />
                </div>

                {/* Slider handle line */}
                <div style={{
                  position: 'absolute', top: 0, bottom: 0, left: `${sliderPos}%`,
                  width: 2, background: 'rgba(255,255,255,0.9)',
                  transform: 'translateX(-1px)',
                  boxShadow: '0 0 8px rgba(0,0,0,0.3), 0 0 20px rgba(0,0,0,0.1)',
                  pointerEvents: 'none',
                }}>
                  {/* Drag handle — diamond shape */}
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%) rotate(45deg)',
                    width: 38, height: 38, borderRadius: 8,
                    background: 'linear-gradient(135deg, #ffffff, #f0ebe2)',
                    boxShadow: '0 3px 12px rgba(0,0,0,0.25), 0 0 0 2px rgba(201,168,76,0.4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <div style={{ transform: 'rotate(-45deg)', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <span style={{ fontSize: 11, color: '#a08030', fontWeight: 800 }}>◂</span>
                      <span style={{ fontSize: 11, color: '#a08030', fontWeight: 800 }}>▸</span>
                    </div>
                  </div>
                </div>

                {/* Labels */}
                <div style={{
                  position: 'absolute', top: 12, left: 12,
                  background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                  color: '#fff', padding: '5px 14px', borderRadius: 20,
                  fontSize: 12, fontWeight: 600, letterSpacing: '0.5px',
                  fontFamily: "'Lora', serif", textTransform: 'uppercase',
                  opacity: sliderPos > 15 ? 1 : 0, transition: 'opacity 0.2s',
                }}>Before</div>
                <div style={{
                  position: 'absolute', top: 12, right: 12,
                  background: 'linear-gradient(135deg, rgba(201,168,76,0.85), rgba(160,128,48,0.9))',
                  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                  color: '#fff', padding: '5px 14px', borderRadius: 20,
                  fontSize: 12, fontWeight: 600, letterSpacing: '0.5px',
                  fontFamily: "'Lora', serif", textTransform: 'uppercase',
                  opacity: sliderPos < 85 ? 1 : 0, transition: 'opacity 0.2s',
                }}>After</div>
              </div>

              {/* Hint text */}
              <p style={{ fontSize: '0.82rem', color: 'rgba(15,23,42,0.4)', textAlign: 'center', marginBottom: 24, fontStyle: 'italic', fontFamily: "'Lora', serif" }}>
                This is how your photo will look printed in your book
              </p>

              {/* Action buttons */}
              <button onClick={() => applyEnhancedPhoto(previewData)}
                style={{
                  width: '100%', padding: '16px 24px', marginBottom: 10,
                  background: 'linear-gradient(135deg, #7c2d12, #a8882e)',
                  border: 'none', borderRadius: 12, cursor: 'pointer',
                  fontSize: '1.1rem', fontWeight: 700, color: '#fff',
                  fontFamily: "'Lora', serif", letterSpacing: '0.5px',
                  boxShadow: '0 4px 16px rgba(201,168,76,0.35)',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = '0 6px 24px rgba(201,168,76,0.45)'; }}
                onMouseLeave={e => { e.target.style.transform = ''; e.target.style.boxShadow = '0 4px 16px rgba(201,168,76,0.35)'; }}
              >
                Use Enhanced Version
              </button>
              <button onClick={() => { setStBookStep(isCoverPhoto ? 9 : enhanceReturnStep || 0); setEnhancePreviewPage(null); window.scrollTo(0, 0); }}
                className="st-btn-secondary" style={{ width: '100%', borderRadius: 12 }}>
                Keep original
              </button>
            </>
          )}
        </div>
      );
    }

    // ═══ STEP 9 — PHOTO DASHBOARD ═══
    if (stBookStep === 9) {
      // Build cover photo virtual page (if cover photo exists)
      const coverPhotoSrc = stBookCoverPhotoBase64 || stBookCoverPhotoUrl;
      const coverPhotoPage = coverPhotoSrc ? {
        storyId: '__cover__',
        photoUrl: stBookCoverPhotoUrl,
        photoBase64: stBookCoverPhotoBase64,
        photoPath: stBookCoverPhotoPath,
        photoEnhanced: activeBook?.coverPhotoEnhanced || false,
        originalPhotoPath: activeBook?.coverPhotoOriginalPath || null,
        caption: 'Cover Photo',
      } : null;
      const chapterPhotos = stBookPages.filter(p => p.photoUrl);
      const photosPages = coverPhotoPage ? [coverPhotoPage, ...chapterPhotos] : chapterPhotos;
      const enhancedPhotos = photosPages.filter(p => p.photoEnhanced);
      const unenhancedPhotos = photosPages.filter(p => !p.photoEnhanced);
      const freeUsed = accountSettings.enhancementsUsed || 0;
      const freeRemaining = Math.max(0, 10 - freeUsed);

      // Filtered list based on photoFilter
      const filteredPhotos = photoFilter === 'all' ? photosPages
        : photoFilter === 'enhanced' ? enhancedPhotos
        : photoFilter === 'unenhanced' ? unenhancedPhotos
        : photosPages; // fallback

      return (
        <div className="storyteller-content" data-journey-step="book-9">
          <button onClick={() => { setStBookStep(0); setPhotoFilter('all'); window.scrollTo(0, 0); }} className="st-back">← Back</button>
          <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 4 }}>Photo Manager</h2>
          <p className="st-sub" style={{ marginBottom: 20 }}>Manage and enhance all photos in your book.</p>

          {/* Stats bar */}
          <div style={{
            background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 12, padding: '14px 16px', marginBottom: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#0f172a', fontFamily: "'Lora', serif" }}>
                {photosPages.length} photo{photosPages.length !== 1 ? 's' : ''} in your book
              </span>
              <span style={{ fontSize: '0.85rem', color: '#3c4653' }}>
                {enhancedPhotos.length} enhanced
              </span>
            </div>
            {/* Progress bar */}
            <div style={{ height: 6, background: 'rgba(15,23,42,0.08)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 3,
                width: photosPages.length > 0 ? `${(enhancedPhotos.length / photosPages.length) * 100}%` : '0%',
                background: 'linear-gradient(90deg, #7c2d12, #5a7a4a)',
                transition: 'width 0.4s ease',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: '0.82rem', color: '#3c4653' }}>
              <span>{unenhancedPhotos.length} ready to enhance</span>
              <span>{freeRemaining} of 10 remaining</span>
            </div>
          </div>

          {/* Enhance All button — disabled during beta (10 enhancement limit) */}

          {/* Filter bar */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
            {[
              { id: 'all', label: `All (${photosPages.length})` },
              { id: 'unenhanced', label: `Needs Enhancement (${unenhancedPhotos.length})` },
              { id: 'enhanced', label: `Enhanced (${enhancedPhotos.length})` },
            ].map(f => (
              <button key={f.id} onClick={() => setPhotoFilter(f.id)} style={{
                padding: '6px 14px', borderRadius: 20, fontSize: '0.82rem', fontWeight: 500,
                background: photoFilter === f.id ? '#0f172a' : 'rgba(15,23,42,0.06)',
                color: photoFilter === f.id ? '#e8dcc8' : '#3c4653',
                border: photoFilter === f.id ? '1.5px solid #7c2d12' : '1px solid rgba(15,23,42,0.12)',
                cursor: 'pointer', transition: 'all 0.15s',
                fontFamily: "'Lora', serif",
              }}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Photo grid */}
          {filteredPhotos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 16px', color: '#3c4653' }}>
              <p style={{ fontSize: '1.1rem', fontFamily: "'Lora', serif" }}>
                {photoFilter === 'enhanced' ? 'No enhanced photos yet' : photoFilter === 'unenhanced' ? 'All photos are enhanced!' : 'No photos in your book yet'}
              </p>
              <p style={{ fontSize: '0.9rem', marginTop: 8 }}>
                {photoFilter === 'unenhanced' ? 'Great work — every photo is print-ready.' : 'Add photos to your chapters to get started.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(184px, 184px))', gap: 10 }}>
              {filteredPhotos.map(page => {
                const isCover = page.storyId === '__cover__';
                const story = isCover ? null : stories.find(s => s.id === page.storyId);
                const chapterTitle = isCover ? 'Cover Photo' : (story ? story.title : 'Untitled');
                return (
                  <div
                    key={page.storyId}
                    className="photo-grid-card"
                    onClick={() => handleEnhancePhoto(page)}
                  >
                    {/* TOP: photo + title overlay */}
                    <div
                      className="pgc-photo"
                      style={{ backgroundImage: `url(${page.photoUrl || page.photoBase64})` }}
                    >
                      <div className="pgc-title">
                        {page.caption || chapterTitle}
                      </div>
                    </div>
                    {/* BOTTOM: enhance button */}
                    <div className="pgc-action">
                      {page.photoEnhanced
                        ? <div className="pgc-enhanced-badge">✓ Enhanced</div>
                        : <button className="pgc-enhance-btn" onClick={e => { e.stopPropagation(); handleEnhancePhoto(page); }}> Enhance</button>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Enhancement counter */}
          <div style={{
            marginTop: 24, padding: '16px', borderRadius: 12,
            background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.18)',
            textAlign: 'center',
          }}>
            <p style={{ fontSize: '0.9rem', color: '#3c4653' }}>
              {freeRemaining > 0
                ? `${freeRemaining} of 10 enhancement${freeRemaining !== 1 ? 's' : ''} remaining`
                : 'All 10 enhancements used'}
            </p>
          </div>

          {/* ── Overview & FAQ ── */}
          <div style={{ marginTop: 32, borderTop: '1px solid rgba(15,23,42,0.08)', paddingTop: 28 }}>

            {/* Overview */}
            <div style={{
              background: '#fffdf5', border: '1px solid rgba(201,168,76,0.15)',
              borderRadius: 14, padding: '22px 22px 20px', marginBottom: 24,
            }}>
              <h3 style={{ fontFamily: "'Lora', serif", fontSize: '1.15rem', color: '#0f172a', marginBottom: 10 }}>
                Why enhance your photos?
              </h3>
              <p style={{ fontSize: '0.95rem', color: '#0f172a', lineHeight: 1.7, marginBottom: 14 }}>
                Photos that look great on your phone can come out soft, grainy, or faded when printed in a hardcover book. Screen pixels and ink on paper are very different — what looks sharp at arm's length on a phone may disappoint at full page size.
              </p>
              <p style={{ fontSize: '0.95rem', color: '#0f172a', lineHeight: 1.7, marginBottom: 16 }}>
                Photo Manager uses professional-grade AI to prepare each photo specifically for print. It sharpens faces, brings back detail lost to compression, and adjusts everything for how ink actually lands on paper — so your printed book looks as vivid as your memories.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { icon: <PhUser size={18} weight="duotone" />, text: 'Faces are sharpened naturally — no plastic, over-processed look' },
                  { icon: <PhPalette size={18} weight="duotone" />, text: 'Colors and contrast are tuned for printed paper, not screens' },
                  { icon: <PhCamera size={18} weight="duotone" />, text: 'Old scans, low-light shots, and phone photos benefit the most' },
                  { icon: <PhUndo size={18} weight="duotone" />, text: 'Don\'t like the result? You can always revert to the original' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: '1rem', flexShrink: 0 }}>{item.icon}</span>
                    <span style={{ fontSize: '0.9rem', color: '#3c4653', lineHeight: 1.5 }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <h3 style={{ fontFamily: "'Lora', serif", fontSize: '1.15rem', color: '#0f172a', marginBottom: 14 }}>
              Common Questions
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                {
                  q: 'Do all my photos need enhancement?',
                  a: 'Not necessarily. Recent photos taken with a good phone camera often print fine on their own. Enhancement makes the biggest difference on older photos, scanned prints, screenshots, or anything taken in low light. Try one and compare — that\'s what the free enhancements are for.',
                },
                {
                  q: 'Will my photo look different?',
                  a: 'Enhancement sharpens and clarifies, but it keeps the character of your original photo. It won\'t dramatically change colors or alter what\'s in the image. The before/after slider shows you exactly what the result looks like before you commit.',
                },
                {
                  q: 'What if I don\'t like the result?',
                  a: 'Tap any enhanced photo and choose "Revert to original photo." Your original is always saved — enhancement is never destructive.',
                },
                {
                  q: 'How long does it take?',
                  a: 'About 20–40 seconds per photo. You\'ll see a before/after comparison as soon as it\'s ready.',
                },
                {
                  q: 'How many enhancements do I get?',
                  a: 'During beta, every account gets 10 photo enhancements. Use them on whichever photos matter most for your printed book.',
                },
                {
                  q: 'Is the preview what actually gets printed?',
                  a: 'Yes. The enhanced photo you see in the before/after slider is the exact image that goes into your book. What you see is what gets printed.',
                },
                {
                  q: 'Can I enhance a photo more than once?',
                  a: 'You can revert to the original and re-enhance if you\'d like, but running enhancement twice on the same photo won\'t improve it further — one pass captures the full benefit.',
                },
              ].map((faq, i) => (
                <details key={i} style={{
                  background: 'rgba(15,23,42,0.025)', border: '1px solid rgba(15,23,42,0.08)',
                  borderRadius: 10, overflow: 'hidden',
                }}>
                  <summary style={{
                    padding: '13px 16px', cursor: 'pointer',
                    fontSize: '0.95rem', fontWeight: 600, color: '#0f172a',
                    fontFamily: "'Lora', serif", listStyle: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    {faq.q}
                    <span style={{ fontSize: '0.8rem', color: '#3c4653', marginLeft: 8, flexShrink: 0 }}>+</span>
                  </summary>
                  <div style={{
                    padding: '0 16px 14px', fontSize: '0.9rem', color: '#3c4653',
                    lineHeight: 1.65,
                  }}>
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (stBookStep === 2) {
      return (
        <div className="storyteller-content" data-journey-step="book-2">
          <button onClick={() => { setStBookStep(0); window.scrollTo(0, 0); }} className="st-back">← Back</button>
          <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 4 }}>Customize My Book</h2>
          <p className="st-sub" style={{ marginBottom: 24 }}>Title, dedication, and cover design.</p>

          {/* Title */}
          <div style={{ marginBottom: 20 }}>
            <label className="st-label">Book Title</label>
            <input className="st-input" value={stBookTitle} onChange={e => setStBookTitle(e.target.value)} placeholder="Tales From My Life" />
          </div>

          {/* Subtitle */}
          <div style={{ marginBottom: 20 }}>
            <label className="st-label">Subtitle (optional)</label>
            <input className="st-input" value={stBookSubtitle} onChange={e => setStBookSubtitle(e.target.value)} placeholder="Stories, memories, and moments worth keeping" />
          </div>

          {/* Author / From */}
          <div style={{ marginBottom: 20 }}>
            <label className="st-label">{isGiftBook ? 'Author / From' : 'Author'}</label>
            <input className="st-input" value={stBookAuthor} onChange={e => setStBookAuthor(e.target.value)} placeholder={isGiftBook ? 'From Sarah, Matthew & David' : 'By Grandma'} />
            {isGiftBook && <p style={{ fontSize: '0.88rem', color: '#3c4653', marginTop: 4 }}>This appears on the cover. e.g. "From Your Grandchildren" or "From All of Us"</p>}
          </div>

          {/* Your relationship to the recipient — drives how Pip phrases the interview */}
          {isGiftBook && (
            <div style={{ marginBottom: 20 }}>
              <label className="st-label">Your relationship to {activeBook?.recipientName || 'them'}</label>
              <select className="st-input" value={activeBook?.authorRelationship || ''}
                onChange={e => { saveBookData({ authorRelationship: e.target.value || null }); showToast('Saved. Pip will phrase the questions for you.'); }}>
                <option value="">Choose one...</option>
                <option value="partner">Partner or spouse</option>
                <option value="child">Son or daughter</option>
                <option value="grandchild">Grandchild</option>
                <option value="parent">Parent</option>
                <option value="grandparent">Grandparent</option>
                <option value="sibling">Sibling</option>
                <option value="friend">Friend</option>
                <option value="colleague">Colleague or coworker</option>
                <option value="other">Other</option>
              </select>
              <p style={{ fontSize: '0.88rem', color: '#3c4653', marginTop: 4 }}>This phrases the interview the right way. A partner writes a shared 'you and them' story; everyone else answers questions about {activeBook?.recipientName || 'them'}.</p>
            </div>
          )}

          {isGiftBook && (
            <div style={{ marginBottom: 20 }}>
              <label className="st-label">Pronouns for {activeBook?.recipientName || 'them'} (optional)</label>
              <select className="st-input" value={activeBook?.recipientPronouns || ''}
                onChange={e => { saveBookData({ recipientPronouns: e.target.value || null }); showToast('Saved.'); }}>
                <option value="">Not sure / prefer not to say (they/them)</option>
                <option value="she">She/her</option>
                <option value="he">He/him</option>
              </select>
              <p style={{ fontSize: '0.88rem', color: '#3c4653', marginTop: 4 }}>Pip uses this when phrasing questions about {activeBook?.recipientName || 'them'}.</p>
            </div>
          )}

          {/* Dedication */}
          <div style={{ marginBottom: 28 }}>
            <label className="st-label">Dedication (optional)</label>
            <textarea className="st-input st-textarea" rows={3} value={stBookDedication} onChange={e => setStBookDedication(e.target.value)} placeholder="For my grandchildren — so you'll always know where you came from." style={{ minHeight: 100 }} />
          </div>

          {/* Cover Layout — wrap designs lock to Framed only (classic doesn't
              work with full-bleed wrap art; see session notes). */}
          <div style={{ marginBottom: 24, display: FULLWRAP_DESIGNS.includes(stBookCoverDesign) ? 'none' : 'block' }}>
            <label className="st-label">Cover Layout</label>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              {[
                { id: 'classic', label: 'Classic', desc: 'Text only', icon: <PhType size={24} weight="duotone" /> },
                { id: 'framed', label: 'Framed', desc: 'With cover photo', icon: <PhFrame size={24} weight="duotone" /> },
              ].map(s => {
                const active = stBookCover === s.id;
                return (
                <button key={s.id} onClick={() => setStBookCover(s.id)} style={{
                  flex: 1, minWidth: 0, padding: '14px 12px', borderRadius: 12, cursor: 'pointer',
                  background: active ? '#FFFDF5' : '#fff',
                  border: active ? '2px solid #C9A227' : '2px solid transparent',
                  boxShadow: active ? '0 0 0 3px rgba(201, 162, 39, 0.15)' : 'none',
                  outline: active ? 'none' : '1px solid #e0d8cc',
                  textAlign: 'center', transition: 'all 0.15s',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                  <span style={{ fontSize: '1.8rem', lineHeight: 1 }}>{s.icon}</span>
                  <span style={{ fontSize: '0.8rem', fontFamily: "'Lora', serif", fontWeight: 500, color: active ? '#5C4D3C' : '#3c4653' }}>{s.label}</span>
                  <span style={{ fontSize: '0.7rem', fontFamily: "'Lora', serif", fontWeight: 400, color: active ? '#3c4653' : '#b0a89e' }}>{s.desc}</span>
                </button>
                );
              })}
            </div>
          </div>

          {/* Live 3D book preview — same as home page. Deliberately OUTSIDE
              the Cover Layout wrapper above (which is display:none for
              full-wrap designs) — it used to be nested inside that wrapper,
              which hid the ENTIRE 3D preview (not just the classic/framed
              picker) for floralwrap/burgwrap/mustardplum. Charcoal was the
              only design not in FULLWRAP_DESIGNS, so it was the only one
              that still showed a preview. See coverPreviewBranches.test.js
              for the regression guard. */}
          <div style={{ perspective: 800, maxWidth: '100%', display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
              {/* data-testid hooks (07-cover-preview-vs-print.spec.ts, PREVIEW-vs-PRINT
                  divergence gate): the e2e test neutralizes this element's `transform`
                  before measuring/screenshotting so text-line-count and
                  text-vs-florals pixel checks read a flat, un-skewed panel — the 3D
                  tilt is a paint-time effect only and does not change layout/wrapping,
                  so this is safe to strip for measurement without changing what's
                  being tested. */}
              <div data-testid="cover-preview-3d-rotator" style={{
                width: Math.min(300, window.innerWidth - 64), height: Math.min(388, (window.innerWidth - 64) * 1.293),
                position: 'relative',
                transformStyle: 'preserve-3d',
                transform: 'rotateY(-12deg) rotateX(2deg)',
              }}>
                {/* Spine face */}
                <div style={{
                  position: 'absolute', top: 0, left: 0, bottom: 0, width: 16,
                  background: 'linear-gradient(to right, #161310, #1e1a16)',
                  transform: 'rotateY(-90deg) translateX(-8px) translateZ(8px)',
                  backfaceVisibility: 'hidden',
                }} />
                {/* Page edges bottom */}
                <div style={{
                  position: 'absolute', bottom: -4, left: 8, right: 0, height: 4,
                  background: 'linear-gradient(to bottom, #e8e0d4, #d8cfc0)',
                  borderRadius: '0 0 2px 0',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                }} />
                {/* Front cover */}
                <div data-testid="cover-preview-panel" style={{
                  position: 'absolute', inset: 0,
                  borderRadius: '2px 8px 8px 2px',
                  overflow: 'hidden',
                  background: '#1e1a16',
                  backfaceVisibility: 'hidden',
                  boxShadow: '6px 4px 20px rgba(10,6,2,0.35), 12px 12px 40px rgba(10,6,2,0.2), -2px 0 8px rgba(10,6,2,0.1)',
                }}>
                  <div style={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'6\' height=\'6\' viewBox=\'0 0 6 6\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h1v1H0zM3 3h1v1H3z\' fill=\'%23fff\' fill-opacity=\'1\'/%3E%3C/svg%3E")', pointerEvents: 'none', zIndex: 3 }} />
                  {(() => {
                    const isBotanical = stBookCoverDesign !== 'charcoal';
                    const bgImage = stBookCoverDesign === 'burgundy' ? BurgundyCover : stBookCoverDesign === 'mothersday' ? MothersDayCover : stBookCoverDesign === 'linen' ? GoldLinenCover : stBookCoverDesign === 'charcoalgold' ? CharcoalGoldCover : stBookCoverDesign === 'floralwrap' ? FloralWrapCover : stBookCoverDesign === 'burgwrap' ? BurgWrapCover : stBookCoverDesign === 'slatechevron' ? SlateChevronCover : null;
                    const coverPhoto = stBookCoverPhotoBase64 || stBookCoverPhotoUrl || null;

                    const isFullWrapDesign = FULLWRAP_DESIGNS.includes(stBookCoverDesign);
                    // For full-wrap designs, preview the TRUE printed front panel
                    // (honest crop), not the wrap art with a background-position
                    // guess that leaks trimmed-off edge flowers into the preview.
                    const fullWrapFrontBg = stBookCoverDesign === 'floralwrap' ? FloralWrapFrontPanel
                      : stBookCoverDesign === 'burgwrap' ? BurgWrapFrontPanel : bgImage;

                    if (stBookCoverDesign === 'mustardplum') {
                      return renderFloralWrapPreview();
                    } else if (isBotanical && stBookCover === 'framed') {
                      const photoSlot = getFramedPhotoSlot(stBookCoverPhotoOrientation);
                      return (<>
                        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${isFullWrapDesign ? fullWrapFrontBg : bgImage})`, backgroundSize: 'cover', backgroundPosition: isFullWrapDesign ? 'center' : 'center bottom' }} />
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                          {/* marginLeft/Right mirrors FrontFramedBotanical PDF: 0.125" hinge shift toward fore-edge (COVER-001) */}
                          <div style={{ width: BOTANICAL_FRAMED_PREVIEW.panelWidthPct, padding: BOTANICAL_FRAMED_PREVIEW.panelPadding, background: 'rgba(253,246,238,0.88)', borderRadius: 10, border: '0.75px solid rgba(190,155,120,0.38)', textAlign: 'center', marginLeft: '1.47%', marginRight: '-1.47%' }}>
                            <div style={{ fontFamily: "'Lora', serif", fontSize: BOTANICAL_FRAMED_PREVIEW.titleFontRem, color: '#1e0e06', lineHeight: 1.2, marginBottom: 5 }}>{stBookTitle || 'Our Story'}</div>
                            <div style={{ width: '55%', height: 0.8, background: '#7c2d12', margin: '0 auto 6px' }} />
                            {coverPhoto ? (
                              <div style={{ width: BOTANICAL_FRAMED_PREVIEW.photoWidthPct, margin: '0 auto', aspectRatio: photoSlot.aspect, border: '1px solid #7c2d12', padding: 2, marginBottom: 6, overflow: 'hidden' }}>
                                <img loading="lazy" src={coverPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `center ${stBookCoverPhotoY}%`, display: 'block' }} />
                              </div>
                            ) : (
                              <div style={{ width: BOTANICAL_FRAMED_PREVIEW.photoWidthPct, margin: '0 auto', aspectRatio: photoSlot.aspect, border: '1px solid rgba(201,168,76,0.3)', padding: 2, marginBottom: 6 }} />
                            )}
                            <div style={{ width: '55%', height: 0.8, background: '#7c2d12', margin: '0 auto 5px' }} />
                            {stBookSubtitle && <div style={{ fontFamily: "'Lora', serif", fontSize: '0.7rem', color: '#5a3820', fontStyle: 'italic' }}>{stBookSubtitle}</div>}
                            {stBookAuthor && <div style={{ fontFamily: "'Lora', serif", fontSize: '0.55rem', color: '#6a4028', fontStyle: 'italic', marginTop: 2 }}>{stBookAuthor}</div>}
                            <div style={{ fontFamily: "'Lora', serif", fontSize: '0.45rem', color: '#7c2d12', fontStyle: 'italic', opacity: 0.7, marginTop: 2 }}>{new Date().getFullYear()}</div>
                          </div>
                        </div>
                      </>);
                    } else if (isBotanical) {
                      return (<>
                        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${isFullWrapDesign ? fullWrapFrontBg : bgImage})`, backgroundSize: 'cover', backgroundPosition: isFullWrapDesign ? 'center' : 'center bottom' }} />
                        <div style={{ position: 'absolute', top: 20, left: 20, width: 'fit-content', minWidth: 80, maxWidth: 140, padding: '12px 14px', background: 'rgba(250,245,237,0.92)', border: '0.75px solid rgba(190,155,120,0.38)', outline: '3px solid rgba(240,230,215,0.55)', outlineOffset: 3, zIndex: 1 }}>
                          <div style={{ fontFamily: "'Lora', serif", fontSize: '1.2rem', color: '#1e0e06', lineHeight: 1.15, marginBottom: 6 }}>{stBookTitle || 'Our Story'}</div>
                          <div style={{ width: 20, height: 0.5, background: 'rgba(160,110,75,0.45)', marginBottom: 5 }} />
                          {stBookSubtitle && <div style={{ fontFamily: "'Lora', serif", fontSize: '0.75rem', color: '#5a3820', fontStyle: 'italic', marginBottom: 2 }}>{stBookSubtitle}</div>}
                          {stBookAuthor && <div style={{ fontFamily: "'Lora', serif", fontSize: '0.6rem', color: '#6a4028', fontStyle: 'italic' }}>{stBookAuthor}</div>}
                        </div>
                      </>);
                    } else if (stBookCover === 'framed') {
                      const photoSlot = getFramedPhotoSlot(stBookCoverPhotoOrientation);
                      return (<>
                        {/* paddingLeft > paddingRight mirrors FrontFramedCharcoal PDF: 0.125" hinge shift toward fore-edge (COVER-001) */}
                        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 28, paddingBottom: 28, paddingLeft: 'calc(32px + 1.47%)', paddingRight: 'calc(32px - 1.47%)' }}>
                          <div style={{ fontFamily: "'Newsreader', serif", fontSize: '1rem', letterSpacing: 3, color: '#d4c8b0', marginBottom: 4 }}>Talebound</div>
                          <div style={{ width: 30, height: 0.5, background: 'rgba(200,189,168,0.2)', marginBottom: 6 }} />
                          <div style={{ fontFamily: "'Lora', serif", fontSize: '1.5rem', color: '#e8dcc8', textAlign: 'center', lineHeight: 1.2, marginBottom: 6 }}>{stBookTitle || 'Our Story'}</div>
                          <div style={{ width: 30, height: 0.5, background: 'rgba(200,189,168,0.2)', marginBottom: 10 }} />
                          {coverPhoto ? (
                            <div style={{ width: '62%', aspectRatio: photoSlot.aspect, border: '1.5px solid rgba(200,189,168,0.25)', padding: 3, marginBottom: 10, overflow: 'hidden' }}>
                              <img loading="lazy" src={coverPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `center ${stBookCoverPhotoY}%`, display: 'block' }} />
                            </div>
                          ) : (
                            <div style={{ width: '62%', aspectRatio: photoSlot.aspect, border: '1.5px solid rgba(200,189,168,0.15)', padding: 3, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontSize: '1.05rem', color: 'rgba(200,189,168,0.2)', fontStyle: 'italic' }}>Add a photo</span>
                            </div>
                          )}
                          {stBookSubtitle && <div style={{ fontFamily: "'Lora', serif", fontSize: '0.7rem', color: 'rgba(200,189,168,0.55)', fontStyle: 'italic', textAlign: 'center' }}>{stBookSubtitle}</div>}
                          {stBookAuthor && <div style={{ fontFamily: "'Lora', serif", fontSize: '0.65rem', color: 'rgba(200,189,168,0.4)', fontStyle: 'italic', marginTop: 3 }}>{stBookAuthor}</div>}
                        </div>
                      </>);
                    } else {
                      return (<>
                        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '36px 32px' }}>
                          <div style={{ width: 50, height: 1, background: 'rgba(200,189,168,0.15)', marginBottom: 20 }} />
                          <div style={{ fontFamily: "'Newsreader', serif", fontSize: '1rem', letterSpacing: 4, color: '#d4c8b0', marginBottom: 16 }}>Talebound</div>
                          <div style={{ fontFamily: "'Lora', serif", fontSize: '2rem', color: '#d4c8b0', fontWeight: 500, lineHeight: 1.2, marginBottom: 10, textAlign: 'center' }}>{stBookTitle || 'Our Story'}</div>
                          <div style={{ width: 35, height: 1, background: 'rgba(200,189,168,0.2)', marginBottom: 10 }} />
                          {stBookSubtitle && <div style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: 'rgba(200,189,168,0.55)', fontStyle: 'italic', lineHeight: 1.4, textAlign: 'center' }}>{stBookSubtitle}</div>}
                          {stBookAuthor && <div style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: 'rgba(200,189,168,0.4)', marginTop: 8, textAlign: 'center' }}>{stBookAuthor}</div>}
                          <div style={{ width: 50, height: 1, background: 'rgba(200,189,168,0.15)', marginTop: 20 }} />
                        </div>
                      </>);
                    }
                  })()}
                </div>
              </div>
          </div>

          {/* Cover Design (color/background) */}
          <div style={{ marginBottom: 24 }}>
            <label className="st-label">Cover Design</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 10 }}>
              {[
                { id: 'charcoal', label: 'Charcoal', bg: '#2a2420', textColor: '#e8dcc8' },
                { id: 'burgundy', label: 'Burgundy', image: BurgundyCover },
                { id: 'mothersday', label: 'Floral', image: MothersDayCover },
                { id: 'linen', label: 'Linen', image: GoldLinenCover },
                { id: 'charcoalgold', label: 'Charcoal Gold', image: CharcoalGoldCover },
                { id: 'floralwrap', label: 'Floral Wrap', image: FloralWrapCover },
                { id: 'burgwrap', label: 'Burgundy', image: BurgWrapCover },
                { id: 'mustardplum', label: 'Plum Blossom', image: MustardPlumCover },
                { id: 'slatechevron', label: 'Slate Chevron', image: SlateChevronCover },
              ].filter(d => LAUNCH_COVER_DESIGNS.includes(d.id)).map(design => (
                <button key={design.id} onClick={() => setStBookCoverDesign(design.id)}
                  style={{
                    padding: 0, background: stBookCoverDesign === design.id ? 'rgba(15,23,42,0.06)' : '#fff',
                    border: stBookCoverDesign === design.id ? '2.5px solid #7c2d12' : '1.5px solid #e0d5c5',
                    borderRadius: 10, cursor: 'pointer', overflow: 'hidden',
                    transition: 'all 0.15s',
                  }}>
                  <div style={{ width: '100%', aspectRatio: '1', position: 'relative', overflow: 'hidden' }}>
                    {design.image ? (
                      <img src={design.image} alt="" loading="lazy" decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ position: 'absolute', inset: 0, background: design.bg }} />
                    )}
                  </div>
                  <div style={{ padding: '6px 4px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: stBookCoverDesign === design.id ? '#0f172a' : '#1a1612', fontFamily: "'Lora', serif" }}>{design.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Cover Photo */}
          <div style={{ marginBottom: 28 }}>
              <label className="st-label">Cover Photo</label>
              {stBookCover !== 'framed' && (
                <p style={{ fontSize: '0.95rem', color: '#3c4653', marginBottom: 8, fontStyle: 'italic' }}>Select the "Framed" layout above to add a photo to your cover.</p>
              )}
              {stBookCover === 'framed' && (<>
              {stBookCoverPhotoUrl || stBookCoverPhotoBase64 || stBookCoverPhotoPath ? (
                <div style={{ marginBottom: 16 }}>
                  {/* Drag-to-reposition preview */}
                  <div style={{ position: 'relative', width: '100%', maxWidth: 320, marginBottom: 8 }}>
                    <div
                      style={{
                        width: '100%', aspectRatio: stBookCoverDesign === 'mustardplum' ? (FLORAL_WRAP_PREVIEW.photoAspect[stBookCoverPhotoOrientation] || FLORAL_WRAP_PREVIEW.photoAspect.landscape) : getFramedPhotoSlot(stBookCoverPhotoOrientation).aspect, overflow: 'hidden', borderRadius: 8,
                        border: '1.5px solid rgba(15,23,42,0.15)', cursor: 'grab', position: 'relative',
                        userSelect: 'none', touchAction: 'none',
                      }}
                      onPointerDown={(e) => {
                        e.currentTarget.setPointerCapture(e.pointerId);
                        e.currentTarget.dataset.dragging = 'true';
                        e.currentTarget.dataset.startY = String(e.clientY);
                        e.currentTarget.dataset.startVal = String(stBookCoverPhotoY);
                        e.currentTarget.style.cursor = 'grabbing';
                      }}
                      onPointerMove={(e) => {
                        if (e.currentTarget.dataset.dragging !== 'true') return;
                        const startY = parseFloat(e.currentTarget.dataset.startY);
                        const startVal = parseFloat(e.currentTarget.dataset.startVal);
                        const frameH = e.currentTarget.getBoundingClientRect().height;
                        const delta = ((e.clientY - startY) / frameH) * -100;
                        const newVal = Math.max(0, Math.min(100, startVal + delta));
                        setStBookCoverPhotoY(Math.round(newVal));
                      }}
                      onPointerUp={(e) => {
                        if (e.currentTarget.dataset.dragging === 'true') {
                          const startY = parseFloat(e.currentTarget.dataset.startY);
                          const startVal = parseFloat(e.currentTarget.dataset.startVal);
                          const frameH = e.currentTarget.getBoundingClientRect().height;
                          const delta = ((e.clientY - startY) / frameH) * -100;
                          const finalVal = Math.round(Math.max(0, Math.min(100, startVal + delta)));
                          saveBookData({ coverPhotoY: finalVal });
                        }
                        e.currentTarget.dataset.dragging = 'false';
                        e.currentTarget.style.cursor = 'grab';
                      }}
                      onPointerCancel={(e) => {
                        e.currentTarget.dataset.dragging = 'false';
                        e.currentTarget.style.cursor = 'grab';
                      }}
                    >
                      {stBookCoverPhotoBase64 || stBookCoverPhotoUrl ? (
                        <img loading="lazy" src={coverPhotoDisplaySrc || stBookCoverPhotoBase64 || stBookCoverPhotoUrl} alt="Cover"
                          draggable={false}
                          onError={async (e) => {
                            if (stBookCoverPhotoPath && !e.target.dataset.retried) {
                              e.target.dataset.retried = 'true';
                              try {
                                const fileName = stBookCoverPhotoPath.split('/').pop();
                                const pathFn = ({ identityId }) => `private/${identityId}/photos/${fileName}`;
                                const result = await getUrl({ path: pathFn });
                                if (result?.url) setStBookCoverPhotoUrl(result.url.toString());
                              } catch (err) { console.warn('[Cover Photo] Re-resolve failed:', err); }
                            }
                          }}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `center ${stBookCoverPhotoY}%`, display: 'block', pointerEvents: 'none' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.05)' }}>
                          <span style={{ color: '#3c4653', fontSize: '0.9rem' }}>Loading photo...</span>
                        </div>
                      )}
                    </div>
                    <button onClick={() => {
                      setStBookCoverPhotoPath('');
                      setStBookCoverPhotoUrl('');
                      setStBookCoverPhotoBase64('');
                      setStBookCoverPhotoY(50);
                      saveBookData({ coverPhotoPath: '', coverPhotoY: 50 });
                    }}
                      style={{
                        position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.6)',
                        color: '#fff', border: 'none', borderRadius: 20, width: 26, height: 26,
                        cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>✕</button>
                  </div>
                  <p style={{ fontSize: '0.88rem', color: '#3c4653', fontStyle: 'italic' }}>Drag to reposition your photo</p>
                </div>
              ) : (
                <p style={{ fontSize: '1.05rem', color: '#3c4653', marginBottom: 12 }}>Choose a favorite family photo for the cover of your book.</p>
              )}

              {/* Orientation toggle — Portrait / Landscape */}
              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: '0.92rem', color: '#3c4653', marginBottom: 8 }}>Photo frame shape</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { value: 'portrait', label: 'Portrait', hint: 'Best for vertical photos' },
                    { value: 'landscape', label: 'Landscape', hint: 'Best for wide shots' },
                  ].map(opt => (
                    <button key={opt.value} onClick={() => {
                      setStBookCoverPhotoOrientation(opt.value);
                      saveBookData({ coverPhotoOrientation: opt.value });
                    }}
                      title={opt.hint}
                      style={{
                        padding: '8px 16px',
                        background: stBookCoverPhotoOrientation === opt.value ? '#0f172a' : '#fff',
                        color: stBookCoverPhotoOrientation === opt.value ? '#fbf8f1' : '#1a1612',
                        border: stBookCoverPhotoOrientation === opt.value ? '2px solid #0f172a' : '1.5px solid #d4c9b8',
                        borderRadius: 9999, cursor: 'pointer', fontSize: '0.92rem', fontWeight: 600,
                        fontFamily: "'Nunito', sans-serif", transition: 'all 0.15s',
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: '0.8rem', color: '#7c6e5e', marginTop: 5, fontStyle: 'italic' }}>
                  {stBookCoverPhotoOrientation === 'portrait' ? 'Best for vertical (portrait) photos — people, individuals' : 'Best for wide (landscape) photos — scenery, groups'}
                </p>
              </div>

              {/* Photo color (tone) toggle — floral wrap only, mirrors RemnantCoverPDF mat treatment */}
              {stBookCoverDesign === 'mustardplum' && stBookCover === 'framed' && (stBookCoverPhotoBase64 || stBookCoverPhotoUrl || stBookCoverPhotoPath) && (
                <div style={{ marginBottom: 14 }}>
                  <p style={{ fontSize: '0.92rem', color: '#3c4653', marginBottom: 8 }}>Photo color</p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[
                      { value: 'color', label: 'Color' },
                      { value: 'sepia', label: 'Sepia' },
                      { value: 'bw', label: 'B&W' },
                    ].map(opt => (
                      <button key={opt.value} onClick={() => {
                        setStBookCoverPhotoTone(opt.value);
                        saveBookData({ coverPhotoTone: opt.value });
                      }}
                        style={{
                          padding: '8px 16px',
                          background: stBookCoverPhotoTone === opt.value ? '#0f172a' : '#fff',
                          color: stBookCoverPhotoTone === opt.value ? '#fbf8f1' : '#1a1612',
                          border: stBookCoverPhotoTone === opt.value ? '2px solid #0f172a' : '1.5px solid #d4c9b8',
                          borderRadius: 9999, cursor: 'pointer', fontSize: '0.92rem', fontWeight: 600,
                          fontFamily: "'Nunito', sans-serif", transition: 'all 0.15s',
                        }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p style={{ fontSize: '0.8rem', color: '#7c6e5e', marginTop: 5, fontStyle: 'italic' }}>
                    Sepia blends beautifully with the floral art. Color keeps your photo natural.
                  </p>
                </div>
              )}

              {/* Pick from existing book photos */}
              {stBookPages.some(p => p.photoBase64 || p.photoUrl) && (
                <div style={{ marginBottom: 14 }}>
                  <p style={{ fontSize: '1rem', color: '#3c4653', marginBottom: 8 }}>Use a photo from your book:</p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {stBookPages.filter(p => p.photoBase64 || p.photoUrl).map((p, i) => (
                      <button key={i} onClick={() => {
                        setStBookCoverPhotoBase64(p.photoBase64 || '');
                        setStBookCoverPhotoUrl(p.photoUrl || '');
                        setStBookCoverPhotoPath(p.photoPath || '');
                        setStBookCoverPhotoY(50);
                        saveBookData({ coverPhotoPath: p.photoPath || '', coverPhotoY: 50 });
                      }}
                        style={{
                          width: 60, height: 60, padding: 0,
                          border: (p.photoPath && p.photoPath === stBookCoverPhotoPath) ? '2.5px solid #7c2d12' : '1.5px solid #d4c9b8',
                          borderRadius: 8, overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
                        }}>
                        <img loading="lazy" src={p.photoBase64 || p.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload new photo — primary CTA on this page */}
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 26px',
                background: '#0f172a', color: '#fbf8f1', border: 'none',
                borderRadius: 9999, cursor: 'pointer', fontSize: '1rem', fontWeight: 600,
                fontFamily: "'Nunito', sans-serif",
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)', transition: 'background 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#1e2d45'}
                onMouseLeave={e => e.currentTarget.style.background = '#0f172a'}>
                <PhCamera size={18} weight="duotone" /> Upload New Photo
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 10 * 1024 * 1024) { showToast('Image must be under 10 MB.', 'error'); return; }
                  try {
                    const normalizedFile = await normalizeImageForBook(file);
                    const ext = normalizedFile.name.split('.').pop() || 'jpg';
                    const fileName = `cover_photo_${Date.now()}.${ext}`;
                    const pathBuilder = ({ identityId }) => `private/${identityId}/photos/${fileName}`;
                    const uploadResult = await (uploadData({ path: pathBuilder, data: normalizedFile, options: { contentType: normalizedFile.type, cacheControl: 'public, max-age=31536000, immutable' } })).result;
                    const storagePath = uploadResult?.path;
                    if (!storagePath) throw new Error('Upload failed');
                    const urlResult = await getUrl({ path: storagePath });
                    const signedUrl = urlResult?.url?.toString() || URL.createObjectURL(normalizedFile);
                    setStBookCoverPhotoPath(storagePath);
                    setStBookCoverPhotoUrl(signedUrl);
                    setStBookCoverPhotoY(50);
                    const reader = new FileReader();
                    reader.onload = (readerEvent) => {
                      const base64 = readerEvent.target.result;
                      setStBookCoverPhotoBase64(base64);
                      // Auto-detect orientation from natural image dimensions
                      const img = new window.Image();
                      img.onload = () => {
                        const detectedOrientation = img.naturalHeight > img.naturalWidth ? 'portrait' : 'landscape';
                        setStBookCoverPhotoOrientation(detectedOrientation);
                        saveBookData({ coverPhotoPath: storagePath, coverPhotoY: 50, coverPhotoOrientation: detectedOrientation });
                      };
                      img.onerror = () => {
                        // Fallback: save without orientation change
                        saveBookData({ coverPhotoPath: storagePath, coverPhotoY: 50 });
                      };
                      img.src = base64;
                    };
                    reader.readAsDataURL(normalizedFile);
                    showToast('Cover photo uploaded!');
                  } catch (err) { showToast('Something went wrong uploading your photo. Please try again.', 'error'); }
                }} />
              </label>
          </>)}
          </div>

          {/* Debug cover preview — hidden behind ?dev flag */}
          {new URLSearchParams(window.location.search).has('dev') && (
            <>
            <button onClick={async () => {
              try {
                showToast('Generating debug cover preview...');
                // TB-102 Phase 2: generate the interior first so we can read the real
                // rendered page count. This keeps the debug tool honest — it shows the
                // cover at the same spine the actual print file would use.
                const est = stBookEstimate(resolvablePageCount(stBookPages, stories));
                // Build pdfChapters locally — this handler is in the render scope,
                // not inside the doCheckout closure where the shared pdfChapters lives.
                const debugPdfChapters = stBookPages.map(page => {
                  const story = stories.find(s => s.id === page.storyId);
                  if (!story) return null;
                  const contribRelationship = page.contributorName && activeBook?.contributors
                    ? (activeBook.contributors.find(c => c.name && page.contributorName.toLowerCase().includes(c.name.toLowerCase()))?.relationship || '')
                    : '';
                  const rawContent = story.content || '';
                  const content = page.contributorName ? cleanContributionContent(rawContent) : rawContent;
                  return {
                    title: promptToChapterTitle(story.title) || 'Untitled', content,
                    photoUrl: page.photoBase64 || page.photoUrl || null, caption: page.caption || '',
                    layout: page.layout || 'classic',
                    photoFocusX: page.photoFocusX ?? 50, photoFocusY: page.photoFocusY ?? 50,
                    photoOrientation: page.photoOrientation || 'landscape', subtitle: '',
                    contributorName: page.contributorName || '',
                    contributorRelationship: contribRelationship,
                    isKidStory: page.isKidStory || false,
                    qrDataUrl: (page.shareId && page.qrEnabled !== false) ? chapterQrCodes[page.storyId] : null,
                    mediaType: page.mediaType,
                  };
                }).filter(Boolean);
                const debugInteriorProps = {
                  title: stBookTitle || 'Our Story', author: stBookAuthor, familyName: stBookAuthor || '',
                  dedication: stBookDedication || '', isCaretaker: isKeeper,
                  lovedOneName: legacySubjectName, chapters: debugPdfChapters,
                  occasion: isGiftBook ? activeBook?.occasion : null,
                  recipientName: isGiftBook ? (activeBook?.recipientName || '') : '',
                  contributorCount: isGiftBook ? (activeBook?.contributors?.length || 0) : 0,
                };
                let debugRealPages = est.luluPages;
                try {
                  let debugInteriorBlob = await pdf(<RemnantBookPDF {...debugInteriorProps} />).toBlob();
                  const dbuf = await debugInteriorBlob.arrayBuffer();
                  const dpdfDoc = await pdfjs.getDocument({ data: dbuf }).promise;
                  debugRealPages = dpdfDoc.numPages;
                  if (debugRealPages % 2 !== 0) {
                    debugInteriorBlob = await pdf(<RemnantBookPDF {...debugInteriorProps} forceEvenPages={true} />).toBlob();
                    const dbuf2 = await debugInteriorBlob.arrayBuffer();
                    const dpdfDoc2 = await pdfjs.getDocument({ data: dbuf2 }).promise;
                    debugRealPages = dpdfDoc2.numPages;
                  }
                  debugRealPages = Math.max(debugRealPages, 24);
                  console.log('[TB-102 debug] Interior pages:', debugRealPages, '(estimate was', est.luluPages, ')');
                } catch (e) {
                  console.warn('[TB-102 debug] pdf.js read failed — using estimate:', e);
                  debugRealPages = est.luluPages;
                }
                let debugCoverPhoto = await resolveCoverPhotoBase64() || stBookCoverPhotoBase64 || stBookCoverPhotoUrl || null;
                if (debugCoverPhoto && stBookCoverDesign === 'mustardplum' && stBookCoverPhotoTone && stBookCoverPhotoTone !== 'color') {
                  try { debugCoverPhoto = await applyPhotoTone(debugCoverPhoto, stBookCoverPhotoTone); } catch (e) { console.warn('[Cover Photo] debug tone bake failed:', e); }
                }
                // Compress cover photo for PDF renderer (large base64 causes silent failures)
                if (debugCoverPhoto && debugCoverPhoto.length > 500000) {
                  try {
                    const img = new window.Image();
                    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = debugCoverPhoto; });
                    const canvas = document.createElement('canvas');
                    const maxW = 1200;
                    const scale = Math.min(1, maxW / img.naturalWidth);
                    canvas.width = Math.round(img.naturalWidth * scale);
                    canvas.height = Math.round(img.naturalHeight * scale);
                    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
                    debugCoverPhoto = canvas.toDataURL('image/jpeg', 0.85);
                  } catch (e) { console.warn('[Cover Photo] Compress failed:', e); }
                }
                const debugDoc = (
                  <RemnantCoverPDF title={stBookTitle || 'Our Story'} subtitle={stBookSubtitle} author={stBookAuthor}
                    pageCount={debugRealPages} coverStyle={stBookCover} coverDesign={stBookCoverDesign}
                    coverPhotoUrl={debugCoverPhoto}
                    coverPhotoY={stBookCoverPhotoY}
                    coverPhotoOrientation={stBookCoverPhotoOrientation}
                    botanicalImageUrl={['burgundy','mothersday','linen','charcoalgold'].includes(stBookCoverDesign) ? COVER_FULL_RES[stBookCoverDesign] : null}
                    fullWrapImageUrl={FULLWRAP_DESIGNS.includes(stBookCoverDesign) ? COVER_FULL_RES[stBookCoverDesign] : null}
                    debug={true} />
                );
                const blob = await pdf(debugDoc).toBlob();
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
              } catch (err) { showToast('Something went wrong loading the cover preview. Please try again.', 'error'); }
            }} className="st-btn-secondary" style={{ width: '100%', marginBottom: 12, fontSize: '0.92rem', opacity: 0.7 }}>
              Debug: Preview Cover PDF with Guide Lines
            </button>

            {/* Debug: generate interior + cover and download both as files (for manual S3 upload) */}
            <button onClick={async () => {
              try {
                showToast('Generating interior + cover PDFs for download...');
                const est = stBookEstimate(resolvablePageCount(stBookPages, stories));
                const debugPdfChapters2 = stBookPages.map(page => {
                  const story = stories.find(s => s.id === page.storyId);
                  if (!story) return null;
                  const contribRelationship = page.contributorName && activeBook?.contributors
                    ? (activeBook.contributors.find(c => c.name && page.contributorName.toLowerCase().includes(c.name.toLowerCase()))?.relationship || '')
                    : '';
                  const rawContent = story.content || '';
                  const content = page.contributorName ? cleanContributionContent(rawContent) : rawContent;
                  return {
                    title: promptToChapterTitle(story.title) || 'Untitled', content,
                    photoUrl: page.photoBase64 || page.photoUrl || null, caption: page.caption || '',
                    layout: page.layout || 'classic',
                    photoFocusX: page.photoFocusX ?? 50, photoFocusY: page.photoFocusY ?? 50,
                    photoOrientation: page.photoOrientation || 'landscape', subtitle: '',
                    contributorName: page.contributorName || '',
                    contributorRelationship: contribRelationship,
                    isKidStory: page.isKidStory || false,
                    qrDataUrl: (page.shareId && page.qrEnabled !== false) ? chapterQrCodes[page.storyId] : null,
                    mediaType: page.mediaType,
                  };
                }).filter(Boolean);
                const debugInteriorProps2 = {
                  title: stBookTitle || 'Our Story', author: stBookAuthor, familyName: stBookAuthor || '',
                  dedication: stBookDedication || '', isCaretaker: isKeeper,
                  lovedOneName: legacySubjectName, chapters: debugPdfChapters2,
                  occasion: null, recipientName: '', contributorCount: 0,
                };
                let debugInteriorBlob2 = await pdf(<RemnantBookPDF {...debugInteriorProps2} />).toBlob();
                let dbRealPages = est.luluPages;
                try {
                  const dbuf2 = await debugInteriorBlob2.arrayBuffer();
                  const dpdfDoc2 = await pdfjs.getDocument({ data: dbuf2 }).promise;
                  dbRealPages = dpdfDoc2.numPages;
                  if (dbRealPages % 2 !== 0) {
                    debugInteriorBlob2 = await pdf(<RemnantBookPDF {...debugInteriorProps2} forceEvenPages={true} />).toBlob();
                    const dbuf3 = await debugInteriorBlob2.arrayBuffer();
                    const dpdfDoc3 = await pdfjs.getDocument({ data: dbuf3 }).promise;
                    dbRealPages = dpdfDoc3.numPages;
                  }
                  dbRealPages = Math.max(dbRealPages, 24);
                  console.log('[debug-download] Interior pages:', dbRealPages);
                } catch (e) { console.warn('[debug-download] pdf.js failed, using estimate:', e); }
                // Download interior
                const interiorUrl2 = URL.createObjectURL(debugInteriorBlob2);
                const a1 = document.createElement('a'); a1.href = interiorUrl2; a1.download = 'interior.pdf'; a1.click();
                // Generate + download cover
                let dbCoverPhoto2 = await resolveCoverPhotoBase64() || stBookCoverPhotoBase64 || stBookCoverPhotoUrl || null;
                if (dbCoverPhoto2 && stBookCoverDesign === 'mustardplum' && stBookCoverPhotoTone && stBookCoverPhotoTone !== 'color') {
                  try { dbCoverPhoto2 = await applyPhotoTone(dbCoverPhoto2, stBookCoverPhotoTone); } catch (e) { console.warn('[Cover Photo] db tone bake failed:', e); }
                }
                if (dbCoverPhoto2 && dbCoverPhoto2.length > 500000) {
                  try {
                    const img2 = new window.Image();
                    await new Promise((res2, rej2) => { img2.onload = res2; img2.onerror = rej2; img2.src = dbCoverPhoto2; });
                    const canvas2 = document.createElement('canvas');
                    const maxW2 = 1200; const scale2 = Math.min(1, maxW2 / img2.naturalWidth);
                    canvas2.width = Math.round(img2.naturalWidth * scale2);
                    canvas2.height = Math.round(img2.naturalHeight * scale2);
                    canvas2.getContext('2d').drawImage(img2, 0, 0, canvas2.width, canvas2.height);
                    dbCoverPhoto2 = canvas2.toDataURL('image/jpeg', 0.85);
                  } catch (e2) { console.warn('[debug-download] Cover photo compress failed:', e2); }
                }
                const debugCoverDoc2 = (
                  <RemnantCoverPDF title={stBookTitle || 'Our Story'} subtitle={stBookSubtitle} author={stBookAuthor}
                    pageCount={dbRealPages} coverStyle={stBookCover} coverDesign={stBookCoverDesign}
                    coverPhotoUrl={dbCoverPhoto2} coverPhotoY={stBookCoverPhotoY}
                    coverPhotoOrientation={stBookCoverPhotoOrientation}
                    botanicalImageUrl={['burgundy','mothersday','linen','charcoalgold'].includes(stBookCoverDesign) ? COVER_FULL_RES[stBookCoverDesign] : null}
                    fullWrapImageUrl={FULLWRAP_DESIGNS.includes(stBookCoverDesign) ? COVER_FULL_RES[stBookCoverDesign] : null} />
                );
                const coverBlob2 = await pdf(debugCoverDoc2).toBlob();
                const coverUrl2 = URL.createObjectURL(coverBlob2);
                setTimeout(() => {
                  const a2 = document.createElement('a'); a2.href = coverUrl2; a2.download = 'cover.pdf'; a2.click();
                }, 500);
                showToast(`Downloaded interior.pdf (${dbRealPages} pages) + cover.pdf. Upload to S3 then run lulu-resend-premium.js.`, 'success');
              } catch (err) {
                console.error('[debug-download] Error:', err);
                showToast('PDF generation failed. See console.', 'error');
              }
            }} className="st-btn-secondary" style={{ width: '100%', marginBottom: 12, fontSize: '0.92rem', opacity: 0.7 }}>
              Debug: Download interior.pdf + cover.pdf (for S3 upload)
            </button>
            </>
          )}

          {/* Save & back */}
          <div className="sticky-save-bar">
            <button onClick={async () => {
              const bookData = { title: stBookTitle, subtitle: stBookSubtitle, author: stBookAuthor, dedication: stBookDedication, coverStyle: stBookCover, coverDesign: stBookCoverDesign, coverPhotoPath: stBookCoverPhotoPath, coverPhotoY: stBookCoverPhotoY, pages: cleanPagesForSave(stBookPages) };
              saveBookData(bookData);
              showToast('Book details saved!');
              setStBookStep(0); window.scrollTo(0, 0);
            }} className="st-btn-primary" style={{ width: '100%' }}>✓ Save & Back to My Book</button>
          </div>
        </div>
      );
    }

    // ── Step 1: Edit single page (photo + caption + story text) ──
    if (stBookStep === 1 && stBookEditIdx !== null && stBookPages[stBookEditIdx]) {
      const page = stBookPages[stBookEditIdx];
      const story = stories.find(s => s.id === page.storyId);
      if (!story) { setStBookStep(0); return null; }
      const wordCount = story.content ? story.content.split(/\s+/).length : 0;
      // Line-based page estimate matching PDF engine (CHARS_PER_LINE=61, LINES_PER_PAGE=32)
      const textLines = (story.content || '').split('\n').reduce((acc, para) => {
        if (!para.trim()) return acc + 1;
        return acc + Math.max(1, Math.ceil(para.length / 56)) + 1; // +1 for para break
      }, 0);
      const estPages = Math.max(1, Math.ceil(textLines / 32));
      const isShort = textLines < 6;

      return (<>
        <div className="chapter-editor-shell" data-journey-step="book-1">
          <input ref={stBookPhotoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleStBookPhoto} />

          {/* ═══ SCROLLABLE EDITOR COLUMN ═══ */}
          <div className="chapter-editor-scroll">

          {/* ═══ CHAPTER NAV BAR ═══ */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: '#fbf8f1', padding: '12px 24px', borderBottom: '1px solid rgba(15,23,42,0.08)', flexShrink: 0, position: 'sticky', top: 0, zIndex: 10 }}>
            <button onClick={async () => {
              const bookData = { title: stBookTitle, subtitle: stBookSubtitle, author: stBookAuthor, dedication: stBookDedication, coverStyle: stBookCover, coverDesign: stBookCoverDesign, coverPhotoPath: stBookCoverPhotoPath, coverPhotoY: stBookCoverPhotoY, pages: cleanPagesForSave(stBookPages) };
              saveBookData(bookData);
              setStBookStep(0); setStBookEditIdx(null); window.scrollTo(0, 0);
            }} className="st-back" style={{ marginBottom: 0 }}>← My Stories</button>
            <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(15,23,42,0.6)' }}>Chapter {stBookPages.slice(0, stBookEditIdx + 1).filter(p => stories.some(s => s.id === p.storyId)).length}</span>
            <button onClick={async () => {
              const bookData = { title: stBookTitle, subtitle: stBookSubtitle, author: stBookAuthor, dedication: stBookDedication, coverStyle: stBookCover, coverDesign: stBookCoverDesign, coverPhotoPath: stBookCoverPhotoPath, coverPhotoY: stBookCoverPhotoY, pages: cleanPagesForSave(stBookPages) };
              saveBookData(bookData);
              try { await client.graphql({ query: updateLegacyEntry, variables: { input: { id: story.id, title: story.title, content: story.content } } }); } catch (e) {}
              showToast('Saved!');
            }} style={{ padding: '6px 20px', fontSize: 14, fontWeight: 500, background: '#0f172a', color: '#fbf8f1', border: 'none', borderRadius: 9999, cursor: 'pointer', transition: 'background 0.15s' }} onMouseEnter={e => e.target.style.background='#1e2d45'} onMouseLeave={e => e.target.style.background='#0f172a'}>Save</button>
          </div>

          {/* ═══ CONTENT ═══ */}
          <div className="chapter-editor-inner">

          {/* Preview button — shown below 1200px only */}
          <button className="chapter-preview-btn" onClick={() => setPreviewModalOpen(true)}
            style={{ alignItems: 'center', gap: 8, background: '#efebe0', color: '#0f172a', border: '1px solid rgba(15,23,42,0.15)', borderRadius: 9999, padding: '8px 18px', fontSize: 14, fontWeight: 500, cursor: 'pointer', marginBottom: 20, transition: 'background 0.15s' }}>
            <PhBookOpen size={16} weight="duotone" /> Preview page
          </button>

          {/* Chapter eyebrow + title + amber rule */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.15em', color: '#7c2d12', marginBottom: 12 }}>Chapter {stBookPages.slice(0, stBookEditIdx + 1).filter(p => stories.some(s => s.id === p.storyId)).length}</div>
            <input value={story.title} placeholder="Give this chapter a title..." onChange={async (e) => {
              const newTitle = e.target.value;
              setStories(prev => prev.map(s => s.id === story.id ? { ...s, title: newTitle } : s));
            }} onBlur={async () => {
              try { await client.graphql({ query: updateLegacyEntry, variables: { input: { id: story.id, title: story.title } } }); } catch (e) { console.error('Title save error:', e); }
            }} style={{ width: '100%', fontFamily: "'Lora', serif", fontSize: 40, fontWeight: 500, color: '#0f172a', background: 'transparent', border: 'none', borderBottom: '2px solid rgba(15,23,42,0.10)', paddingBottom: 12, outline: 'none' }} />
            <div style={{ width: 64, height: 2, background: '#d97706', marginTop: 4 }} />
          </div>

          {/* ═══ LAYOUT PICKER ═══ */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 10, textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.1em', color: '#3c4653', display: 'block', marginBottom: 10 }}>Page layout</label>
            <div style={{ display: 'flex', flexDirection: 'row', overflowX: 'auto', gap: 10, paddingBottom: 4 }}>
              {[
                { id: 'photo-spread', label: 'Photo Page',  description: 'Photo fills the page. Words appear at the bottom.', icon: <PhImage size={24} weight="duotone" /> },
                { id: 'classic',      label: 'Title Page',  description: 'Photo gets its own page. Story starts on the next.', icon: <PhFileText size={24} weight="duotone" /> },
                { id: 'story-page',   label: 'Story Page',  description: 'Photo at top, story right below. One page.', icon: <PhLayout size={24} weight="duotone" /> },
                { id: 'text-only',    label: 'Text Only',   description: 'Just words, no photo.', icon: <PhAlignLeft size={24} weight="duotone" /> },
              ].map(layout => {
                const current = page.layout || 'photo-spread';
                const active = current === layout.id;
                return (
                  <button key={layout.id} onClick={async () => {
                    const updated = [...stBookPages];
                    updated[stBookEditIdx] = { ...updated[stBookEditIdx], layout: layout.id };
                    setStBookPages(updated);
                    const photoUrl = updated[stBookEditIdx].photoUrl || updated[stBookEditIdx].photoBase64;
                    if (photoUrl && layout.id !== 'text-only') {
                      try {
                        const resp = await fetch(photoUrl);
                        const blob = await resp.blob();
                        const file = new File([blob], 'check.jpg', { type: blob.type });
                        const quality = await checkPhotoQuality(file, layout.id);
                        setPhotoQualityWarning(quality.level !== 'good' ? quality : null);
                      } catch { setPhotoQualityWarning(null); }
                    } else { setPhotoQualityWarning(null); }
                  }} style={{
                    flex: 1, minWidth: 110, padding: '14px 10px', borderRadius: 12, cursor: 'pointer',
                    background: active ? '#efebe0' : 'rgba(255,255,255,0.50)',
                    border: active ? '2px solid #d97706' : '2px solid rgba(15,23,42,0.10)',
                    textAlign: 'center', transition: 'all 0.15s',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: 6,
                  }}>
                    <span style={{ color: active ? '#7c2d12' : 'rgba(15,23,42,0.6)' }}>{layout.icon}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: active ? '#0f172a' : 'rgba(15,23,42,0.85)' }}>{layout.label}</span>
                    <span style={{ fontSize: 10, lineHeight: 1.3, color: active ? 'rgba(15,23,42,0.65)' : 'rgba(15,23,42,0.50)' }}>{layout.description}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Text-only hint */}
          {(page.layout) === 'text-only' && (
            <p style={{ fontSize: '0.88rem', color: '#3c4653', marginBottom: 20, fontStyle: 'italic' }}>
              Switch to a different layout above to add a photo.
            </p>
          )}

          {/* Portrait / Landscape toggle — Classic layout only */}
          {page.layout === 'classic' && (page.photoUrl || page.photoBase64 || page.photoPath) && (
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 10, textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.1em', color: '#3c4653', display: 'block', marginBottom: 10 }}>Photo orientation</label>
              <div style={{ display: 'flex', background: 'rgba(255,255,255,0.50)', border: '1px solid rgba(15,23,42,0.12)', borderRadius: 12, overflow: 'hidden' }}>
                {[
                  { id: 'landscape', label: 'Landscape' },
                  { id: 'portrait', label: 'Portrait' },
                ].map(opt => {
                  const active = (page.photoOrientation || 'landscape') === opt.id;
                  return (
                    <button key={opt.id} onClick={() => {
                      const updated = [...stBookPages];
                      updated[stBookEditIdx] = { ...updated[stBookEditIdx], photoOrientation: opt.id };
                      setStBookPages(updated);
                      saveBookData({ pages: cleanPagesForSave(updated) });
                    }} style={{
                      flex: 1, padding: '10px 0', cursor: 'pointer', border: 'none',
                      background: active ? '#0f172a' : 'transparent',
                      color: active ? '#fbf8f1' : 'rgba(15,23,42,0.8)',
                      fontSize: 14, fontWeight: 500, transition: 'all 0.15s',
                    }}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Photo section — hidden for text-only layout */}
          {(page.layout || 'photo-spread') !== 'text-only' && (<>
          {(page.photoUrl || page.photoBase64 || page.photoPath) ? (
            <div style={{ marginBottom: 24 }}>
              <label className="st-label">Photo — drag to reposition</label>
              {/* Drag-to-reposition preview matching PDF aspect ratio */}
              <div style={{ position: 'relative', width: '100%', maxWidth: 360, marginBottom: 10 }}>
                <div
                  style={{
                    width: '100%',
                    aspectRatio: (page.layout || 'photo-spread') === 'photo-spread' ? '630/810' : (page.layout === 'story-page' ? '630/360' : (page.layout === 'classic' && page.photoOrientation === 'portrait' ? '190/280' : '280/190')),
                    overflow: 'hidden', borderRadius: 8,
                    border: '1.5px solid rgba(15,23,42,0.15)', cursor: 'grab',
                    position: 'relative', userSelect: 'none', touchAction: 'none',
                  }}
                  onPointerDown={(e) => {
                    e.currentTarget.setPointerCapture(e.pointerId);
                    e.currentTarget.dataset.dragging = 'true';
                    e.currentTarget.dataset.startX = String(e.clientX);
                    e.currentTarget.dataset.startY = String(e.clientY);
                    e.currentTarget.dataset.startFocusX = String(page.photoFocusX ?? 50);
                    e.currentTarget.dataset.startFocusY = String(page.photoFocusY ?? 50);
                    e.currentTarget.style.cursor = 'grabbing';
                  }}
                  onPointerMove={(e) => {
                    if (e.currentTarget.dataset.dragging !== 'true') return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const startX = parseFloat(e.currentTarget.dataset.startX);
                    const startY = parseFloat(e.currentTarget.dataset.startY);
                    const startFocusX = parseFloat(e.currentTarget.dataset.startFocusX);
                    const startFocusY = parseFloat(e.currentTarget.dataset.startFocusY);
                    const deltaX = ((e.clientX - startX) / rect.width) * -100;
                    const deltaY = ((e.clientY - startY) / rect.height) * -100;
                    const updated = [...stBookPages];
                    updated[stBookEditIdx] = {
                      ...updated[stBookEditIdx],
                      photoFocusX: Math.round(Math.max(0, Math.min(100, startFocusX + deltaX))),
                      photoFocusY: Math.round(Math.max(0, Math.min(100, startFocusY + deltaY))),
                    };
                    setStBookPages(updated);
                  }}
                  onPointerUp={(e) => {
                    e.currentTarget.dataset.dragging = 'false';
                    e.currentTarget.style.cursor = 'grab';
                    saveBookData({ pages: cleanPagesForSave(stBookPages) });
                  }}
                  onPointerCancel={(e) => {
                    e.currentTarget.dataset.dragging = 'false';
                    e.currentTarget.style.cursor = 'grab';
                  }}
                >
                  <img loading="lazy" src={page.photoBase64 || page.photoUrl} alt="Chapter" draggable={false}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${page.photoFocusX ?? 50}% ${page.photoFocusY ?? 50}%`, display: 'block', pointerEvents: 'none' }} />
                </div>
              </div>
              {/* Photo action pills */}
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={() => stBookPhotoRef.current?.click()}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 9999, border: '1px solid rgba(15,23,42,0.15)', background: '#efebe0', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: '#0f172a', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#e4dece'}
                  onMouseLeave={e => e.currentTarget.style.background = '#efebe0'}>
                  Change
                </button>
                <button onClick={() => {
                  const updated = [...stBookPages];
                  updated[stBookEditIdx] = { ...updated[stBookEditIdx], photoUrl: null, photoPath: null, photoBase64: null, originalPhotoUrl: null, originalPhotoBase64: null, caption: '', photoSize: 'large', photoFocusX: undefined, photoFocusY: undefined };
                  setStBookPages(updated);
                  setPhotoQualityWarning(null);
                }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 9999, border: '1px solid rgba(15,23,42,0.15)', background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: 'rgba(15,23,42,0.8)', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#efebe0'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  Remove
                </button>
                <button onClick={() => handleEnhancePhoto(page)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 9999, border: page.photoEnhanced ? '1px solid rgba(15,23,42,0.15)' : '1px solid rgba(124,45,18,0.30)', background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: page.photoEnhanced ? '#5a7a4a' : '#7c2d12', transition: 'background 0.15s' }}
                  onMouseEnter={e => { if (!page.photoEnhanced) e.currentTarget.style.background = 'rgba(124,45,18,0.08)'; }}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <PhSparkle size={12} weight="duotone" /> {page.photoEnhanced ? 'Enhanced' : 'Enhance'}
                </button>
              </div>
              {photoQualityWarning && !page.photoEnhanced && (
                <div style={{
                  marginTop: 10, padding: '10px 14px', borderRadius: 8,
                  background: photoQualityWarning.level === 'low' ? 'rgba(192,57,43,0.06)' : 'rgba(220,160,60,0.06)',
                  border: `1px solid ${photoQualityWarning.level === 'low' ? 'rgba(192,57,43,0.2)' : 'rgba(220,160,60,0.2)'}`,
                  fontSize: '0.9rem', color: photoQualityWarning.level === 'low' ? '#8b2c1a' : '#8a6d20', lineHeight: 1.5,
                }}>
                  {photoQualityWarning.message}
                </div>
              )}
              <div style={{ marginTop: 16 }}>
                <input placeholder="Add a photo caption (optional)..." value={page.caption || ''} style={{ width: '100%', fontStyle: 'italic', fontSize: 14, color: '#0f172a', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(15,23,42,0.12)', padding: '8px 0', outline: 'none' }} onChange={e => {
                  const updated = [...stBookPages];
                  updated[stBookEditIdx] = { ...updated[stBookEditIdx], caption: e.target.value };
                  setStBookPages(updated);
                }} />
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 24 }}>
              <button onClick={() => stBookPhotoRef.current?.click()}
                disabled={stBookPhotoUploading}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '20px 22px', background: 'rgba(15,23,42,0.03)', border: '1px solid rgba(15,23,42,0.22)', borderRadius: 14, cursor: 'pointer', width: '100%' }}>
                <PhCamera size={28} weight="duotone" />
                <span style={{ fontSize: '1.2rem', color: '#0f172a', fontFamily: "'Lora', serif" }}>
                  {stBookPhotoUploading ? 'Uploading...' : 'Add a photo to this chapter'}
                </span>
              </button>
              <p style={{ fontSize: '1rem', color: '#3c4653', marginTop: 8 }}>Optional — this chapter will print as text only.</p>
            </div>
          )}
          </>)}

          {/* ── Separator ── */}
          <div style={{ borderTop: '1px solid rgba(15,23,42,0.08)', margin: '32px 0' }} />

          {/* Story text — editable */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <span style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#3c4653' }}>Your story</span>
              <span style={{ fontSize: 12, color: 'rgba(15,23,42,0.8)' }}>{wordCount} words · ~{estPages} {estPages === 1 ? 'page' : 'pages'}</span>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.5)', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 12, padding: '16px 20px' }}>
              <textarea rows={12} value={story.content || ''} onChange={(e) => {
                const newContent = e.target.value;
                setStories(prev => prev.map(s => s.id === story.id ? { ...s, content: newContent } : s));
              }} onBlur={async () => {
                try { await client.graphql({ query: updateLegacyEntry, variables: { input: { id: story.id, content: story.content } } }); setStOriginalContent(null); } catch (e) { console.error('Content save error:', e); }
              }} style={{ width: '100%', minHeight: 280, fontSize: 15, lineHeight: 1.8, color: '#0f172a', background: 'transparent', border: 'none', outline: 'none', resize: 'none', fontFamily: "'Nunito', sans-serif", boxSizing: 'border-box' }} />
            </div>
          </div>

          {/* ── Separator ── */}
          <div style={{ borderTop: '1px solid rgba(15,23,42,0.08)', margin: '32px 0' }} />

          {/* ═══ CHAPTER RECORDING — QR code in printed book ═══ */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#3c4653', marginBottom: 6 }}>Add audio or video</div>
            <p style={{ fontSize: 14, color: '#3c4653', marginBottom: 16 }}>A QR code in the printed book lets readers scan to hear your voice or watch your video.</p>
            {page.shareId ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#efebe0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {page.mediaType === 'VIDEO' ? <PhCamera size={16} weight="duotone" style={{ color: '#7c2d12' }} /> : <PhSparkle size={16} weight="duotone" style={{ color: '#7c2d12' }} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '1rem', color: '#0f172a', fontFamily: "'Lora', serif" }}>Recording attached</div>
                    <div style={{ fontSize: '0.9rem', color: '#3c4653' }}>Readers can scan a QR code in the printed book to hear this message.</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '1rem', color: '#0f172a' }}>
                    <input type="checkbox" checked={page.qrEnabled !== false} onChange={(e) => {
                      const updated = [...stBookPages];
                      updated[stBookEditIdx] = { ...updated[stBookEditIdx], qrEnabled: e.target.checked };
                      setStBookPages(updated);
                    }} style={{ width: 18, height: 18, accentColor: '#0f172a' }} />
                    Include QR code in printed book
                  </label>
                </div>
                {chapterQrCodes[page.storyId] && page.qrEnabled !== false && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px', background: '#fff', borderRadius: 8, border: '1px solid rgba(15,23,42,0.08)', marginBottom: 10 }}>
                    <img src={chapterQrCodes[page.storyId]} alt="QR code" style={{ width: 64, height: 64, borderRadius: 4 }} />
                    <div style={{ fontSize: '0.9rem', color: '#3c4653', fontStyle: 'italic' }}>
                      {page.mediaType === 'VIDEO' ? 'Scan to watch their message' : 'Scan to hear their voice'}
                    </div>
                  </div>
                )}
                <button onClick={() => removeChapterMedia(stBookEditIdx, page.storyId)}
                  style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(220,80,60,0.2)', background: '#fff', cursor: 'pointer', fontSize: '0.9rem', color: '#c53030' }}>
                  Remove recording
                </button>
              </div>
            ) : (
              <div>
                {story.content?.match(/\[audio:[^\]]+\]/) && (
                  <button onClick={() => linkInterviewRecording(stBookEditIdx, page.storyId)}
                    disabled={chapterMediaSaving}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(15,23,42,0.10)', borderRadius: 12, cursor: 'pointer', textAlign: 'left', width: '100%', marginBottom: 12, transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#efebe0'; e.currentTarget.style.borderColor = 'rgba(15,23,42,0.20)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; e.currentTarget.style.borderColor = 'rgba(15,23,42,0.10)'; }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#efebe0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <PhSparkle size={16} weight="duotone" style={{ color: '#7c2d12' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: '#0f172a' }}>{chapterMediaSaving ? 'Linking...' : 'Use interview recording'}</div>
                      <div style={{ fontSize: 12, color: '#3c4653' }}>Reuse the recording from this story.</div>
                    </div>
                  </button>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <button onClick={() => { resetChapterMedia(); setChapterMediaType('audio'); setChapterMediaModal({ pageIdx: stBookEditIdx, storyId: page.storyId }); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(15,23,42,0.10)', borderRadius: 12, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#efebe0'; e.currentTarget.style.borderColor = 'rgba(15,23,42,0.20)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; e.currentTarget.style.borderColor = 'rgba(15,23,42,0.10)'; }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#efebe0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Microphone size={16} weight="duotone" style={{ color: '#7c2d12' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: '#0f172a' }}>Voice message</div>
                      <div style={{ fontSize: 12, color: '#3c4653' }}>Record audio for this chapter.</div>
                    </div>
                  </button>
                  <button onClick={() => { resetChapterMedia(); setChapterMediaType('video'); setChapterMediaModal({ pageIdx: stBookEditIdx, storyId: page.storyId }); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(15,23,42,0.10)', borderRadius: 12, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#efebe0'; e.currentTarget.style.borderColor = 'rgba(15,23,42,0.20)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; e.currentTarget.style.borderColor = 'rgba(15,23,42,0.10)'; }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#efebe0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <PhCamera size={16} weight="duotone" style={{ color: '#7c2d12' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: '#0f172a' }}>Video message</div>
                      <div style={{ fontSize: 12, color: '#3c4653' }}>Record yourself on camera.</div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Save & Return ── */}
          <div className="sticky-save-bar" style={{ marginTop: 32 }}>
            <button onClick={async () => {
              const bookData = { title: stBookTitle, subtitle: stBookSubtitle, author: stBookAuthor, dedication: stBookDedication, coverStyle: stBookCover, coverDesign: stBookCoverDesign, coverPhotoPath: stBookCoverPhotoPath, coverPhotoY: stBookCoverPhotoY, pages: cleanPagesForSave(stBookPages) };
              saveBookData(bookData);
              try { await client.graphql({ query: updateLegacyEntry, variables: { input: { id: story.id, title: story.title, content: story.content } } }); } catch (e) {}
              setStBookStep(0); setStBookEditIdx(null); window.scrollTo(0, 0);
              showToast('Saved!');
            }} style={{ width: '100%', height: 48, fontSize: 16, fontWeight: 500, background: 'linear-gradient(180deg, #AE6015 0%, #A85A0F 100%)', color: '#fbf8f1', border: 'none', borderRadius: 9999, cursor: 'pointer', transition: 'filter 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.06)'}
              onMouseLeave={e => e.currentTarget.style.filter = 'none'}>
              Save &amp; Return
            </button>
            {/* F-17: quiet secondary row — benign actions only. Confirms open a ConfirmModal
                (replace context), never render inline beside siblings. */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, marginTop: 14 }}>
              {books.length > 1 && (
                <button onClick={() => setMovePickerIdx(stBookEditIdx)}
                  style={{ background: 'none', border: 'none', color: 'rgba(15,23,42,0.7)', fontSize: 14, cursor: 'pointer', transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#0f172a'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(15,23,42,0.7)'}>
                  Transfer to another book
                </button>
              )}
              {books.length > 1 && <span style={{ color: 'rgba(15,23,42,0.3)' }}>·</span>}
              <button onClick={() => setConfirmRemoveChapter(true)}
                style={{ background: 'none', border: 'none', color: 'rgba(15,23,42,0.7)', fontSize: 14, cursor: 'pointer', transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color = '#0f172a'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(15,23,42,0.7)'}>
                Remove from this book
              </button>
            </div>
            {/* F-17: Delete forever set apart — distance + hairline so it's never a casual sibling. */}
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(127,29,29,0.14)', textAlign: 'center' }}>
              <button onClick={() => setConfirmDeleteChapter(true)}
                style={{ background: 'none', border: 'none', color: '#9b6a5f', fontSize: 13, fontWeight: 500, letterSpacing: 0.2, cursor: 'pointer', transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color = '#7f1d1d'}
                onMouseLeave={e => e.currentTarget.style.color = '#9b6a5f'}>
                Delete forever
              </button>
            </div>
          </div>

          </div>{/* end chapter-editor-inner */}
          </div>{/* end chapter-editor-scroll */}

          {/* ═══ FROSTED PREVIEW PANE (>=1200px only) ═══ */}
          <div className="chapter-preview-pane">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(15,23,42,0.50)' }}>Page preview</div>
              <ChapterPagePreview
              title={story.title}
              content={story.content}
              layout={page.layout || 'photo-spread'}
              photoUrl={page.photoBase64 || page.photoUrl || null}
              caption={page.caption}
              photoFocusX={page.photoFocusX}
              photoFocusY={page.photoFocusY}
              photoOrientation={page.photoOrientation}
            />
              <p style={{ fontSize: 13, color: 'rgba(15,23,42,0.4)', textAlign: 'center', lineHeight: 1.5, marginTop: 4, maxWidth: 220 }}>Updates as you write. Open Book to see your page exactly as it will print.</p>
            </div>{/* end centered label+card wrapper */}
          </div>

          {/* Preview modal — shown below 1200px */}
          {previewModalOpen && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setPreviewModalOpen(false)}>
              <div style={{ background: '#fbf8f1', borderRadius: 16, padding: '32px 24px', maxWidth: 400, width: '100%', position: 'relative' }} onClick={e => e.stopPropagation()}>
                <button onClick={() => setPreviewModalOpen(false)} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', fontSize: 20, color: 'rgba(15,23,42,0.5)', cursor: 'pointer' }}>×</button>
                <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(15,23,42,0.50)', marginBottom: 16, textAlign: 'center' }}>Page preview</div>
                <ChapterPagePreview
                  title={story.title}
                  content={story.content}
                  layout={page.layout || 'photo-spread'}
                  photoUrl={page.photoBase64 || page.photoUrl || null}
                  caption={page.caption}
                  photoFocusX={page.photoFocusX}
                  photoFocusY={page.photoFocusY}
                  photoOrientation={page.photoOrientation}
                />
              </div>
            </div>
          )}

          {/* Enhance confirmation modal */}

          {/* Delete-forever confirm (permanent: deletes the story everywhere) */}
          {confirmDeleteChapter && (
            <ConfirmModal
              title="Delete this chapter forever?"
              message="This permanently deletes the story and removes it from your book. This can't be undone."
              confirmLabel="Delete forever"
              cancelLabel="Keep it"
              destructive
              onConfirm={async () => {
                const sid = story.id;
                // F-252: ATOMIC ORDER, per the executeDeleteStory contract (App.js:2129-2131).
                // This path used to drop the page and persist it BEFORE awaiting the delete, so a
                // failed delete left the story alive with its page gone — the orphan the contract
                // exists to prevent. It also announced "Chapter deleted." unconditionally:
                // executeDeleteStory RETURNS false on failure rather than throwing, so the catch
                // below could never fire and the user was told a delete succeeded when it had not.
                setConfirmDeleteChapter(false);
                await runStoryDelete({
                  storyId: sid,
                  deleteEntry: (id) => executeDeleteStory(id, { silent: true }),
                  // Stay exactly where we are — the chapter is untouched and still in the book.
                  onFailure: () => showToast("Couldn't delete it. Nothing changed. Please try again.", 'error'),
                  onDeleted: (id) => {
                    setStBookPages(prev => prev.filter(pg => pg.storyId !== id));
                    removeStoryPageEverywhere(id); // every book, tombstoned so the merge cannot undo it
                    setStBookEditIdx(null);
                    setStBookStep(0);
                    window.scrollTo(0, 0);
                    showToast('Chapter deleted.');
                  },
                });
              }}
              onCancel={() => setConfirmDeleteChapter(false)}
            />
          )}

          {/* Remove-from-book confirm (non-destructive: the story stays in the library) */}
          {confirmRemoveChapter && (
            <ConfirmModal
              title="Remove from this book?"
              message="Your story stays in your library. It's just removed from this book."
              confirmLabel="Remove"
              cancelLabel="Keep it"
              onConfirm={() => {
                const sid = stBookPages[stBookEditIdx]?.storyId;
                setStBookPages(prev => prev.filter((_, i) => i !== stBookEditIdx));
                // F-252: non-destructive — tombstone THIS book only, so the story stays in the
                // library and can be re-added later (the add path clears the tombstone). Without
                // the tombstone the additive merge simply put this page back.
                if (sid) removeStoryPageFromBook(activeBookId, sid);
                setStBookEditIdx(null);
                setStBookStep(0);
                setConfirmRemoveChapter(false);
                window.scrollTo(0, 0);
                showToast('Removed from this book. Your story is still saved.');
              }}
              onCancel={() => setConfirmRemoveChapter(false)}
            />
          )}

          {/* Move to Book picker */}
          {movePickerIdx === stBookEditIdx && books.length > 1 && (() => {
            const otherBooks = [...books].filter(b => b.id !== activeBookId).sort((a, b) => (a.bookType === 'PERSONAL' ? 0 : 1) - (b.bookType === 'PERSONAL' ? 0 : 1));
            return (
              <div className="transfer-scrim" onClick={() => setMovePickerIdx(null)}>
                <div className="transfer-panel" onClick={e => e.stopPropagation()}>
                <div className="transfer-grab" />
                <div style={{ fontFamily: "'Lora', serif", fontSize: '0.82rem', letterSpacing: 2, textTransform: 'uppercase', color: '#3c4653', marginBottom: 14 }}>Move to another book</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {otherBooks.map(book => (
                    <div key={book.id} onClick={() => {
                      const pageToMove = stBookPages[stBookEditIdx];
                      // Remove from current book
                      const updatedCurrent = stBookPages.filter((_, i) => i !== stBookEditIdx);
                      setStBookPages(updatedCurrent);
                      // Add to target book
                      const targetBook = (booksRef.current || books).find(b => b.id === book.id);
                      const targetPages = [...(targetBook?.pages || []), cleanPagesForSave([pageToMove])[0]];
                      // F-252: a Move is a remove + an add, so both halves need tombstone care.
                      // Source book: tombstone the story, or the additive merge moves it straight
                      // back. Target book: clear any tombstone, or the merge strips the new page.
                      const sid = pageToMove?.storyId;
                      const updatedBooks = (booksRef.current || books).map(b => {
                        if (b.id === activeBookId) {
                          const src = { ...b, pages: cleanPagesForSave(updatedCurrent) };
                          return sid ? withStoryRemovedFromBook([src], b.id, sid)[0] : src;
                        }
                        if (b.id === book.id) return clearTombstones({ ...b, pages: targetPages }, targetPages);
                        return b;
                      });
                      persistBooks(updatedBooks);
                      setMovePickerIdx(null);
                      setStBookEditIdx(null);
                      setStBookStep(0);
                      showToast(`Moved to ${book.title || 'book'}`);
                    }} style={{
                      padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
                      border: '1.5px solid rgba(15,23,42,0.1)', background: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c2d12'; e.currentTarget.style.background = '#f5efd8'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(15,23,42,0.1)'; e.currentTarget.style.background = '#fff'; }}
                    >
                      <span style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: '#0f172a' }}>{book.title || 'Untitled Book'}</span>
                      <span style={{
                        fontSize: '0.75rem', letterSpacing: 1, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 6,
                        background: book.bookType === 'PERSONAL' ? 'rgba(15,23,42,0.06)' : 'rgba(140,80,180,0.08)',
                        color: book.bookType === 'PERSONAL' ? '#3c4653' : '#8c50b4',
                        border: book.bookType === 'PERSONAL' ? '1px solid rgba(15,23,42,0.1)' : '1px solid rgba(140,80,180,0.15)',
                      }}>{bookTypeChipLabel(book.bookType)}</span>
                    </div>
                  ))}
                </div>
                </div>
              </div>
            );
          })()}
        </div>{/* end chapter-editor-shell */}
      </>);
    }

    // ── Step 0: Book home — PDF spread + senior-friendly controls ──
    const hasPhotosToLoad = stBookPages.some(p => p.photoPath && !p.photoBase64);

    return (
      <>
      <div className="storyteller-content" data-journey-step="book-0" style={{ maxWidth: 1280 }}>
        <input ref={stBookPhotoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleStBookPhoto} />

        {stBookSaveStatus && (
          <div style={{ textAlign: 'center', marginBottom: 4 }}>
            <span style={{ fontSize: '1.05rem', color: stBookSaveStatus === 'saving' ? '#0f172a' : '#4a8a4a', fontFamily: "'Lora', serif" }}>
              {stBookSaveStatus === 'saving' ? '💾 Saving...' : '✓ Saved'}
            </span>
          </div>
        )}

        {/* Gift Book CTA banner removed 2026-05-15 — discovery now via sidebar My Books → list view */}

        {/* Gift book deadline bar + contributor tracker */}
        {isGiftBook && activeBook && (
          <div style={{ padding: '16px 20px', background: '#fffdf5', border: '2px solid #7c2d12', borderRadius: 12, marginBottom: 16 }}>
            {(() => {
              const occ = OCCASION_TYPES.find(o => o.id === activeBook.occasion);
              const daysLeft = activeBook.deliveryDate ? Math.ceil((new Date(activeBook.deliveryDate) - new Date()) / 86400000) : null;
              const daysUntilDeadline = activeBook.contributionDeadline ? Math.ceil((new Date(activeBook.contributionDeadline) - new Date()) / 86400000) : null;
              const contributors = activeBook.contributors || [];
              const contributed = contributors.filter(c => c.status === 'CONTRIBUTED').length; // eslint-disable-line no-unused-vars
              const pct = daysLeft > 0 && activeBook.deliveryDate ? Math.max(0, Math.min(100, ((new Date() - new Date(activeBook.createdAt)) / (new Date(activeBook.deliveryDate) - new Date(activeBook.createdAt))) * 100)) : 100;
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 22 }}>{occ?.emoji || ''}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.9rem', color: '#3c4653', fontWeight: 500 }}>{activeBook.occasion === 'IN_MEMORY' ? `In memory of ${activeBook.recipientName}` : `${occ?.label || 'Celebration'} for ${activeBook.recipientName}`}</div>
                    </div>
                    {daysLeft != null && (
                      <div style={{ fontSize: '0.92rem', fontWeight: 600, color: daysLeft <= 7 ? '#c53030' : daysLeft <= 14 ? '#b8860b' : '#6b8f71' }}>
                        {daysLeft > 0 ? `${daysLeft} days left` : 'Past due'}
                      </div>
                    )}
                  </div>
                  {/* Progress bar */}
                  <div style={{ height: 6, background: 'rgba(15,23,42,0.08)', borderRadius: 3, marginBottom: 10, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: daysLeft <= 5 ? '#c53030' : daysLeft <= 14 ? '#b8860b' : '#6b8f71', borderRadius: 3, transition: 'width 0.3s' }} />
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: '0.88rem', color: '#3c4653' }}>
                    {activeBook.orderByDate && <span>Have ready by: {new Date(activeBook.orderByDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                  </div>
                  {/* Contributors header with + Invite */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, marginBottom: 8 }}>
                    <div style={{ fontFamily: "'Lora', serif", fontSize: '0.82rem', letterSpacing: 2, color: '#3c4653', textTransform: 'uppercase', fontWeight: 600 }}>
                      Contributors {contributors.length > 0 && `(${contributors.length})`}
                    </div>
                    <button onClick={() => { setStBookStep(7); setStInviteLink(''); setStInviteQuestion(''); setStInviteMessage(''); setStInviteName(''); setStInviteRelationship(''); window.scrollTo(0, 0); }}
                      style={{ fontSize: '0.82rem', padding: '4px 12px', borderRadius: 4, background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.25)', cursor: 'pointer', color: '#7a5a10', fontWeight: 600, fontFamily: "'Lora', serif", letterSpacing: 1 }}>
                      + Invite
                    </button>
                  </div>
                  {contributors.length > 0 ? (
                    <div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {contributors.map((c, i) => {
                          const contribLink = `${window.location.origin}/contribute/${c.token}`;
                          const isExpanded = expandedContributor === i;
                          return (
                            <div key={i}>
                              <div onClick={() => setExpandedContributor(isExpanded ? null : i)}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: isExpanded ? '10px 10px 0 0' : 10, background: c.status === 'CONTRIBUTED' ? 'rgba(90,160,80,0.06)' : 'rgba(15,23,42,0.03)', border: `1px solid ${c.status === 'CONTRIBUTED' ? 'rgba(90,160,80,0.15)' : 'rgba(15,23,42,0.08)'}`, borderBottom: isExpanded ? 'none' : undefined, cursor: 'pointer', transition: 'all 0.15s' }}>
                                <div style={{ width: 32, height: 32, borderRadius: '50%', background: c.status === 'CONTRIBUTED' ? 'rgba(90,160,80,0.15)' : 'rgba(15,23,42,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                                  {c.status === 'CONTRIBUTED' ? '✓' : ''}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '0.92rem', fontWeight: 500, color: '#0f172a' }}>{c.name}</div>
                                  {c.relationship && <div style={{ fontSize: '0.82rem', color: '#3c4653' }}>{c.relationship}</div>}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                  <span style={{ fontSize: '0.78rem', padding: '3px 8px', borderRadius: 12, fontWeight: 500, background: c.status === 'CONTRIBUTED' ? 'rgba(90,160,80,0.12)' : 'rgba(201,168,76,0.12)', color: c.status === 'CONTRIBUTED' ? '#4a8a4a' : '#7a5a10' }}>
                                    {c.status === 'CONTRIBUTED' ? 'Contributed' : 'Invited'}
                                  </span>
                                  <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(contribLink).then(() => showToast('Link copied for ' + c.name)); }}
                                    style={{ fontSize: '0.78rem', padding: '3px 8px', borderRadius: 12, background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.1)', cursor: 'pointer', color: '#3c4653', whiteSpace: 'nowrap' }}
                                    title="Copy invite link">
                                    
                                  </button>
                                  {c.status !== 'CONTRIBUTED' && (
                                    <button onClick={(e) => { e.stopPropagation(); if (navigator.share) { navigator.share({ title: `Share a story for ${activeBook.recipientName}'s book`, text: `We're making a book for ${activeBook.recipientName} and would love your story!`, url: contribLink }).catch(() => {}); } else { navigator.clipboard.writeText(contribLink).then(() => showToast('Link copied — send it to ' + c.name)); } }}
                                      style={{ fontSize: '0.78rem', padding: '3px 8px', borderRadius: 12, background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.1)', cursor: 'pointer', color: '#3c4653', whiteSpace: 'nowrap' }}
                                      title="Nudge — share or copy link">
                                      Nudge
                                    </button>
                                  )}
                                  <span style={{ fontSize: '0.75rem', color: '#b0a090', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>▼</span>
                                </div>
                              </div>
                              {/* Expanded detail panel */}
                              {isExpanded && (
                                <div style={{ padding: '12px 14px', background: c.status === 'CONTRIBUTED' ? 'rgba(90,160,80,0.03)' : 'rgba(15,23,42,0.02)', border: `1px solid ${c.status === 'CONTRIBUTED' ? 'rgba(90,160,80,0.15)' : 'rgba(15,23,42,0.08)'}`, borderTop: 'none', borderRadius: '0 0 10px 10px' }}>
                                  {c.question && (
                                    <div style={{ marginBottom: 10 }}>
                                      <div style={{ fontSize: '0.75rem', color: '#3c4653', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Question</div>
                                      <div style={{ fontSize: '0.92rem', color: '#0f172a', fontStyle: 'italic', lineHeight: 1.5 }}>"{c.question}"</div>
                                    </div>
                                  )}
                                  <div style={{ marginBottom: 10 }}>
                                    <div style={{ fontSize: '0.75rem', color: '#3c4653', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Invite Link</div>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                      <div style={{ flex: 1, fontSize: '0.85rem', color: '#3c4653', wordBreak: 'break-all', background: '#fff', padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(15,23,42,0.08)' }}>
                                        {contribLink}
                                      </div>
                                      <button onClick={() => { navigator.clipboard.writeText(contribLink).then(() => showToast('Link copied!')); }}
                                        style={{ padding: '6px 12px', fontSize: '0.82rem', borderRadius: 8, background: '#7c2d12', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        Copy Link
                                      </button>
                                    </div>
                                  </div>
                                  <div style={{ fontSize: '0.78rem', color: '#b0a090' }}>
                                    Invited {c.invitedAt ? new Date(c.invitedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'recently'}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {contributors.some(c => c.status !== 'CONTRIBUTED') && (
                        <button onClick={() => {
                          const pending = contributors.filter(c => c.status !== 'CONTRIBUTED');
                          const text = pending.map(c => `${c.name}: ${window.location.origin}/contribute/${c.token}`).join('\n');
                          if (navigator.share) { navigator.share({ title: `Reminder: Share a story for ${activeBook.recipientName}'s book`, text }).catch(() => {}); }
                          else { navigator.clipboard.writeText(text).then(() => showToast(`${pending.length} invite link${pending.length > 1 ? 's' : ''} copied!`)); }
                        }}
                          style={{ marginTop: 8, width: '100%', padding: '8px 14px', fontSize: '0.88rem', borderRadius: 4, background: '#7c2d12', border: 'none', cursor: 'pointer', color: '#1a1612', fontFamily: "'Lora', serif", fontWeight: 700, boxShadow: '0 2px 12px rgba(201,168,76,0.3)' }}>
                          📣 Nudge All ({contributors.filter(c => c.status !== 'CONTRIBUTED').length} pending)
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '12px 0', fontSize: '0.92rem', color: '#3c4653', fontStyle: 'italic' }}>
                      No contributors yet — invite family to share their stories.
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* (Removed) The organizer's own-story brown CTA was redundant with the primary
            "Add your next story" hero below — both opened the same interview, which read as
            two identical buttons. The In-Memory warm wording now lives on that hero button. */}

        <>
            {!stBookOpen ? (
              /* ═══ CLOSED BOOK COVER — mirrors actual print cover ═══ */
              <div style={{ display: 'flex', gap: 40, alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap' }}>
                {/* Left: 3D book cover */}
                <div style={{ perspective: 800, flexShrink: 0, maxWidth: '100%', position: 'relative' }}>
                  {/* Edit cover affordance — overlays the 3D book, click stops propagation so it doesn't also open the book */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setStBookStep(2); window.scrollTo(0, 0); }}
                    aria-label="Edit cover"
                    title="Edit cover"
                    style={{
                      position: 'absolute', top: 8, right: 8, zIndex: 10,
                      width: 36, height: 36, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.92)', border: '1px solid rgba(15,23,42,0.15)',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                      transition: 'all 0.15s', padding: 0,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.transform = 'scale(1.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; e.currentTarget.style.transform = 'none'; }}>
                    <PhPencil size={18} weight="duotone" color="#0f172a" />
                  </button>
                  <div
                    onClick={() => { setStBookOpen(true); setStBookPreviewIdx(0); }}
                    style={{
                      width: Math.min(300, window.innerWidth - 64), height: Math.min(388, (window.innerWidth - 64) * 1.293), position: 'relative', cursor: 'pointer',
                      transformStyle: 'preserve-3d',
                      transform: 'rotateY(-12deg) rotateX(2deg)',
                      transition: 'transform 0.4s ease',
                    }}
                    onMouseOver={e => { e.currentTarget.style.transform = 'rotateY(-6deg) rotateX(1deg)'; }}
                    onMouseOut={e => { e.currentTarget.style.transform = 'rotateY(-12deg) rotateX(2deg)'; }}
                  >
                    {/* Spine face */}
                    <div style={{
                      position: 'absolute', top: 0, left: 0, bottom: 0, width: 16,
                      background: 'linear-gradient(to right, #161310, #1e1a16)',
                      transform: 'rotateY(-90deg) translateX(-8px) translateZ(8px)',
                      backfaceVisibility: 'hidden',
                    }} />
                    {/* Page edges bottom */}
                    <div style={{
                      position: 'absolute', bottom: -4, left: 8, right: 0, height: 4,
                      background: 'linear-gradient(to bottom, #e8e0d4, #d8cfc0)',
                      borderRadius: '0 0 2px 0',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                    }} />
                    {/* Front cover */}
                    <div style={{
                      position: 'absolute', inset: 0,
                      borderRadius: '2px 8px 8px 2px',
                      overflow: 'hidden',
                      background: '#1e1a16',
                      backfaceVisibility: 'hidden',
                      boxShadow: '6px 4px 20px rgba(10,6,2,0.35), 12px 12px 40px rgba(10,6,2,0.2), -2px 0 8px rgba(10,6,2,0.1)',
                    }}>
                      {/* Subtle linen texture */}
                      <div style={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'6\' height=\'6\' viewBox=\'0 0 6 6\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h1v1H0zM3 3h1v1H3z\' fill=\'%23fff\' fill-opacity=\'1\'/%3E%3C/svg%3E")', pointerEvents: 'none', zIndex: 3 }} />
                  {/* ── 3D COVER PREVIEW — design × layout ── */}
                  {(() => {
                    const isBotanical = stBookCoverDesign !== 'charcoal';
                    const bgImage = stBookCoverDesign === 'burgundy' ? BurgundyCover : stBookCoverDesign === 'mothersday' ? MothersDayCover : stBookCoverDesign === 'linen' ? GoldLinenCover : stBookCoverDesign === 'charcoalgold' ? CharcoalGoldCover : stBookCoverDesign === 'floralwrap' ? FloralWrapCover : stBookCoverDesign === 'burgwrap' ? BurgWrapCover : stBookCoverDesign === 'slatechevron' ? SlateChevronCover : null;
                    const coverPhoto = stBookCoverPhotoBase64 || stBookCoverPhotoUrl || null;

                    const isFullWrapDesign = FULLWRAP_DESIGNS.includes(stBookCoverDesign);
                    // For full-wrap designs, preview the TRUE printed front panel
                    // (honest crop), not the wrap art with a background-position
                    // guess that leaks trimmed-off edge flowers into the preview.
                    const fullWrapFrontBg = stBookCoverDesign === 'floralwrap' ? FloralWrapFrontPanel
                      : stBookCoverDesign === 'burgwrap' ? BurgWrapFrontPanel : bgImage;

                    if (stBookCoverDesign === 'mustardplum') {
                      return renderFloralWrapPreview();
                    } else if (isBotanical && stBookCover === 'framed') {
                      // Botanical + Framed — centered panel with photo
                      // Full-wrap designs: show only the right portion (front panel) of the landscape image
                      const photoSlot = getFramedPhotoSlot(stBookCoverPhotoOrientation);
                      return (<>
                        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${isFullWrapDesign ? fullWrapFrontBg : bgImage})`, backgroundSize: 'cover', backgroundPosition: isFullWrapDesign ? 'center' : 'center bottom' }} />
                        <div style={{
                          position: 'absolute', inset: 0,
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          zIndex: 1,
                        }}>
                          {/* marginLeft/Right mirrors FrontFramedBotanical PDF: 0.125" hinge shift toward fore-edge (COVER-001) */}
                          <div style={{
                            width: BOTANICAL_FRAMED_PREVIEW.panelWidthPct, padding: BOTANICAL_FRAMED_PREVIEW.panelPadding,
                            background: 'rgba(253,246,238,0.88)',
                            borderRadius: 10,
                            border: '0.75px solid rgba(190,155,120,0.38)',
                            textAlign: 'center',
                            marginLeft: '1.47%', marginRight: '-1.47%',
                          }}>
                            <div style={{ fontFamily: "'Lora', serif", fontSize: BOTANICAL_FRAMED_PREVIEW.titleFontRem, color: '#1e0e06', lineHeight: 1.2, marginBottom: 5 }}>{stBookTitle || 'Our Story'}</div>
                            <div style={{ width: '55%', height: 0.8, background: '#7c2d12', margin: '0 auto 6px' }} />
                            {coverPhoto ? (
                              <div style={{ width: BOTANICAL_FRAMED_PREVIEW.photoWidthPct, margin: '0 auto', aspectRatio: photoSlot.aspect, border: '1px solid #7c2d12', padding: 2, marginBottom: 6, overflow: 'hidden' }}>
                                <img loading="lazy" src={coverPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `center ${stBookCoverPhotoY}%`, display: 'block' }} />
                              </div>
                            ) : (
                              <div style={{ width: BOTANICAL_FRAMED_PREVIEW.photoWidthPct, margin: '0 auto', aspectRatio: photoSlot.aspect, border: '1px solid rgba(201,168,76,0.3)', padding: 2, marginBottom: 6 }} />
                            )}
                            <div style={{ width: '55%', height: 0.8, background: '#7c2d12', margin: '0 auto 5px' }} />
                            {stBookSubtitle && <div style={{ fontFamily: "'Lora', serif", fontSize: '0.7rem', color: '#5a3820', fontStyle: 'italic' }}>{stBookSubtitle}</div>}
                            {stBookAuthor && <div style={{ fontFamily: "'Lora', serif", fontSize: '0.55rem', color: '#6a4028', fontStyle: 'italic', marginTop: 2 }}>{stBookAuthor}</div>}
                            <div style={{ fontFamily: "'Lora', serif", fontSize: '0.45rem', color: '#7c2d12', fontStyle: 'italic', opacity: 0.7, marginTop: 2 }}>{new Date().getFullYear()}</div>
                          </div>
                        </div>
                      </>);
                    } else if (isBotanical) {
                      // Botanical + Classic — top-left panel, text only
                      return (<>
                        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${isFullWrapDesign ? fullWrapFrontBg : bgImage})`, backgroundSize: 'cover', backgroundPosition: isFullWrapDesign ? 'center' : 'center bottom' }} />
                        <div style={{
                          position: 'absolute', top: 20, left: 20,
                          width: 'fit-content', minWidth: 80, maxWidth: 140, padding: '12px 14px',
                          background: 'rgba(250,245,237,0.92)',
                          border: '0.75px solid rgba(190,155,120,0.38)',
                          outline: '3px solid rgba(240,230,215,0.55)', outlineOffset: 3,
                          zIndex: 1,
                        }}>
                          <div style={{ fontFamily: "'Lora', serif", fontSize: '1.2rem', color: '#1e0e06', lineHeight: 1.15, marginBottom: 6 }}>{stBookTitle || 'Our Story'}</div>
                          <div style={{ width: 20, height: 0.5, background: 'rgba(160,110,75,0.45)', marginBottom: 5 }} />
                          {stBookSubtitle && <div style={{ fontFamily: "'Lora', serif", fontSize: '0.75rem', color: '#5a3820', fontStyle: 'italic', marginBottom: 2 }}>{stBookSubtitle}</div>}
                          {stBookAuthor && <div style={{ fontFamily: "'Lora', serif", fontSize: '0.6rem', color: '#6a4028', fontStyle: 'italic' }}>{stBookAuthor}</div>}
                        </div>
                      </>);
                    } else if (stBookCover === 'framed') {
                      // Charcoal + Framed
                      // paddingLeft > paddingRight mirrors FrontFramedCharcoal PDF: 0.125" hinge shift toward fore-edge (COVER-001)
                      const photoSlot = getFramedPhotoSlot(stBookCoverPhotoOrientation);
                      return (<>
                        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 28, paddingBottom: 28, paddingLeft: 'calc(32px + 1.47%)', paddingRight: 'calc(32px - 1.47%)' }}>
                          <div style={{ fontFamily: "'Newsreader', serif", fontSize: '1rem', letterSpacing: 3, color: '#d4c8b0', marginBottom: 4 }}>Talebound</div>
                          <div style={{ width: 30, height: 0.5, background: 'rgba(200,189,168,0.2)', marginBottom: 6 }} />
                          <div style={{ fontFamily: "'Lora', serif", fontSize: '1.5rem', color: '#e8dcc8', textAlign: 'center', lineHeight: 1.2, marginBottom: 6 }}>{stBookTitle || 'Our Story'}</div>
                          <div style={{ width: 30, height: 0.5, background: 'rgba(200,189,168,0.2)', marginBottom: 10 }} />
                          {coverPhoto ? (
                            <div style={{ width: '62%', aspectRatio: photoSlot.aspect, border: '1.5px solid rgba(200,189,168,0.25)', padding: 3, marginBottom: 10, overflow: 'hidden' }}>
                              <img loading="lazy" src={coverPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `center ${stBookCoverPhotoY}%`, display: 'block' }} />
                            </div>
                          ) : (
                            <div style={{ width: '62%', aspectRatio: photoSlot.aspect, border: '1.5px solid rgba(200,189,168,0.15)', padding: 3, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontSize: '1.05rem', color: 'rgba(200,189,168,0.2)', fontStyle: 'italic' }}>Add a photo</span>
                            </div>
                          )}
                          {stBookSubtitle && <div style={{ fontFamily: "'Lora', serif", fontSize: '0.7rem', color: 'rgba(200,189,168,0.55)', fontStyle: 'italic', textAlign: 'center' }}>{stBookSubtitle}</div>}
                          {stBookAuthor && <div style={{ fontFamily: "'Lora', serif", fontSize: '0.65rem', color: 'rgba(200,189,168,0.4)', fontStyle: 'italic', marginTop: 3 }}>{stBookAuthor}</div>}
                        </div>
                      </>);
                    } else {
                      // Charcoal + Classic (default)
                      return (<>
                        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '36px 32px' }}>
                          <div style={{ width: 50, height: 1, background: 'rgba(200,189,168,0.15)', marginBottom: 20 }} />
                          <div style={{ fontFamily: "'Newsreader', serif", fontSize: '1rem', letterSpacing: 4, color: '#d4c8b0', marginBottom: 16 }}>Talebound</div>
                          <div style={{ fontFamily: "'Lora', serif", fontSize: '2rem', color: '#d4c8b0', fontWeight: 500, lineHeight: 1.2, marginBottom: 10, textAlign: 'center' }}>{stBookTitle || 'Our Story'}</div>
                          <div style={{ width: 35, height: 1, background: 'rgba(200,189,168,0.2)', marginBottom: 10 }} />
                          {stBookSubtitle && <div style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: 'rgba(200,189,168,0.55)', fontStyle: 'italic', lineHeight: 1.4, textAlign: 'center' }}>{stBookSubtitle}</div>}
                          {stBookAuthor && <div style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: 'rgba(200,189,168,0.4)', marginTop: 8, textAlign: 'center' }}>{stBookAuthor}</div>}
                          <div style={{ width: 50, height: 1, background: 'rgba(200,189,168,0.15)', marginTop: 20 }} />
                        </div>
                      </>);
                    }
                  })()}
                    </div>
                    {/* end front cover */}
                  </div>
                  {/* end 3D book */}
                </div>
                {/* end perspective / left column */}

                {/* Right: info + actions — Replit layout */}
                <div style={{ flex: 1, minWidth: 200, paddingTop: 4 }}>

                {/* Badge + count */}
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  {!est.needsMorePages ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 14px', background: '#efebe0', border: '1px solid rgba(15,23,42,0.2)', borderRadius: 9999, fontSize: '0.8rem', color: '#0f172a' }}>Ready to print</span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 14px', background: 'rgba(220,160,60,0.08)', border: '1px solid rgba(220,160,60,0.25)', borderRadius: 9999, fontSize: '0.8rem', color: '#92400e' }}>{(() => { const s = Math.max(1, Math.ceil(est.pagesNeeded / 3)); return `About ${s} more ${s === 1 ? 'story' : 'stories'} to unlock printing`; })()}</span>
                  )}
                  <span style={{ fontSize: '0.9rem', color: 'rgba(15,23,42,0.8)' }}>
                    {stBookPages.length} chapters · {activeBook?.realPageCount != null ? `${activeBook.realPageCount} pages` : `~${est.printPages} pages`}
                  </span>
                  {isGiftBook && (
                    <button onClick={async () => { giftSyncedRef.current = false; await refreshStories(); syncGiftBookChapters(stories, books); showToast('Checked for new contributions'); }}
                      style={{ background: 'none', border: '1px solid rgba(15,23,42,0.12)', borderRadius: 9999, padding: '4px 10px', cursor: 'pointer', fontSize: '0.8rem', color: '#3c4653', transition: 'all 0.2s' }}
                      title="Check for new contributions">↻ Check for new stories</button>
                  )}
                </div>

                {/* Book title */}
                {/* Book title doubles as a switcher when there's more than one book. */}
                <div style={{ position: 'relative', marginBottom: 20 }}>
                  <button
                    onClick={() => { if ((books || []).length >= 1) setSwitcherOpen(o => !o); }}
                    onMouseEnter={e => { if ((books || []).length >= 1) e.currentTarget.style.background = 'rgba(201,168,76,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    title="Switch books or start a new one"
                    style={{ background: 'transparent', border: 'none', padding: '4px 10px', margin: '-4px -10px', borderRadius: 12, cursor: (books || []).length >= 1 ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 12, textAlign: 'left', transition: 'background 0.15s' }}>
                    <h1 style={{ fontFamily: "'Lora', serif", fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 500, color: '#0f172a', margin: 0, lineHeight: 1.2 }}>{stBookTitle || 'My Stories'}</h1>
                    {(books || []).length >= 1 && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '50%', background: 'rgba(201,168,76,0.16)', border: '1px solid rgba(201,168,76,0.4)', color: '#b8860b', fontSize: '0.9rem', flexShrink: 0, transform: switcherOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
                    )}
                  </button>
                  {switcherOpen && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 60, marginTop: 8, minWidth: 280, maxWidth: 360, background: '#fff', border: '1px solid rgba(15,23,42,0.12)', borderRadius: 14, boxShadow: '0 12px 32px rgba(15,23,42,0.16)', padding: 8 }}>
                      {(books || []).map(bk => (
                        <button key={bk.id}
                          onClick={() => { setSwitcherOpen(false); if (bk.id !== activeBookId) enterBook(bk.id); }}
                          style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, padding: '11px 12px', background: bk.id === activeBookId ? 'rgba(201,168,76,0.10)' : 'transparent', border: 'none', borderRadius: 10, cursor: 'pointer', textAlign: 'left' }}>
                          <PhBookOpen size={16} weight="duotone" style={{ color: '#7a5a10', flexShrink: 0 }} />
                          <span style={{ flex: 1, fontFamily: "'Lora', serif", fontSize: '1rem', fontWeight: bk.id === activeBookId ? 600 : 500, color: '#0f172a' }}>{bk.title || 'My Stories'}</span>
                          {bk.id === activeBookId && <span style={{ color: '#7a5a10', fontWeight: 700 }}>✓</span>}
                        </button>
                      ))}
                      {(memoryCareSubjects || []).map(subject => (
                        <button key={`mc-${subject.id}`}
                          onClick={() => { setSwitcherOpen(false); if (!MEMORY_CARE_ENABLED) { openMemoryCareComingSoon(); return; } setMcSubject(subject); setMcStep('welcome'); setMemoryTab('interview'); setMemoryStep('intro'); setActiveTab('memory'); window.scrollTo(0, 0); }}
                          style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, padding: '11px 12px', background: 'transparent', border: 'none', borderRadius: 10, cursor: 'pointer', textAlign: 'left' }}>
                          <PhSparkle size={16} weight="duotone" style={{ color: '#9b8faa', flexShrink: 0 }} />
                          <span style={{ flex: 1, fontFamily: "'Lora', serif", fontSize: '1rem', color: '#0f172a' }}>{subject.name}<span style={{ fontSize: '0.8rem', color: '#9b8faa' }}> · Memory Care</span></span>
                        </button>
                      ))}
                      <div style={{ borderTop: '1px solid rgba(15,23,42,0.08)', marginTop: 6, paddingTop: 6 }}>
                        <button onClick={() => { setSwitcherOpen(false); exitToBookList(); }}
                          style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8, padding: '11px 12px', background: 'transparent', border: 'none', borderRadius: 10, cursor: 'pointer', color: '#7a5a10', fontFamily: "'Nunito', sans-serif", fontSize: '0.95rem', fontWeight: 700 }}>
                          <PhPlus size={15} weight="bold" /> Start a new book
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* PRIMARY ACTION — "Add your next story" is ALWAYS the hero (you can always
                    keep adding). Once the book is printable, an Order button appears beneath it. */}
                <div style={{ marginBottom: 12 }}>
                  <button onClick={() => { setStShowGateway(false); setStInterviewMode(false); setActiveTab('talk'); }}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, width: '100%', maxWidth: 440, background: 'linear-gradient(135deg,#F0A24C,#D2761F)', color: '#fff', border: 'none', borderRadius: 16, padding: '16px 24px', fontFamily: "'Nunito', sans-serif", fontSize: '1.1rem', fontWeight: 800, cursor: 'pointer', boxShadow: '0 8px 20px rgba(210,118,31,0.32), inset 0 1px 0 rgba(255,255,255,0.3)' }}>
                    <PhPlus size={18} weight="bold" /> {isGiftBook && activeBook?.occasion === 'IN_MEMORY' ? `Share your memory of ${activeBook.recipientName || 'them'}` : 'Add your next story'}
                  </button>
                  {!est.needsMorePages && (
                    <button onClick={() => { trackEvent('book_order_started', { item_category: isGiftBook ? 'celebration' : 'book' }); setStOrderStep('address'); setStBookStep(6); window.scrollTo(0, 0); }}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, width: '100%', maxWidth: 440, marginTop: 10, background: 'linear-gradient(135deg,#8a5a2a,#7c2d12)', color: '#fff', border: 'none', borderRadius: 16, padding: '15px 24px', fontFamily: "'Nunito', sans-serif", fontSize: '1.05rem', fontWeight: 800, cursor: 'pointer', boxShadow: '0 8px 20px rgba(124,45,18,0.3), inset 0 1px 0 rgba(255,255,255,0.2)' }}>
                      <PhBookOpen size={18} weight="duotone" /> Order your book
                    </button>
                  )}
                </div>

                {/* QUICK ROW — everyday actions; everything else lives under More */}
                {(() => {
                  const quickStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', color: '#3c4653', border: '1.5px solid rgba(15,23,42,0.14)', borderRadius: 9999, padding: '9px 18px', fontFamily: "'Nunito', sans-serif", fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' };
                  return (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                      <button onClick={() => { setStBookOpen(true); setStBookPreviewIdx(0); }} style={quickStyle}>
                        <PhBookOpen size={15} weight="duotone" /> Preview
                      </button>
                      <button onClick={() => setStBookStep(2)} style={quickStyle}>
                        <PhPalette size={15} weight="duotone" /> Cover
                      </button>
                      <button onClick={() => { setPhotoFilter('all'); setStBookStep(9); window.scrollTo(0, 0); }} style={quickStyle}>
                        <PhImage size={15} weight="duotone" /> Enhance photos
                      </button>
                      <button onClick={() => { setStBookStep(5); window.scrollTo(0, 0); }} style={quickStyle}>
                        <PhFileText size={15} weight="duotone" /> Special page
                      </button>
                      {isGiftBook && (
                        <button onClick={() => setStBookStep(7)} style={quickStyle}>
                          <PhUsers size={15} weight="duotone" /> Invite
                        </button>
                      )}
                    </div>
                  );
                })()}
                {(() => {
                  const photosWithImages = stBookPages.filter(p => p.photoUrl || p.photoPath);
                  const enhancedCount = stBookPages.filter(p => p.photoEnhanced).length;
                  const unenhancedCount = photosWithImages.filter(p => !p.photoEnhanced).length;
                  const freeUsed = accountSettings.enhancementsUsed || 0;
                  const freeRemaining = Math.max(0, 10 - freeUsed);
                  if (photosWithImages.length === 0) return null;
                  return (
                    <div style={{
                      marginTop: 10, padding: '12px 16px',
                      background: '#efebe0', border: '1px solid rgba(15,23,42,0.05)', borderRadius: 8,
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                    }}>
                      <PhSparkle size={20} weight="duotone" style={{ color: '#7c2d12', flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 500, color: '#0f172a' }}>
                          Make photos print-ready
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'rgba(15,23,42,0.8)', marginTop: 2 }}>
                          {enhancedCount > 0
                            ? `${enhancedCount} photo${enhancedCount !== 1 ? 's' : ''} enhanced · ${freeRemaining} of 10 remaining`
                            : freeRemaining > 0
                              ? `${freeRemaining} of 10 enhancements available. Tap Enhance photos above to start.`
                              : 'All 10 enhancements used'
                          }
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* (Quick-row now exposes Preview / Cover / Enhance photos / Special page directly;
                    the old "More" disclosure was removed.) */}

                </div>
                {/* end right column */}
              </div>
            ) : (
              /* ═══ OPEN BOOK — PDF spread interior ═══ */
              <>
                {/* Sticky reader bar */}
                <div className="reader-sticky-bar">
                  <button onClick={() => setStBookOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Lora', serif", fontSize: "0.95rem", color: "#3c4653", padding: "4px 8px", transition: "color 0.2s", whiteSpace: "nowrap" }}
                    onMouseOver={e => e.currentTarget.style.color = "#0f172a"}
                    onMouseOut={e => e.currentTarget.style.color = "#3c4653"}>
                    ← Back
                  </button>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: "1.1rem", fontWeight: 500, color: "#0f172a", flex: 1, textAlign: "center" }}>{stBookTitle || "My Stories"}</div>
                  <button onClick={() => { trackEvent('book_order_started', { item_category: isGiftBook ? 'celebration' : 'book' }); setStOrderStep("address"); setStBookStep(6); window.scrollTo(0, 0); }} className="btn-order-main" style={{ padding: "8px 20px", fontSize: "0.85rem", whiteSpace: 'nowrap' }}>
                    {saleOffer.saleActive
                      ? <>Order · <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{fmtUsd(saleOffer.anchorCents)}</span> {fmtUsd(saleOffer.saleCents)}</>
                      : 'Order · $89.99'}
                  </button>
                </div>
                {(hasPhotosToLoad && photosResolving) ? (
                  <div style={{ textAlign: 'center', padding: '60px 16px' }}>
                    <div style={{ width: 200, height: 4, background: 'rgba(15,23,42,0.07)', borderRadius: 2, margin: '0 auto 16px', overflow: 'hidden' }}>
                      <div style={{ width: '60%', height: '100%', background: 'rgba(15,23,42,0.2)', borderRadius: 2, animation: 'loading 1.5s ease-in-out infinite' }} />
                    </div>
                    <p style={{ fontSize: '1.1rem', color: '#0f172a', fontFamily: "'Lora', serif" }}>Loading your photos...</p>
                    <p style={{ fontSize: '1.05rem', color: '#3c4653', marginTop: 6 }}>This takes just a moment</p>
                  </div>
                ) : (() => {
                  const pdfChapters = stBookPages.map(page => {
                    const story = stories.find(s => s.id === page.storyId);
                    if (!story) return null;
                    // Use compressed preview photo for rendering, falling back to original
                    // base64 then to signed URL. photoUrl (signed) is a last resort — it
                    // works for freshly-added photos before base64 is ready, but may be
                    // absent on celebration/gift chapters that were synced from contributions.
                    const photo = stPreviewPhotos[page.storyId] || page.photoBase64 || page.photoUrl || null;
                    const subtitle = '';
                    // Look up contributor relationship from book's contributors array
                    const contribRelationship = page.contributorName && activeBook?.contributors
                      ? (activeBook.contributors.find(c => c.name && page.contributorName.toLowerCase().includes(c.name.toLowerCase()))?.relationship || '')
                      : '';
                    const rawContent = story.content || '';
                    const content = page.contributorName ? cleanContributionContent(rawContent) : rawContent;
                    return {
                      title: promptToChapterTitle(story.title) || 'Untitled',
                      content,
                      photoUrl: page.layout === 'text-only' ? null : photo,
                      caption: page.caption || '',
                      layout: page.layout === 'text-only' ? 'classic' : (page.layout || (photo ? 'photo-spread' : 'classic')),
                      photoFocusX: page.photoFocusX ?? 50, photoFocusY: page.photoFocusY ?? 50,
                      photoOrientation: page.photoOrientation || 'landscape', subtitle,
                      contributorName: page.contributorName || '',
                      contributorRelationship: contribRelationship,
                      isKidStory: page.isKidStory || false,
                      qrDataUrl: (page.shareId && page.qrEnabled !== false) ? chapterQrCodes[page.storyId] : null,
                      mediaType: page.mediaType,
                    };
                  }).filter(Boolean);

                  const bookDoc = (
                    <RemnantBookPDF
                      title={stBookTitle || 'Our Story'}
                      author={stBookAuthor}
                      familyName={stBookAuthor || ''}
                      dedication={stBookDedication || ''}
                      isCaretaker={isKeeper}
                      lovedOneName={legacySubjectName}
                      chapters={pdfChapters}
                      occasion={isGiftBook ? activeBook?.occasion : null}
                      recipientName={isGiftBook ? (activeBook?.recipientName || '') : ''}
                      contributorCount={isGiftBook ? (activeBook?.contributors?.length || 0) : 0}
                    />
                  );

                  // Stable key — only changes when book content actually changes
                  const bookKey = `${stBookTitle}|${stBookAuthor}|${stBookDedication}|${pdfChapters.length}|${pdfChapters.map(c => c.title + (c.photoUrl ? '1' : '0') + (c.qrDataUrl ? 'q' : '')).join(',')}`;

                  return (
                    <BookSpreadPreview
                        bookDoc={bookDoc}
                        bookKey={bookKey}
                        previewIdx={stBookPreviewIdx}
                        setPreviewIdx={setStBookPreviewIdx}
                        onBack={() => { setStBookOpen(false); window.scrollTo(0, 0); }}
                        onSave={null}
                        chapterCount={pdfChapters.length}
                        est={est}
                        isHome={true}
                        onRealPageCount={(n) => { saveBookData({ realPageCount: n }); }}
                      />
                  );
                })()}
              </>
            )}

            {/* ═══ SEPARATOR + TABLE OF CONTENTS ═══ */}
            <div style={{ borderTop: '1px solid rgba(15,23,42,0.1)', marginBottom: 24, paddingTop: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ fontFamily: "'Lora', serif", fontSize: '1.3rem', fontWeight: 500, color: '#0f172a', margin: 0 }}>Table of Contents</h2>
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <ChapterSearchList
                pages={stBookPages}
                stories={stories}
                onReorder={(event) => {
                  const { active, over } = event;
                  if (active.id !== over?.id) {
                    const oldIndex = stBookPages.findIndex(p => p.storyId === active.id);
                    const newIndex = stBookPages.findIndex(p => p.storyId === over.id);
                    setStBookPages(arrayMove(stBookPages, oldIndex, newIndex));
                  }
                }}
                onEditChapter={(idx) => { setStBookEditIdx(idx); setStBookStep(1); }}
                onToggleEnhance={handleEnhancePhoto}
              />
            </div>

            {/* Stories not yet in book — hidden for gift books (gift stories come via invites) */}
            {!isGiftBook && stories.filter(item => !stBookPages.some(p => p.storyId === item.id)).length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <p style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#0f172a', textTransform: 'uppercase', marginBottom: 10, fontWeight: 500 }}>Add an earlier story</p>
                {stories.filter(item => !stBookPages.some(p => p.storyId === item.id)).map(item => (
                  <button key={item.id} onClick={() => {
                    setStBookPages([...stBookPages, { storyId: item.id, photoUrl: null, photoPath: null, caption: '', photoSize: 'large' }]);
                    showToast(`Added "${item.title}" to your book!`);
                  }} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: '#ffffff', border: '1px solid rgba(15,23,42,0.07)', borderRadius: 12, padding: '14px 16px', cursor: 'pointer', textAlign: 'left', marginBottom: 6 }}>
                    <div style={{ width: 48, height: 48, flexShrink: 0, background: 'rgba(15,23,42,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, fontSize: '1.3rem', color: '#0f172a' }}>+</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '1rem', color: '#1a1612', fontWeight: 600, fontFamily: "'Lora', serif" }}>{item.title}</div>
                      <div style={{ fontSize: '1.05rem', color: '#3c4653', marginTop: 2 }}>Add to your book</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

          </>
      </div>

      </>
    );
  };

  const sharedEnhanceConfirms = (
    <>
      {confirmRevertPage && (
        <ConfirmModal
          title="Revert to original photo?"
          message="Your original was never overwritten. Reverting puts it back, and you can enhance again whenever you like."
          confirmLabel="Revert"
          cancelLabel="Keep enhanced"
          onConfirm={() => { const p = confirmRevertPage; setConfirmRevertPage(null); revertEnhancedPhoto(p); }}
          onCancel={() => setConfirmRevertPage(null)}
        />
      )}
      {confirmEnhanceAll && (
        <ConfirmModal
          title={`Enhance all ${confirmEnhanceAll.count} photo${confirmEnhanceAll.count !== 1 ? 's' : ''}?`}
          message={`This will use ${confirmEnhanceAll.count} enhancement credit${confirmEnhanceAll.count !== 1 ? 's' : ''}.`}
          confirmLabel="Enhance all"
          cancelLabel="Cancel"
          onConfirm={() => {
            setConfirmEnhanceAll(null);
            executeEnhanceAll();
          }}
          onCancel={() => setConfirmEnhanceAll(null)}
        />
      )}
      {confirmEnhancePage && (() => {
        const freeUsed = accountSettings.enhancementsUsed || 0;
        const freeRemaining = Math.max(0, 10 - freeUsed);
        return (
          <ConfirmModal
            title="Enhance this photo?"
            message={`AI enhancement makes your photo print-ready. You have ${freeRemaining} of 10 free enhancement${freeRemaining !== 1 ? 's' : ''} remaining. You can re-enhance or revert any photo anytime.`}
            confirmLabel="Enhance"
            cancelLabel="Cancel"
            onConfirm={() => proceedWithEnhance(confirmEnhancePage)}
            onCancel={() => setConfirmEnhancePage(null)}
          />
        );
      })()}
    </>
  );

  // F-304: the enhance confirms render OUTSIDE renderStorytellerBook, so they mount on EVERY
  // screen instead of only the branches that happened to carry a copy.
  //
  // renderStorytellerBook is one function with eight early returns (steps 1, 2, 4, 5, 6, 7, 8, 9).
  // These two modals used to live in its FALLBACK return, so any screen that returned early never
  // mounted them. The Photo Manager (step 9) is exactly that case: clicking "Enhance" on a photo
  // card set `confirmEnhancePage` and rendered nothing at all — no dialog, no preview, no error.
  // The chapter-edit page worked only because step 1 carried a DUPLICATE of the same modal, which
  // is the real defect: one modal copied per branch, and the branch the quick-action row actually
  // labels "Enhance photos" was the one that was missed. That duplicate is deleted here rather
  // than a third copy added (Change Protocol rule 3 — duplicated things get deduped).
  //
  // Both are state-guarded (`confirmEnhanceAll` / `confirmEnhancePage`), so rendering them here is
  // inert until something sets that state. Found by the F-301 runtime leg; ENHANCE-CONFIRM-UNREACHABLE-1.
  return (
    <>
      {renderStorytellerBook()}
      {sharedEnhanceConfirms}
    </>
  );
}

// Separate export for the Gift Books tab used in Legacy Manager mode
export function GiftBooksTab() {
  const ctx = useAppContext();
  const {
    books, enterBook, setActiveTab, setActiveTabRaw,
    setShowGiftCreate, setCbCreateStep,
  } = ctx;
  const giftBooks = books.filter(b => b.bookType === 'GIFT');
  if (giftBooks.length === 0) {
    return (
      <div className="storyteller-content" style={{ textAlign: 'center', padding: '40px 16px' }}>
        <div style={{ marginBottom: 16, color: '#7c2d12' }}><PhPackage size={56} weight="duotone" /></div>
        <h2 style={{ fontFamily: "'Lora', serif", fontSize: '1.8rem', color: '#0f172a', fontWeight: 400, marginBottom: 10, lineHeight: 1.3 }}>Give a book they'll treasure forever</h2>
        <p style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#3c4653', lineHeight: 1.6, maxWidth: 420, margin: '0 auto 32px' }}>
          Gather stories from your family into a beautiful hardcover book for Mother's Day, Father's Day, birthdays, and more.
        </p>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 32 }}>
          {[
            { emoji: '💐', title: 'For Mom,\nWith Love', bg: 'linear-gradient(145deg, #d4a0a0, #c48888)' },
            { emoji: '🏔️', title: 'Dad,\nFrom All of Us', bg: 'linear-gradient(145deg, #3d5a80, #2c4a6e)' },
            { emoji: '🎂', title: 'Happy Birthday,\nGrandma', bg: 'linear-gradient(145deg, #c4933f, #a87d2e)' },
          ].map((cover, i) => (
            <div key={i} style={{
              width: 100, height: 130, borderRadius: '2px 6px 6px 2px',
              background: cover.bg, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', padding: 10,
              boxShadow: '3px 3px 12px rgba(0,0,0,0.2)', transition: 'transform 0.3s',
            }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px) rotate(-1deg)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
              <span style={{ fontSize: '1.4rem', marginBottom: 6, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }}>{cover.emoji}</span>
              <div style={{ fontFamily: "'Lora', serif", fontSize: '0.65rem', color: '#fff', lineHeight: 1.3, textAlign: 'center', whiteSpace: 'pre-line', textShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>{cover.title}</div>
              <div style={{ fontFamily: "'Newsreader', serif", fontSize: '0.45rem', letterSpacing: 1.5, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>Talebound</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 32, maxWidth: 500, margin: '0 auto 32px' }}>
          {[
            { num: '1', text: 'Pick the occasion' },
            { num: '2', text: 'Invite your family' },
            { num: '3', text: 'We print & ship' },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center', flex: '1 1 120px' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 6px', fontFamily: "'Newsreader', serif", fontSize: '0.8rem', color: '#7c2d12', fontWeight: 600 }}>{s.num}</div>
              <div style={{ fontSize: '0.88rem', color: '#3c4653', fontFamily: "'Lora', serif" }}>{s.text}</div>
            </div>
          ))}
        </div>
        <button onClick={() => { setShowGiftCreate(true); setCbCreateStep(0); setActiveTabRaw('book'); }}
          style={{ padding: '18px 40px', background: '#7c2d12', color: '#1a1612', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '1.1rem', fontFamily: "'Lora', serif", fontWeight: 700, letterSpacing: 2, boxShadow: '0 2px 12px rgba(201,168,76,0.3)', transition: 'all 0.3s', textTransform: 'uppercase' }}>
          Start a Celebration Book
        </button>
        <p style={{ fontSize: '0.92rem', color: '#3c4653', marginTop: 12 }}>Free to start · No credit card required</p>
      </div>
    );
  }
  return (
    <div className="storyteller-content">
      <h2 className="st-h2" style={{ fontSize: '2rem', marginBottom: 24 }}>{giftBooks.some(b => b.occasion === 'IN_MEMORY') ? 'Your Books' : 'Celebration Books'}</h2>
      <div style={{ display: 'grid', gap: 14, marginBottom: 24 }}>
        {giftBooks.map(book => {
          const occ = OCCASION_TYPES.find(o => o.id === book.occasion);
          const daysLeft = book.deliveryDate ? Math.ceil((new Date(book.deliveryDate) - new Date()) / 86400000) : null;
          const contributed = (book.contributors || []).filter(c => c.status === 'CONTRIBUTED').length;
          const totalContrib = (book.contributors || []).length;
          return (
            <button key={book.id} onClick={() => { setActiveTab('book'); enterBook(book.id); }}
              style={{
                width: '100%', padding: '18px 20px', background: '#fff',
                border: '2px solid rgba(201,168,76,0.4)', borderRadius: 12,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16,
                textAlign: 'left', transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c2d12'; e.currentTarget.style.boxShadow = '0 0 0 4px rgba(201,168,76,0.12), 0 8px 32px rgba(201,168,76,0.18)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(201,168,76,0.4)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
              <div style={{
                width: 52, height: 68, borderRadius: '2px 6px 6px 2px', flexShrink: 0,
                background: book.occasion === 'MOTHERS_DAY' ? 'none' : `linear-gradient(135deg, ${occ?.accent || '#3c4653'}, ${occ?.accent || '#3c4653'}cc)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '2px 2px 6px rgba(0,0,0,0.15)',
                overflow: 'hidden', position: 'relative',
              }}>
                {book.occasion === 'MOTHERS_DAY' ? (
                  <img src={MothersDayCover} alt="" loading="lazy" decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: 22, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))' }}>{occ?.emoji || ''}</span>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Lora', serif", fontSize: '1.1rem', fontWeight: 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {book.title || 'Untitled Book'}
                </div>
                <div style={{ fontSize: '0.92rem', color: '#3c4653', marginTop: 3 }}>
                  {occ?.label || 'Celebration'} for {book.recipientName} · {(book.pages || []).length} chapters
                  {totalContrib > 0 && <> · {contributed}/{totalContrib} contributed</>}
                </div>
                {daysLeft != null && (
                  <div style={{ fontSize: '0.85rem', marginTop: 4, color: daysLeft <= 7 ? '#c53030' : daysLeft <= 14 ? '#b8860b' : '#6b8f71', fontWeight: 500 }}>
                    {daysLeft > 0 ? `${daysLeft} days until delivery` : 'Past delivery date'}
                  </div>
                )}
              </div>
              <span style={{ color: '#3c4653', fontSize: '1.2rem', flexShrink: 0 }}>→</span>
            </button>
          );
        })}
      </div>
      <button onClick={() => { setShowGiftCreate(true); setCbCreateStep(0); setActiveTabRaw('book'); }}
        style={{ width: '100%', padding: '18px 20px', background: '#fffdf5', border: '2px solid #7c2d12', borderRadius: 12, cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s', fontFamily: "'Lora', serif", fontSize: '1.05rem', fontWeight: 600, color: '#5a4a30', boxShadow: '0 0 0 4px rgba(201,168,76,0.12), 0 8px 32px rgba(201,168,76,0.18)' }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 0 5px rgba(201,168,76,0.18), 0 16px 48px rgba(201,168,76,0.28)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 0 4px rgba(201,168,76,0.12), 0 8px 32px rgba(201,168,76,0.18)'; e.currentTarget.style.transform = 'none'; }}>
         Create Another Celebration Book
      </button>
    </div>
  );
}
